import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import type { userState } from "../../interface/MainInterface";
import { HttpMonitoringApi } from "../../interface/monitoring-api";
import { buildMonitoringDetailUrl } from "./monitoringDashboardUtils";
import { formatMonitoringActivePeriod, isMonitoringExpired } from "./monitoringActiveWindowUtils";
import {
  buildSentimentKeywordConfigFromMonitoring,
  buildSentimentKeywordDisplayState,
  type SentimentKeywordDisplayState,
  type SentimentKeywordMode,
} from "./monitoringSentimentKeywordUtils";
import { normalizeExcludeTerms } from "./monitoringExcludeTermUtils";
import {
  buildSearchTermRefsFromMonitoring,
  getSearchTermNames,
  mapSearchTermOption,
  mergeSearchTermOptions,
  resolveSearchTermExcludeRows,
  type SearchTermExcludeDisplayRow,
  type SearchTermOption,
  type SearchTermRef,
} from "./monitoringSearchTermUtils";

type MonitoringStatus = "ACTIVE" | "PAUSED";
type MonitoringType = "PRESS_RELEASE" | "KEYWORD";
type NotificationAudience = "ME" | "ALL" | "CUSTOM";
type ViewMode = "articles" | "runs" | "settings";
type RunStatus = "SUCCESS" | "FAILED" | "SKIPPED" | "RUNNING";
type NotificationDeliveryStatus = "NOT_REQUESTED" | "QUEUED" | "SUCCEEDED" | "FAILED" | "SUPPRESSED";
type NotificationRecipientDeliveryStatus = "SUCCEEDED" | "FAILED" | "SUPPRESSED" | "PENDING" | "SENDING" | "QUEUED" | "UNKNOWN";
type Tone = "NEGATIVE" | "NEUTRAL" | "UNKNOWN";
type ToneFilter = "ALL" | "NEGATIVE";
type RevisionFilter = "ALL" | "DETECTED" | "UNCHANGED";
type RevisionTrackingFilter = "ALL" | "ACTIVE";
type LinkedArticleFilterMode = "NEGATIVE_ONLY" | "REVISION_ONLY" | "NEGATIVE_OR_REVISION";
type ArticleRangeDays = 1 | 2 | 3 | 4 | 5 | 6 | 7;
type KeywordMatchLevel = "HIGH" | "MEDIUM" | "LOW";
type ArticleRevisionTrackingStatus = "ACTIVE" | "EXPIRED" | "STOPPED" | "";
type ArticleRevisionPdfArtifactStatus = "PENDING" | "GENERATING" | "READY" | "FAILED";
type ArticleRevisionPdfArtifactKind = "baseline" | "revision";
type ArticleDenseRowVariant = "default" | "pressRelated";
type RevisionPdfBadgeTone = "baselineReady" | "ready" | "failed" | "pending" | "error";

interface MonitoringConfig {
  id: string;
  name: string;
  description: string;
  monitoringType: MonitoringType;
  status: MonitoringStatus;
  intervalMinutes: number;
  includeTerms: string[];
  excludeTerms: string[];
  searchTermRefs: SearchTermRef[];
  sentimentEnabled: boolean;
  sentimentKeywordMode: SentimentKeywordMode;
  positiveSentimentKeywords: string[];
  negativeSentimentKeywords: string[];
  notificationEnabled: boolean;
  notificationAudience: NotificationAudience;
  notificationRecipientUserCount: number;
  retentionDays: number;
  revisionTrackingEnabled: boolean;
  revisionTrackingHours?: number;
  revisionTrackingStartedAt?: string | null;
  revisionTrackingExpiresAt?: string | null;
  activeUntilDate: string;
  activeUntil: string;
  deactivatedAt: string;
  deactivationReason: string;
}

interface ResultItem {
  id: string;
  pressReleaseId?: string;
  title: string;
  publisher: string;
  reporter?: string;
  reporterEmail?: string;
  publishedAt: string;
  modifiedAt?: string;
  url?: string;
  matchedKeywords: string[];
  tone: Tone;
  body: string;
  revisionDetected?: boolean;
  reviewTags?: string[];
  keywordMatchScore?: number | null;
  keywordMatchLevel?: KeywordMatchLevel | null;
  keywordMatchDetails: KeywordMatchDetail[];
  revisionTrackingStatus?: ArticleRevisionTrackingStatus;
  revisionTrackingHours?: number;
  revisionTrackingStartedAt?: string;
  revisionTrackingExpiresAt?: string;
  revisionTrackingNotifyEnabled?: boolean;
  revisionPdfArtifacts?: ArticleRevisionPdfArtifacts;
}

interface ArticleRevisionPdfArtifact {
  artifactId: string;
  artifactType: string;
  status: ArticleRevisionPdfArtifactStatus;
  downloadAvailable: boolean;
  retryAvailable: boolean;
  message: string;
  requestedAt: string;
  generatedAt: string;
  failedAt: string;
  retryRequestedAt: string;
  attemptCount: number;
  maxAttempts: number;
  contentVersion: string;
}

interface ArticleRevisionPdfArtifacts {
  baseline?: ArticleRevisionPdfArtifact;
  revision?: ArticleRevisionPdfArtifact;
  all: ArticleRevisionPdfArtifact[];
}

interface KeywordMatchDetail {
  keyword: string;
  score: number;
  titleMatched: boolean;
  bodyMatched: boolean;
  titleHitCount: number;
  bodyHitCount: number;
}

interface PressReleaseCandidate extends ResultItem {
  alreadyRelated: boolean;
}

interface CandidateSearchState {
  loading: boolean;
  approvingArticleIds?: string[];
  error?: string | null;
  searched?: boolean;
  items: PressReleaseCandidate[];
}

interface RunHistoryItem {
  id: string;
  scheduledAt: string;
  status: RunStatus;
  foundCount: number;
  needsReviewCount: number;
  notificationSentCount: number;
  notificationDeliveryStatus: NotificationDeliveryStatus;
  notificationDeliveryMessage: string;
  notificationSnapshotAvailable: boolean;
  notificationSnapshotHash: string;
  notificationRecipientSummary: NotificationRecipientSummary;
  notificationRecipientResults: NotificationRecipientResult[];
  message: string;
}

interface NotificationRecipientSummary {
  totalCount: number;
  succeededCount: number;
  failedCount: number;
  pendingCount: number;
  sendingCount: number;
  manualRetryEligibleRecipientIds: string[];
}

interface NotificationRecipientResult {
  recipientId: string;
  deliveryStatus: NotificationRecipientDeliveryStatus;
  retryable: boolean;
  lastMessage: string;
}

interface PressReleaseGroup {
  id: string;
  title: string;
  company: string;
  distributedAt: string;
  tone?: Tone;
  originalSearchTitle: string;
  suggestedSearchQuery: string;
  suggestedSearchKeywords: string[];
  suggestedSearchSource: string;
  suggestedSearchGeneratedAt: string;
  articles: ResultItem[];
  relatedArticleCount: number;
  articleRangeRelatedArticleCount?: number;
  negativeRelatedArticleCount: number;
  revisionDetectedArticleCount: number;
  articlesLoaded: boolean;
  groupKeywordMatched?: boolean;
  loadError?: string | null;
}

interface RevisionDiffEntry {
  field: string;
  label: string;
  changed: boolean;
  before: string;
  after: string;
}

interface RevisionDiffData {
  exists: boolean;
  diff?: RevisionDiffEntry[];
}

interface RevisionHighlightPart {
  text: string;
  highlighted: boolean;
}

interface NewPressReleasePageProps {
  userState: userState;
}

const monitoringApi = new HttpMonitoringApi();

const normalizeDate = (value?: string) => {
  if (!value) return "";
  if (/^\d{14}/.test(value)) {
    return `${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6, 8)}T${value.slice(8, 10)}:${value.slice(10, 12)}:${value.slice(12, 14)}+09:00`;
  }
  return value;
};

const formatDateTime = (value?: string) => {
  const normalized = normalizeDate(value);
  if (!normalized) return "-";
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return normalized;
  return new Intl.DateTimeFormat("ko-KR", { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false }).format(date);
};

const formatRunDateTime = (date: Date) => {
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const getTodayKst = () =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Seoul",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());

const defaultArticleRangeDays: ArticleRangeDays = 1;
const publicAssetBaseUrl = process.env.PUBLIC_URL || "";
const monitoringListIconBaseUrl = `${publicAssetBaseUrl}/assets/monitoring-list`;
const monitoringDetailIconBaseUrl = `${publicAssetBaseUrl}/assets/monitoring-detail`;
const monitoringDetailIcons = {
  selectArrow: `${monitoringListIconBaseUrl}/icon-select-arrow.png`,
  filter: `${monitoringDetailIconBaseUrl}/icon-filter-sliders.png`,
  rerunPlay: `${monitoringDetailIconBaseUrl}/icon-rerun-play.png`,
  revisionTrackingActive: `${monitoringDetailIconBaseUrl}/icon-revision-tracking-active.png`,
  revisionTrackingStopped: `${monitoringDetailIconBaseUrl}/icon-revision-tracking-stopped.png`,
  revisionTrackingExpired: `${monitoringDetailIconBaseUrl}/icon-revision-tracking-expired.png`,
};

const articleRangeDayOptions: { value: ArticleRangeDays; label: string }[] = [
  { value: 1, label: "기준일 1일" },
  { value: 2, label: "최근 2일" },
  { value: 3, label: "최근 3일" },
  { value: 4, label: "최근 4일" },
  { value: 5, label: "최근 5일" },
  { value: 6, label: "최근 6일" },
  { value: 7, label: "최근 7일" },
];

const normalizeArticleRangeDays = (value: number | string | null): ArticleRangeDays => {
  const numericValue = Number(value);
  return Number.isInteger(numericValue) && numericValue >= 1 && numericValue <= 7
    ? numericValue as ArticleRangeDays
    : defaultArticleRangeDays;
};

const isArticleDateValue = (value?: string | null) =>
  /^\d{4}-\d{2}-\d{2}$/.test((value ?? "").trim());

const normalizeArticleDate = (value?: string | null) => {
  const text = (value ?? "").trim();
  return isArticleDateValue(text) ? text : getTodayKst();
};

const getArticleRangeLabel = (value: ArticleRangeDays, date: string) => {
  if (value === 1) {
    return date === getTodayKst() ? "오늘" : `${date} 기준`;
  }
  return `${date} 기준 최근 ${value}일`;
};

const getArticleRangeOptionLabel = (value: ArticleRangeDays, date: string) => {
  if (value === 1) return date === getTodayKst() ? "오늘" : "기준일";
  return `최근 ${value}일`;
};

const shouldUseCurrentRunArticleLookup = (hasExplicitDate: boolean, date: string, articleRangeDays: ArticleRangeDays) =>
  !hasExplicitDate && date === getTodayKst() && articleRangeDays === defaultArticleRangeDays;

const getNotificationAudience = (recipients?: string[]): NotificationAudience => {
  if (recipients?.includes("CUSTOM")) return "CUSTOM";
  if (recipients?.includes("ALL")) return "ALL";
  return "ME";
};

const getNotificationAudienceFromItem = (item: any): NotificationAudience => {
  const recipients = item.notificationRecipients || item.recipients || [];
  if (item.notificationRecipientScope === "CUSTOM" || recipients.includes("CUSTOM")) return "CUSTOM";
  if (item.notificationRecipientScope === "ALL" || recipients.includes("ALL")) return "ALL";
  return getNotificationAudience(recipients);
};

const getNotificationAudienceLabel = (monitor: Pick<MonitoringConfig, "notificationAudience" | "notificationRecipientUserCount">) => {
  if (monitor.notificationAudience === "CUSTOM") return `지정 사용자 ${monitor.notificationRecipientUserCount}명`;
  return monitor.notificationAudience === "ALL" ? "홍보실 전체" : "나에게만 전송";
};

const toFiniteNumber = (value: unknown, fallback: number) => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : fallback;
};

const clampNumber = (value: unknown, fallback: number, min: number, max: number) =>
  Math.min(max, Math.max(min, toFiniteNumber(value, fallback)));

const supportsRevisionTracking = (monitor: Pick<MonitoringConfig, "monitoringType">) =>
  monitor.monitoringType === "KEYWORD" || monitor.monitoringType === "PRESS_RELEASE";

const revisionPdfTypeBaseline = "TRACKING_START_BASELINE_PDF";
const revisionPdfTypeChanged = "REVISION_DETECTED_PDF";

const normalizeRevisionPdfArtifactStatus = (value?: string): ArticleRevisionPdfArtifactStatus => {
  if (value === "READY" || value === "GENERATING" || value === "FAILED") return value;
  return "PENDING";
};

const getRevisionPdfArtifactKind = (artifact: Pick<ArticleRevisionPdfArtifact, "artifactType">): ArticleRevisionPdfArtifactKind | "" => {
  if (artifact.artifactType === revisionPdfTypeBaseline) return "baseline";
  if (artifact.artifactType === revisionPdfTypeChanged) return "revision";
  return "";
};

const mapRevisionPdfArtifact = (item: any = {}): ArticleRevisionPdfArtifact | null => {
  const artifactId = item.artifactId || "";
  const artifactType = item.artifactType || "";
  if (!artifactType) return null;
  return {
    artifactId,
    artifactType,
    status: normalizeRevisionPdfArtifactStatus(item.status),
    downloadAvailable: item.downloadAvailable === true,
    retryAvailable: item.retryAvailable === true,
    message: item.message || "",
    requestedAt: item.requestedAt || "",
    generatedAt: item.generatedAt || "",
    failedAt: item.failedAt || "",
    retryRequestedAt: item.retryRequestedAt || "",
    attemptCount: toFiniteNumber(item.attemptCount, 0),
    maxAttempts: toFiniteNumber(item.maxAttempts, 0),
    contentVersion: item.contentVersion || "",
  };
};

const buildRevisionPdfArtifacts = (items: ArticleRevisionPdfArtifact[]): ArticleRevisionPdfArtifacts | undefined => {
  const uniqueItems = Array.from(
    items.reduce((map, item) => {
      const key = `${item.artifactType}:${item.artifactId || item.contentVersion || item.status}`;
      map.set(key, item);
      return map;
    }, new Map<string, ArticleRevisionPdfArtifact>()).values(),
  );
  if (!uniqueItems.length) return undefined;
  const sortByLatest = (left: ArticleRevisionPdfArtifact, right: ArticleRevisionPdfArtifact) =>
    String(right.generatedAt || right.requestedAt || right.contentVersion).localeCompare(String(left.generatedAt || left.requestedAt || left.contentVersion));
  const baseline = uniqueItems.filter((item) => getRevisionPdfArtifactKind(item) === "baseline").sort(sortByLatest)[0];
  const revision = uniqueItems.filter((item) => getRevisionPdfArtifactKind(item) === "revision").sort(sortByLatest)[0];
  return {
    baseline,
    revision,
    all: uniqueItems.sort(sortByLatest),
  };
};

const mapRevisionPdfArtifacts = (value: any): ArticleRevisionPdfArtifacts | undefined => {
  if (!value) return undefined;
  const rawItems: any[] = Array.isArray(value)
    ? value
    : Array.isArray(value.artifacts)
      ? value.artifacts
      : [value.baseline, value.revision].filter(Boolean);
  return buildRevisionPdfArtifacts(rawItems.map(mapRevisionPdfArtifact).filter((item: ArticleRevisionPdfArtifact | null): item is ArticleRevisionPdfArtifact => Boolean(item)));
};

const mergeRevisionPdfArtifacts = (
  current?: ArticleRevisionPdfArtifacts,
  next?: ArticleRevisionPdfArtifacts,
): ArticleRevisionPdfArtifacts | undefined => buildRevisionPdfArtifacts([...(current?.all ?? []), ...(next?.all ?? [])]);

const hasRevisionPdfArtifacts = (artifacts?: ArticleRevisionPdfArtifacts) =>
  Boolean(artifacts?.baseline || artifacts?.revision || artifacts?.all.length);

const hasInFlightRevisionPdfArtifacts = (artifacts?: ArticleRevisionPdfArtifacts) =>
  Boolean(artifacts?.all.some((item) => item.status === "PENDING" || item.status === "GENERATING"));

const mapMonitoringConfig = (item: any): MonitoringConfig => {
  const monitoringType = item.monitoringType === "PRESS_RELEASE" ? "PRESS_RELEASE" : "KEYWORD";
  const sentimentKeywordConfig = buildSentimentKeywordConfigFromMonitoring(item);
  const searchTermRefs = buildSearchTermRefsFromMonitoring(item);
  const includeTerms = Array.isArray(item.searchTerms) && item.searchTerms.length
    ? item.searchTerms
    : Array.isArray(item.includeTerms) && item.includeTerms.length
      ? item.includeTerms
      : getSearchTermNames(searchTermRefs);
  return {
    id: item.monitoringId || item.id,
    name: item.title || item.name || "이름 없는 모니터링",
    description: item.description || "",
    monitoringType,
    status: item.status === "PAUSED" ? "PAUSED" : "ACTIVE",
    intervalMinutes: toFiniteNumber(item.runIntervalMinutes ?? item.intervalMinutes, 15),
    includeTerms,
    excludeTerms: normalizeExcludeTerms(item.excludeTerms),
    searchTermRefs,
    sentimentEnabled: item.sentimentEnabled !== false,
    sentimentKeywordMode: sentimentKeywordConfig.sentimentKeywordMode,
    positiveSentimentKeywords: sentimentKeywordConfig.positiveSentimentKeywords,
    negativeSentimentKeywords: sentimentKeywordConfig.negativeSentimentKeywords,
    notificationEnabled: item.notificationEnabled !== false && item.notificationPolicy !== "NONE",
    notificationAudience: getNotificationAudienceFromItem(item),
    notificationRecipientUserCount: Array.isArray(item.notificationRecipientUserIds) ? item.notificationRecipientUserIds.length : 0,
    retentionDays: clampNumber(item.articleRetentionDays ?? item.retentionDays, 3, 1, 7),
    revisionTrackingEnabled: false,
    revisionTrackingHours: 0,
    revisionTrackingStartedAt: null,
    revisionTrackingExpiresAt: null,
    activeUntilDate: item.activeUntilDate || (item.activeUntil ? String(item.activeUntil).slice(0, 10) : ""),
    activeUntil: item.activeUntil || "",
    deactivatedAt: item.deactivatedAt || "",
    deactivationReason: item.deactivationReason || "",
  };
};

const mapTone = (value?: string): Tone => {
  if (value === "NEGATIVE") return "NEGATIVE";
  if (value === "POSITIVE" || value === "NEUTRAL") return "NEUTRAL";
  return "UNKNOWN";
};

const mapArticleItem = (item: any = {}): ResultItem => ({
  id: item.articleId || item.id || item.sk,
  pressReleaseId: item.pressReleaseId || item.relatedPressReleaseId || "",
  title: item.title || "제목 없는 기사",
  publisher: item.publisher || "매체 미확인",
  reporter: item.reporter || "",
  reporterEmail: item.reporterEmail || "",
  publishedAt: item.publishedAt || item.detectedAt || item.collectedAt || "",
  modifiedAt: item.modifiedAt || "",
  url: item.url,
  matchedKeywords: Array.isArray(item.matchedSearchTerms) ? item.matchedSearchTerms : [],
  tone: mapTone(item.sentiment),
  body: item.body || item.bodyPreview || item.description || "",
  revisionDetected: item.revisionDetected === true || (Array.isArray(item.reviewTags) && item.reviewTags.includes("REVISION_DETECTED")),
  reviewTags: Array.isArray(item.reviewTags) ? item.reviewTags : [],
  keywordMatchScore: Number.isFinite(Number(item.keywordMatchScore)) ? Number(item.keywordMatchScore) : null,
  keywordMatchLevel: item.keywordMatchLevel === "HIGH" || item.keywordMatchLevel === "MEDIUM" || item.keywordMatchLevel === "LOW" ? item.keywordMatchLevel : null,
  keywordMatchDetails: Array.isArray(item.keywordMatchDetails)
    ? item.keywordMatchDetails.map((detail: any) => ({
      keyword: detail.keyword || "",
      score: toFiniteNumber(detail.score, 0),
      titleMatched: detail.titleMatched === true,
      bodyMatched: detail.bodyMatched === true,
      titleHitCount: toFiniteNumber(detail.titleHitCount, 0),
      bodyHitCount: toFiniteNumber(detail.bodyHitCount, 0),
    })).filter((detail: KeywordMatchDetail) => detail.keyword)
    : [],
  revisionTrackingStatus:
    item.revisionTrackingStatus === "ACTIVE" || item.revisionTrackingStatus === "EXPIRED" || item.revisionTrackingStatus === "STOPPED"
      ? item.revisionTrackingStatus
      : "",
  revisionTrackingHours: toFiniteNumber(item.revisionTrackingHours, 0),
  revisionTrackingStartedAt: item.revisionTrackingStartedAt || "",
  revisionTrackingExpiresAt: item.revisionTrackingExpiresAt || "",
  revisionTrackingNotifyEnabled: item.revisionTrackingNotifyEnabled === true,
  revisionPdfArtifacts: mapRevisionPdfArtifacts(item.pdfArtifacts || item.revisionPdfArtifacts),
});

const mapPressReleaseArticleItem = (item: any = {}, pressReleaseId: string): ResultItem => {
  const article = mapArticleItem(item);
  return { ...article, pressReleaseId: article.pressReleaseId || pressReleaseId };
};

const revisionArticleKeySeparator = "::";

const getRevisionArticleSelectionKey = (article: Pick<ResultItem, "id" | "pressReleaseId">, usePressReleaseContext = false) =>
  usePressReleaseContext && article.pressReleaseId ? `${article.pressReleaseId}${revisionArticleKeySeparator}${article.id}` : article.id;

const parseRevisionArticleSelectionKey = (key: string) => {
  const separatorIndex = key.indexOf(revisionArticleKeySeparator);
  if (separatorIndex < 0) return { pressReleaseId: "", articleId: key };
  return {
    pressReleaseId: key.slice(0, separatorIndex),
    articleId: key.slice(separatorIndex + revisionArticleKeySeparator.length),
  };
};

const getRevisionPdfArtifactActionKey = (
  article: Pick<ResultItem, "id" | "pressReleaseId">,
  artifact: Pick<ArticleRevisionPdfArtifact, "artifactId" | "artifactType" | "contentVersion" | "status">,
  usePressReleaseContext = false,
) => `${getRevisionArticleSelectionKey(article, usePressReleaseContext)}${revisionArticleKeySeparator}${artifact.artifactId || artifact.contentVersion || artifact.artifactType || artifact.status}`;

const mapCandidateItem = (item: any = {}): PressReleaseCandidate => ({
  ...mapArticleItem(item),
  alreadyRelated: item.alreadyRelated === true,
});

const mapPressReleaseGroup = (
  item: any = {},
  articles: ResultItem[] = [],
  loadError: string | null = null,
  articlesLoaded = false,
  relatedArticleCount?: number,
): PressReleaseGroup => ({
  id: item.pressReleaseId || item.id || item.sk,
  title: item.title || "제목 없는 보도자료",
  company: item.company || "회사 미확인",
  distributedAt: item.distributedAt || item.receivedAt || "",
  ...(item.sentiment ? { tone: mapTone(item.sentiment) } : {}),
  originalSearchTitle: item.originalSearchTitle || item.title || "",
  suggestedSearchQuery: item.suggestedSearchQuery || "",
  suggestedSearchKeywords: Array.isArray(item.suggestedSearchKeywords) ? item.suggestedSearchKeywords : [],
  suggestedSearchSource: item.suggestedSearchSource || "",
  suggestedSearchGeneratedAt: item.suggestedSearchGeneratedAt || "",
  articles,
  relatedArticleCount: relatedArticleCount ?? toFiniteNumber(item.relatedArticleCount ?? item.totalCount, articles.length),
  negativeRelatedArticleCount: toFiniteNumber(item.negativeRelatedArticleCount, articles.filter(matchesNegativeArticle).length),
  revisionDetectedArticleCount: toFiniteNumber(item.revisionDetectedArticleCount, articles.filter(matchesRevisionDetectedArticle).length),
  articlesLoaded,
  loadError,
});

const normalizeNotificationRecipientDeliveryStatus = (value: unknown): NotificationRecipientDeliveryStatus => {
  const status = String(value ?? "").toUpperCase();
  if (status === "SUCCEEDED" || status === "SUCCESS" || status === "SENT" || status === "DELIVERED") return "SUCCEEDED";
  if (status === "FAILED" || status === "FAIL") return "FAILED";
  if (status === "SUPPRESSED") return "SUPPRESSED";
  if (status === "PENDING") return "PENDING";
  if (status === "SENDING" || status === "IN_PROGRESS") return "SENDING";
  if (status === "QUEUED") return "QUEUED";
  return "UNKNOWN";
};

const mapNotificationRecipientResult = (item: any = {}): NotificationRecipientResult => ({
  recipientId: typeof item.recipientId === "string" ? item.recipientId : "",
  deliveryStatus: normalizeNotificationRecipientDeliveryStatus(item.deliveryStatus),
  retryable: item.retryable === true,
  lastMessage: typeof item.lastMessage === "string" ? item.lastMessage : "",
});

const mapNotificationRecipientSummary = (item: any, recipients: NotificationRecipientResult[]): NotificationRecipientSummary => {
  const summary = item.notificationRecipientSummary ?? {};
  const count = (keys: string[], fallback: number) => {
    const value = keys.map((key) => summary[key]).find((candidate) => candidate !== undefined && candidate !== null);
    return toFiniteNumber(value, fallback);
  };
  return {
    totalCount: count(["totalCount", "recipientCount", "total"], recipients.length),
    succeededCount: count(["succeededCount", "successCount", "deliveredCount"], recipients.filter((recipient) => recipient.deliveryStatus === "SUCCEEDED").length),
    failedCount: count(["failedCount", "failureCount"], recipients.filter((recipient) => recipient.deliveryStatus === "FAILED").length),
    pendingCount: count(["pendingCount", "queuedCount"], recipients.filter((recipient) => recipient.deliveryStatus === "PENDING" || recipient.deliveryStatus === "QUEUED").length),
    sendingCount: count(["sendingCount", "inProgressCount"], recipients.filter((recipient) => recipient.deliveryStatus === "SENDING").length),
    manualRetryEligibleRecipientIds: Array.isArray(summary.manualRetryEligibleRecipientIds)
      ? summary.manualRetryEligibleRecipientIds.filter((recipientId: unknown): recipientId is string => typeof recipientId === "string" && Boolean(recipientId))
      : [],
  };
};

const getManualRetryEligibleNotificationRecipients = (item: RunHistoryItem) => {
  const eligibleRecipientIds = new Set(item.notificationRecipientSummary.manualRetryEligibleRecipientIds);
  if (!eligibleRecipientIds.size) return [];
  return item.notificationRecipientResults.filter((recipient) => recipient.recipientId && eligibleRecipientIds.has(recipient.recipientId));
};

const getNotificationDeliveryInProgressCount = (item: RunHistoryItem) =>
  item.notificationRecipientResults.filter((recipient) =>
    recipient.deliveryStatus === "PENDING" || recipient.deliveryStatus === "SENDING",
  ).length;

const getNotificationRetryQueuedMessage = (response: any) => {
  const jobs = Array.isArray(response?.notificationJobs) ? response.notificationJobs : [];
  const stateCounts = new Map<string, number>();
  jobs.forEach((job: any) => {
    const state = String(job?.dispatchState || "UNKNOWN").toUpperCase();
    stateCounts.set(state, (stateCounts.get(state) ?? 0) + 1);
  });
  const jobSummary = jobs.length
    ? `작업 ${jobs.length}건 · ${Array.from(stateCounts.entries()).map(([state, count]) => `${state} ${count}건`).join(", ")}`
    : "작업 접수 상태를 확인 중입니다.";
  return `${response?.queued === true ? "알림 재시도 요청을 접수했습니다." : "알림 재시도 요청 응답을 받았습니다."} 전달 완료가 아니라 작업 접수 상태입니다. ${jobSummary}`;
};

const getNotificationRetryRequestKey = (monitoringId: string, run: RunHistoryItem) =>
  `${monitoringId}::${run.id}::${run.scheduledAt}::${run.notificationSnapshotHash}`;

const mapRunHistoryItem = (item: any): RunHistoryItem => {
  const notificationSnapshotAvailable = item.notificationSnapshotAvailable === true;
  const notificationRecipientResults = notificationSnapshotAvailable && Array.isArray(item.notificationRecipientResults)
    ? item.notificationRecipientResults.map(mapNotificationRecipientResult)
    : [];
  return {
    id: item.runId || item.resultId || item.sk,
    scheduledAt: item.scheduledAt || item.createdAt || "-",
    status: item.status === "FAILED" ? "FAILED" : item.status === "SKIPPED" ? "SKIPPED" : item.status === "RUNNING" ? "RUNNING" : "SUCCESS",
    foundCount: toFiniteNumber(item.foundArticleCount, 0),
    needsReviewCount: toFiniteNumber(item.needsReviewCount, 0),
    notificationSentCount: toFiniteNumber(item.notificationSentCount, 0),
    notificationDeliveryStatus:
      item.notificationDeliveryStatus === "FAILED"
        ? "FAILED"
        : item.notificationDeliveryStatus === "SUPPRESSED"
          ? "SUPPRESSED"
        : item.notificationDeliveryStatus === "SUCCEEDED"
          ? "SUCCEEDED"
          : item.notificationDeliveryStatus === "QUEUED"
            ? "QUEUED"
            : "NOT_REQUESTED",
    notificationDeliveryMessage: item.notificationDeliveryMessage || "",
    notificationSnapshotAvailable,
    notificationSnapshotHash: item.notificationSnapshotHash || "",
    notificationRecipientSummary: mapNotificationRecipientSummary(item, notificationRecipientResults),
    notificationRecipientResults,
    message: item.message || "-",
  };
};

const toneFilterOptions: { value: ToneFilter; label: string }[] = [
  { value: "ALL", label: "전체" },
  { value: "NEGATIVE", label: "부정" },
];

const revisionFilterOptions: { value: RevisionFilter; label: string }[] = [
  { value: "ALL", label: "수정 전체" },
  { value: "DETECTED", label: "수정 감지" },
  { value: "UNCHANGED", label: "변경 없음" },
];

const matchesNegativeArticle = (item: ResultItem) => item.tone === "NEGATIVE" || item.reviewTags?.includes("NEGATIVE");
const matchesRevisionDetectedArticle = (item: ResultItem) => item.revisionDetected === true || item.reviewTags?.includes("REVISION_DETECTED");

const matchesLinkedArticleFilter = (item: ResultItem, mode: LinkedArticleFilterMode | null) => {
  if (!mode) return true;
  const negativeMatched = matchesNegativeArticle(item);
  const revisionMatched = matchesRevisionDetectedArticle(item);
  if (mode === "NEGATIVE_OR_REVISION") return negativeMatched || revisionMatched;
  if (mode === "NEGATIVE_ONLY") return negativeMatched;
  return revisionMatched;
};

const matchesManualArticleFilter = (
  item: ResultItem,
  toneFilter: ToneFilter,
  revisionFilter: RevisionFilter,
  sentimentEnabled: boolean,
  revisionTrackingEnabled: boolean,
) => {
  if (sentimentEnabled && revisionTrackingEnabled && toneFilter === "NEGATIVE" && revisionFilter === "DETECTED") {
    return matchesNegativeArticle(item) || matchesRevisionDetectedArticle(item);
  }
  const toneMatched = !sentimentEnabled || toneFilter === "ALL" || item.tone === toneFilter;
  const revisionMatched = !revisionTrackingEnabled || revisionFilter === "ALL" || (revisionFilter === "DETECTED" ? item.revisionDetected === true : item.revisionDetected !== true);
  return toneMatched && revisionMatched;
};

const isQueryFlagEnabled = (value: string | null) => value === "1" || value?.toLowerCase() === "true";

const resolveViewMode = (value: string | null | undefined): ViewMode => {
  if (value === "runs" || value === "settings" || value === "articles") return value;
  return "articles";
};

const resolveLinkedArticleFilterMode = (negativeOnly: boolean, revisionOnly: boolean): LinkedArticleFilterMode | null => {
  if (negativeOnly && revisionOnly) return "NEGATIVE_OR_REVISION";
  if (negativeOnly) return "NEGATIVE_ONLY";
  if (revisionOnly) return "REVISION_ONLY";
  return null;
};

const getToneFilterFromLinkedFilter = (mode: LinkedArticleFilterMode | null): ToneFilter =>
  mode === "NEGATIVE_ONLY" || mode === "NEGATIVE_OR_REVISION" ? "NEGATIVE" : "ALL";

const getRevisionFilterFromLinkedFilter = (mode: LinkedArticleFilterMode | null): RevisionFilter =>
  mode === "REVISION_ONLY" || mode === "NEGATIVE_OR_REVISION" ? "DETECTED" : "ALL";

const matchesKeywordSelection = (item: ResultItem, selectedKeywords: string[]) => {
  if (!selectedKeywords.length) return true;
  return item.matchedKeywords.some((keyword) => selectedKeywords.includes(keyword));
};

const matchesRevisionTrackingFilter = (item: ResultItem, revisionTrackingFilter: RevisionTrackingFilter) =>
  revisionTrackingFilter === "ALL" || item.revisionTrackingStatus === "ACTIVE";

const NewPressReleasePage = (props: NewPressReleasePageProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const queryMonitoringId = queryParams.get("monitoring") || undefined;
  const queryArticleDate = queryParams.get("date");
  const queryHasArticleDate = isArticleDateValue(queryArticleDate);
  const queryNegativeOnly = isQueryFlagEnabled(queryParams.get("negativeOnly"));
  const queryRevisionOnly = isQueryFlagEnabled(queryParams.get("revisionOnly"));
  const initialLinkedArticleFilter = resolveLinkedArticleFilterMode(queryNegativeOnly, queryRevisionOnly);
  const [monitors, setMonitors] = useState<MonitoringConfig[]>([]);
  const [searchTermOptions, setSearchTermOptions] = useState<SearchTermOption[]>([]);
  const [searchTermOptionsLoaded, setSearchTermOptionsLoaded] = useState(false);
  const searchTermOptionsLoadingRef = useRef(false);
  const [viewMode, setViewMode] = useState<ViewMode>(() => resolveViewMode(queryParams.get("tab")));
  const [articleDate, setArticleDate] = useState(() => normalizeArticleDate(queryArticleDate));
  const [articleRangeDays, setArticleRangeDays] = useState<ArticleRangeDays>(() => normalizeArticleRangeDays(queryParams.get("articleRangeDays")));
  const [filter, setFilter] = useState("");
  const [selectedKeywordFilters, setSelectedKeywordFilters] = useState<string[]>([]);
  const [linkedArticleFilter, setLinkedArticleFilter] = useState<LinkedArticleFilterMode | null>(initialLinkedArticleFilter);
  const [toneFilter, setToneFilter] = useState<ToneFilter>(getToneFilterFromLinkedFilter(initialLinkedArticleFilter));
  const [revisionFilter, setRevisionFilter] = useState<RevisionFilter>(getRevisionFilterFromLinkedFilter(initialLinkedArticleFilter));
  const [revisionTrackingFilter, setRevisionTrackingFilter] = useState<RevisionTrackingFilter>("ALL");
  const [advancedFilterOpen, setAdvancedFilterOpen] = useState(false);
  const [resultItems, setResultItems] = useState<ResultItem[]>([]);
  const [pressReleaseGroups, setPressReleaseGroups] = useState<PressReleaseGroup[]>([]);
  const [expandedGroupIds, setExpandedGroupIds] = useState<string[]>([]);
  const [candidateSearchStates, setCandidateSearchStates] = useState<Record<string, CandidateSearchState>>({});
  const [candidateSearchQueries, setCandidateSearchQueries] = useState<Record<string, string>>({});
  const [candidateDialogGroupId, setCandidateDialogGroupId] = useState<string | null>(null);
  const [runHistoryItems, setRunHistoryItems] = useState<RunHistoryItem[]>([]);
  const [articleLoading, setArticleLoading] = useState(false);
  const [pressReleaseGroupLoading, setPressReleaseGroupLoading] = useState(false);
  const [runHistoryLoading, setRunHistoryLoading] = useState(false);
  const [groupArticleLoadingIds, setGroupArticleLoadingIds] = useState<string[]>([]);
  const groupArticleRequestSeqRef = useRef(0);
  const groupArticleActiveRequestRef = useRef<Record<string, number>>({});
  const [keywordArticleTotalCount, setKeywordArticleTotalCount] = useState(0);
  const [groupLoadError, setGroupLoadError] = useState<string | null>(null);
  const [runHistoryLoadError, setRunHistoryLoadError] = useState<string | null>(null);
  const [rerunRequestedAt, setRerunRequestedAt] = useState<string | null>(null);
  const [rerunningMonitorIds, setRerunningMonitorIds] = useState<string[]>([]);
  const [manualRunMessage, setManualRunMessage] = useState<string | null>(null);
  const [revisionDialogOpen, setRevisionDialogOpen] = useState(false);
  const [revisionTargetArticle, setRevisionTargetArticle] = useState<ResultItem | null>(null);
  const [revisionDiff, setRevisionDiff] = useState<RevisionDiffData | null>(null);
  const [revisionDiffLoading, setRevisionDiffLoading] = useState(false);
  const [revisionDiffError, setRevisionDiffError] = useState<string | null>(null);
  const [revisionHighlightEnabled, setRevisionHighlightEnabled] = useState(true);
  const [revisionTrackingDialogOpen, setRevisionTrackingDialogOpen] = useState(false);
  const [revisionTrackingTargetArticle, setRevisionTrackingTargetArticle] = useState<ResultItem | null>(null);
  const [revisionTrackingHours, setRevisionTrackingHours] = useState(6);
  const [revisionTrackingNotifyEnabled, setRevisionTrackingNotifyEnabled] = useState(false);
  const [revisionTrackingSaving, setRevisionTrackingSaving] = useState(false);
  const [revisionTrackingStoppingKeys, setRevisionTrackingStoppingKeys] = useState<string[]>([]);
  const [revisionTrackingError, setRevisionTrackingError] = useState<string | null>(null);
  const [selectedRevisionArticleKeys, setSelectedRevisionArticleKeys] = useState<string[]>([]);
  const revisionPdfFetchedKeysRef = useRef<Set<string>>(new Set());
  const [revisionPdfLoadingKeys, setRevisionPdfLoadingKeys] = useState<string[]>([]);
  const [revisionPdfActionKeys, setRevisionPdfActionKeys] = useState<string[]>([]);
  const [revisionPdfErrors, setRevisionPdfErrors] = useState<Record<string, string>>({});
  const [batchRevisionDialogOpen, setBatchRevisionDialogOpen] = useState(false);
  const [batchRevisionTrackingHours, setBatchRevisionTrackingHours] = useState(6);
  const [batchRevisionTrackingNotifyEnabled, setBatchRevisionTrackingNotifyEnabled] = useState(false);
  const [batchRevisionTrackingSaving, setBatchRevisionTrackingSaving] = useState(false);
  const [batchRevisionTrackingError, setBatchRevisionTrackingError] = useState<string | null>(null);
  const [batchRevisionTrackingSummary, setBatchRevisionTrackingSummary] = useState<string | null>(null);
  const [batchRevisionTrackingToast, setBatchRevisionTrackingToast] = useState<{ id: number; message: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [savingMonitor, setSavingMonitor] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [notificationRetryingRunIds, setNotificationRetryingRunIds] = useState<string[]>([]);
  const [notificationRetryMessages, setNotificationRetryMessages] = useState<Record<string, string>>({});
  const [notificationRetryErrors, setNotificationRetryErrors] = useState<Record<string, string>>({});
  const notificationRetryRequestIdsRef = useRef<Map<string, string>>(new Map());

  const loadRunHistory = useCallback(async (monitoringId: string, isCurrent: () => boolean = () => true) => {
    setRunHistoryLoading(true);
    setRunHistoryLoadError(null);
    try {
      const response = await monitoringApi.post({ command: "list_monitoring_results", monitoringId, limit: 20 });
      if (!isCurrent()) return;
      if (response?.code === "200") {
        setRunHistoryItems(((response.response?.results ?? []) as any[]).map(mapRunHistoryItem));
      } else {
        setRunHistoryLoadError("실행 기록을 불러오지 못했습니다.");
        setRunHistoryItems([]);
      }
    } catch {
      if (isCurrent()) {
        setRunHistoryLoadError("실행 기록을 불러오지 못했습니다.");
        setRunHistoryItems([]);
      }
    } finally {
      if (isCurrent()) setRunHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setViewMode(resolveViewMode(params.get("tab")));
    setArticleDate(normalizeArticleDate(params.get("date")));
    setArticleRangeDays(normalizeArticleRangeDays(params.get("articleRangeDays")));
  }, [location.search]);

  useEffect(() => {
    let ignore = false;
    const loadMonitorList = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const response = await monitoringApi.post({ command: "list_monitorings" });
        if (ignore) return;
        if (response?.code !== "200") {
          setLoadError(response?.response?.error_msg ?? "모니터링 설정을 불러오지 못했습니다.");
          return;
        }
        const nextMonitors = ((response.response?.monitorings ?? []) as any[]).map(mapMonitoringConfig);
        if (!nextMonitors.length || !queryMonitoringId || !nextMonitors.some((item) => item.id === queryMonitoringId)) {
          navigate("/pressRelease", { replace: true });
          return;
        }
        setMonitors(nextMonitors);
      } catch (error) {
        if (!ignore) setLoadError(error instanceof Error ? error.message : "모니터링 데이터를 불러오지 못했습니다.");
      } finally {
        if (!ignore) setLoading(false);
      }
    };
    loadMonitorList();
    return () => {
      ignore = true;
    };
  }, [navigate, queryMonitoringId]);

  const selectedMonitor = monitors.find((item) => item.id === queryMonitoringId) ?? null;
  const selectedSearchTermExcludeRows = useMemo(
    () => selectedMonitor ? resolveSearchTermExcludeRows(selectedMonitor.searchTermRefs, searchTermOptions) : [],
    [searchTermOptions, selectedMonitor],
  );
  const revisionTrackingEnabled = selectedMonitor ? supportsRevisionTracking(selectedMonitor) : false;
  const selectedMonitorRerunning = selectedMonitor ? rerunningMonitorIds.includes(selectedMonitor.id) : false;
  const selectedMonitorExpired = selectedMonitor ? isMonitoringExpired(selectedMonitor) : false;
  const rerunDisabled = savingMonitor || selectedMonitorRerunning || !selectedMonitor || selectedMonitor.status !== "ACTIVE" || selectedMonitorExpired;

  useEffect(() => {
    if (viewMode !== "settings" || selectedMonitor?.monitoringType !== "KEYWORD" || searchTermOptionsLoaded || searchTermOptionsLoadingRef.current) return;
    let ignore = false;
    const loadSearchTermOptions = async () => {
      searchTermOptionsLoadingRef.current = true;
      try {
        const response = await monitoringApi.post({ command: "get_monitoring_config_options" });
        if (ignore) return;
        if (response?.code === "200") {
          const nextSearchTermOptions = ((response.response?.searchTerms ?? []) as any[])
            .map((item) => mapSearchTermOption(item, "CONFIG"))
            .filter((item): item is SearchTermOption => Boolean(item));
          setSearchTermOptions(mergeSearchTermOptions(nextSearchTermOptions));
        } else {
          setSearchTermOptions([]);
        }
      } catch {
        if (!ignore) setSearchTermOptions([]);
      } finally {
        searchTermOptionsLoadingRef.current = false;
        if (!ignore) setSearchTermOptionsLoaded(true);
      }
    };
    loadSearchTermOptions();
    return () => {
      ignore = true;
    };
  }, [searchTermOptionsLoaded, selectedMonitor?.monitoringType, viewMode]);

  const updateViewMode = (mode: ViewMode) => {
    setViewMode(mode);
    setSelectedRevisionArticleKeys([]);
    setRevisionTrackingFilter("ALL");
    const nextParams = new URLSearchParams(location.search);
    nextParams.set("tab", mode);
    navigate(`${location.pathname}?${nextParams.toString()}`, { replace: true });
  };

  const updateArticleDate = (value: string) => {
    const nextDate = normalizeArticleDate(value);
    setArticleDate(nextDate);
    setSelectedRevisionArticleKeys([]);
    const nextParams = new URLSearchParams(location.search);
    nextParams.set("date", nextDate);
    nextParams.set("tab", "articles");
    navigate(`${location.pathname}?${nextParams.toString()}`, { replace: true });
  };

  const updateArticleRangeDays = (value: number | string) => {
    const nextRangeDays = normalizeArticleRangeDays(value);
    setArticleRangeDays(nextRangeDays);
    setSelectedRevisionArticleKeys([]);
    const nextParams = new URLSearchParams(location.search);
    nextParams.set("articleRangeDays", String(nextRangeDays));
    nextParams.set("tab", "articles");
    navigate(`${location.pathname}?${nextParams.toString()}`, { replace: true });
  };

  const keywordFilterOptions = useMemo(() => {
    if (!selectedMonitor || selectedMonitor.monitoringType !== "KEYWORD") return [];
    const seen = new Set<string>();
    const values: string[] = [];
    resultItems.forEach((item) => {
      item.matchedKeywords.forEach((keyword) => {
        if (keyword && !seen.has(keyword)) {
          seen.add(keyword);
          values.push(keyword);
        }
      });
    });
    return values;
  }, [resultItems, selectedMonitor]);

  useEffect(() => {
    setSelectedKeywordFilters((prev) => prev.filter((keyword) => keywordFilterOptions.includes(keyword)));
  }, [keywordFilterOptions]);

  useEffect(() => {
    let ignore = false;
    const loadSelectedMonitoringData = async () => {
      if (!selectedMonitor?.id) return;
      setLoadError(null);
      setFilter("");
      setSelectedKeywordFilters([]);
      setAdvancedFilterOpen(false);
      setResultItems([]);
      setPressReleaseGroups([]);
      setRunHistoryItems([]);
      setGroupArticleLoadingIds([]);
      setKeywordArticleTotalCount(0);
      const nextLinkedFilter = resolveLinkedArticleFilterMode(queryNegativeOnly, revisionTrackingEnabled ? queryRevisionOnly : false);
      setLinkedArticleFilter(nextLinkedFilter);
      setToneFilter(getToneFilterFromLinkedFilter(nextLinkedFilter));
      setRevisionFilter(getRevisionFilterFromLinkedFilter(nextLinkedFilter));
      setRevisionTrackingFilter("ALL");
      setRevisionDialogOpen(false);
      setRevisionTargetArticle(null);
      setRevisionDiff(null);
      setRevisionDiffError(null);
      setRevisionTrackingDialogOpen(false);
      setRevisionTrackingTargetArticle(null);
      setRevisionTrackingHours(6);
      setRevisionTrackingNotifyEnabled(false);
      setRevisionTrackingError(null);
      setRevisionTrackingStoppingKeys([]);
      setSelectedRevisionArticleKeys([]);
      revisionPdfFetchedKeysRef.current.clear();
      setRevisionPdfLoadingKeys([]);
      setRevisionPdfActionKeys([]);
      setRevisionPdfErrors({});
      setBatchRevisionDialogOpen(false);
      setBatchRevisionTrackingHours(6);
      setBatchRevisionTrackingNotifyEnabled(false);
      setBatchRevisionTrackingError(null);
      setBatchRevisionTrackingSummary(null);
      setExpandedGroupIds([]);
      groupArticleActiveRequestRef.current = {};
      setCandidateSearchStates({});
      setCandidateSearchQueries({});
      setGroupLoadError(null);
      setRunHistoryLoadError(null);
      setRerunRequestedAt(null);
      setManualRunMessage(null);
      setNotificationRetryingRunIds([]);
      setNotificationRetryMessages({});
      setNotificationRetryErrors({});
      notificationRetryRequestIdsRef.current.clear();
      const useCurrentRunArticleLookup = shouldUseCurrentRunArticleLookup(queryHasArticleDate, articleDate, articleRangeDays);
      loadRunHistory(selectedMonitor.id, () => !ignore);

      if (selectedMonitor.monitoringType === "PRESS_RELEASE") {
        setResultItems([]);
        setPressReleaseGroupLoading(true);
        setArticleLoading(false);
        try {
          const useActivePressReleaseGroups = !queryHasArticleDate && articleDate === getTodayKst();
          const response = await monitoringApi.post({
            command: "list_press_release_groups",
            date: articleDate,
            limit: 50,
            ...(useActivePressReleaseGroups ? { mode: "ACTIVE" } : {}),
          });
          if (ignore) return;
          if (response?.code === "200") {
            const groups = ((response.response?.groups ?? []) as any[]).map((group) => mapPressReleaseGroup(group));
            setPressReleaseGroups(groups);
          } else {
            setGroupLoadError(response?.response?.error_msg ?? "보도자료 그룹 목록을 불러오지 못했습니다.");
            setPressReleaseGroups([]);
          }
        } catch (error) {
          if (!ignore) {
            setGroupLoadError(error instanceof Error ? error.message : "보도자료 그룹 목록을 불러오지 못했습니다.");
            setPressReleaseGroups([]);
          }
        } finally {
          if (!ignore) setPressReleaseGroupLoading(false);
        }
        return;
      }

      setPressReleaseGroups([]);
      setArticleLoading(true);
      try {
        const response = await monitoringApi.post({
          command: "list_monitoring_articles",
          monitoringId: selectedMonitor.id,
          date: articleDate,
          limit: "ALL",
          articleRangeDays,
          ...(useCurrentRunArticleLookup ? { articleLookupMode: "CURRENT_RUN" } : {}),
        });
        if (ignore) return;
        if (response?.code === "200") {
          setResultItems(((response.response?.articles ?? []) as any[]).map(mapArticleItem));
          setKeywordArticleTotalCount(toFiniteNumber(response.response?.totalCount, 0));
        } else {
          setLoadError(response?.response?.error_msg ?? "수집 기사 목록을 불러오지 못했습니다.");
          setResultItems([]);
        }
      } catch (error) {
        if (!ignore) {
          setLoadError(error instanceof Error ? error.message : "수집 기사 목록을 불러오지 못했습니다.");
          setResultItems([]);
        }
      } finally {
        if (!ignore) setArticleLoading(false);
      }
    };
    loadSelectedMonitoringData();
    return () => {
      ignore = true;
    };
  }, [articleDate, articleRangeDays, loadRunHistory, queryHasArticleDate, queryMonitoringId, queryNegativeOnly, queryRevisionOnly, selectedMonitor?.id, selectedMonitor?.monitoringType, revisionTrackingEnabled]);

  const filteredResults = useMemo(() => {
    if (!selectedMonitor) return [];
    const keyword = filter.trim().toLowerCase();
    return resultItems.filter((item) => {
      const keywordMatched = !keyword || `${item.title} ${item.body} ${item.publisher} ${item.reporter ?? ""} ${item.matchedKeywords.join(" ")}`.toLowerCase().includes(keyword);
      const selectedKeywordMatched = matchesKeywordSelection(item, selectedKeywordFilters);
      const revisionTrackingMatched = matchesRevisionTrackingFilter(item, revisionTrackingFilter);
      const optionMatched = linkedArticleFilter
        ? matchesLinkedArticleFilter(item, linkedArticleFilter)
        : matchesManualArticleFilter(item, toneFilter, revisionFilter, selectedMonitor.sentimentEnabled, revisionTrackingEnabled);
      return keywordMatched && selectedKeywordMatched && revisionTrackingMatched && optionMatched;
    });
  }, [filter, linkedArticleFilter, resultItems, revisionFilter, revisionTrackingFilter, selectedKeywordFilters, selectedMonitor, revisionTrackingEnabled, toneFilter]);

  const selectedRevisionPressReleaseId = useMemo(() => {
    if (selectedMonitor?.monitoringType !== "PRESS_RELEASE") return "";
    const firstPressReleaseKey = selectedRevisionArticleKeys.find((key) => parseRevisionArticleSelectionKey(key).pressReleaseId);
    return firstPressReleaseKey ? parseRevisionArticleSelectionKey(firstPressReleaseKey).pressReleaseId : "";
  }, [selectedMonitor?.monitoringType, selectedRevisionArticleKeys]);

  const filteredPressReleaseGroups = useMemo(() => {
    if (!selectedMonitor) return [];
    const keyword = filter.trim().toLowerCase();
    const hasArticleLevelFilter = revisionTrackingFilter === "ACTIVE" || Boolean(linkedArticleFilter) || toneFilter !== "ALL" || revisionFilter !== "ALL";
    return pressReleaseGroups
      .map((group) => {
        const groupText = [group.title, group.company, group.suggestedSearchQuery, group.suggestedSearchKeywords.join(" ")].join(" ").toLowerCase();
        const groupKeywordMatched = Boolean(keyword && groupText.includes(keyword));
        const articles = group.articles.filter((article) => {
          const articleKeywordMatched = !keyword || `${article.title} ${article.body} ${article.publisher} ${article.reporter ?? ""}`.toLowerCase().includes(keyword);
          const revisionTrackingMatched = matchesRevisionTrackingFilter(article, revisionTrackingFilter);
          const optionMatched = linkedArticleFilter
            ? matchesLinkedArticleFilter(article, linkedArticleFilter)
            : matchesManualArticleFilter(article, toneFilter, revisionFilter, selectedMonitor.sentimentEnabled, revisionTrackingEnabled);
          return articleKeywordMatched && revisionTrackingMatched && optionMatched;
        });
        const visible = groupKeywordMatched || articles.length > 0 || (!group.articlesLoaded && !keyword) || (!hasArticleLevelFilter && !keyword);
        return { ...group, articles, groupKeywordMatched, visible };
      })
      .filter((group) => group.visible);
  }, [filter, linkedArticleFilter, pressReleaseGroups, revisionFilter, revisionTrackingFilter, selectedMonitor, revisionTrackingEnabled, toneFilter]);

  const selectableRevisionArticleKeys = useMemo(() => {
    if (selectedMonitor?.monitoringType === "KEYWORD") {
      return filteredResults
        .filter((item) => item.revisionTrackingStatus !== "ACTIVE")
        .map((item) => getRevisionArticleSelectionKey(item));
    }
    if (selectedMonitor?.monitoringType !== "PRESS_RELEASE") return [];
    const firstSelectablePressReleaseId = selectedRevisionPressReleaseId || filteredPressReleaseGroups
      .flatMap((group) => group.articles.map((article) => ({ ...article, pressReleaseId: article.pressReleaseId || group.id })))
      .find((article) => article.revisionTrackingStatus !== "ACTIVE")?.pressReleaseId || "";
    if (!firstSelectablePressReleaseId) return [];
    return filteredPressReleaseGroups.flatMap((group) =>
      group.articles
        .filter((article) => article.revisionTrackingStatus !== "ACTIVE" && (article.pressReleaseId || group.id) === firstSelectablePressReleaseId)
        .map((article) => getRevisionArticleSelectionKey({ id: article.id, pressReleaseId: article.pressReleaseId || group.id }, true)),
    );
  }, [filteredPressReleaseGroups, filteredResults, selectedMonitor?.monitoringType, selectedRevisionPressReleaseId]);

  useEffect(() => {
    setSelectedRevisionArticleKeys((prev) => {
      const next = prev.filter((key) => selectableRevisionArticleKeys.includes(key));
      if (next.length === prev.length && next.every((key, index) => key === prev[index])) {
        return prev;
      }
      return next;
    });
  }, [selectableRevisionArticleKeys]);

  const selectedRevisionArticleCount = useMemo(() => {
    if (selectedMonitor?.monitoringType !== "PRESS_RELEASE") return selectedRevisionArticleKeys.length;
    if (!selectedRevisionPressReleaseId) return selectedRevisionArticleKeys.length;
    return selectedRevisionArticleKeys
      .map(parseRevisionArticleSelectionKey)
      .filter((item) => item.pressReleaseId === selectedRevisionPressReleaseId && item.articleId)
      .length;
  }, [selectedMonitor?.monitoringType, selectedRevisionArticleKeys, selectedRevisionPressReleaseId]);

  const runHistory = useMemo<RunHistoryItem[]>(() => {
    if (!rerunRequestedAt) return runHistoryItems;
    return [{
      id: "run-manual",
      scheduledAt: rerunRequestedAt,
      status: "RUNNING",
      foundCount: 0,
      needsReviewCount: 0,
      notificationSentCount: 0,
      notificationDeliveryStatus: "NOT_REQUESTED",
      notificationDeliveryMessage: "",
      notificationSnapshotAvailable: false,
      notificationSnapshotHash: "",
      notificationRecipientSummary: { totalCount: 0, succeededCount: 0, failedCount: 0, pendingCount: 0, sendingCount: 0, manualRetryEligibleRecipientIds: [] },
      notificationRecipientResults: [],
      message: manualRunMessage ?? "수동 실행 요청됨",
    }, ...runHistoryItems];
  }, [manualRunMessage, rerunRequestedAt, runHistoryItems]);

  const visibleArticles = useMemo(
    () => (selectedMonitor?.monitoringType === "PRESS_RELEASE" ? filteredPressReleaseGroups.flatMap((group) => group.articles) : filteredResults),
    [filteredPressReleaseGroups, filteredResults, selectedMonitor?.monitoringType],
  );

  const totalArticleCount = selectedMonitor?.monitoringType === "PRESS_RELEASE"
    ? pressReleaseGroups.reduce((sum, group) => sum + group.relatedArticleCount, 0)
    : keywordArticleTotalCount || resultItems.length;
  const negativeArticleCount = selectedMonitor?.monitoringType === "PRESS_RELEASE"
    ? filteredPressReleaseGroups.reduce((sum, group) => sum + (group.articlesLoaded ? group.articles.filter(matchesNegativeArticle).length : group.negativeRelatedArticleCount), 0)
    : visibleArticles.filter(matchesNegativeArticle).length;
  const revisionArticleCount = selectedMonitor?.monitoringType === "PRESS_RELEASE"
    ? filteredPressReleaseGroups.reduce((sum, group) => sum + (group.articlesLoaded ? group.articles.filter(matchesRevisionDetectedArticle).length : group.revisionDetectedArticleCount), 0)
    : visibleArticles.filter(matchesRevisionDetectedArticle).length;
  const failedRunCount = runHistory.filter((item) => item.status === "FAILED").length;
  const notificationFailureCount = runHistory.filter((item) => item.notificationDeliveryStatus === "FAILED").length;

  const todayArticleDate = getTodayKst();
  const activeFilterCount = [
    articleDate !== todayArticleDate ? "date" : "",
    articleRangeDays !== defaultArticleRangeDays ? "range" : "",
    filter.trim(),
    selectedKeywordFilters.length ? "keyword" : "",
    revisionTrackingFilter === "ACTIVE" ? "revisionTracking" : "",
    linkedArticleFilter || toneFilter !== "ALL" || revisionFilter !== "ALL" ? "article" : "",
  ].filter(Boolean).length;

  const candidateDialogGroup = pressReleaseGroups.find((group) => group.id === candidateDialogGroupId) ?? null;
  const candidateDialogQuery = candidateDialogGroup ? candidateSearchQueries[candidateDialogGroup.id] ?? candidateDialogGroup.suggestedSearchQuery ?? "" : "";

  const applyQuickView = (preset: "ALL" | "NEGATIVE" | "REVISION" | "NEGATIVE_OR_REVISION") => {
    setSelectedKeywordFilters([]);
    if (preset === "ALL") {
      setFilter("");
      setLinkedArticleFilter(null);
      setToneFilter("ALL");
      setRevisionFilter("ALL");
      setRevisionTrackingFilter("ALL");
      return;
    }
    if (preset === "NEGATIVE") {
      setLinkedArticleFilter(null);
      setToneFilter("NEGATIVE");
      setRevisionFilter("ALL");
      return;
    }
    if (preset === "REVISION") {
      setLinkedArticleFilter(null);
      setToneFilter("ALL");
      setRevisionFilter("DETECTED");
      return;
    }
    setLinkedArticleFilter("NEGATIVE_OR_REVISION");
    setToneFilter("NEGATIVE");
    setRevisionFilter("DETECTED");
  };

  const loadPressReleaseGroupArticles = async (groupId: string, force = false) => {
    const targetGroup = pressReleaseGroups.find((group) => group.id === groupId);
    if (!force && targetGroup?.articlesLoaded && !targetGroup.loadError) return targetGroup.articles;
    const requestSeq = groupArticleRequestSeqRef.current + 1;
    groupArticleRequestSeqRef.current = requestSeq;
    groupArticleActiveRequestRef.current[groupId] = requestSeq;
    setGroupArticleLoadingIds((prev) => (prev.includes(groupId) ? prev : [...prev, groupId]));
    try {
      const response = await monitoringApi.post({
        command: "list_press_release_related_articles",
        pressReleaseId: groupId,
        monitoringId: selectedMonitor?.id,
        date: articleDate,
        limit: "ALL",
        articleRangeDays,
      });
      if (groupArticleActiveRequestRef.current[groupId] !== requestSeq) return [];
      if (response?.code !== "200") {
        const message = response?.response?.error_msg ?? "연관 기사 목록을 다시 불러오지 못했습니다.";
        setPressReleaseGroups((prev) => prev.map((group) => (group.id === groupId ? { ...group, loadError: message, articlesLoaded: false } : group)));
        setGroupLoadError(message);
        throw new Error(message);
      }
      const nextArticles = ((response.response?.articles ?? []) as any[]).map((article) => mapPressReleaseArticleItem(article, groupId));
      const totalCount = toFiniteNumber(response.response?.totalCount, nextArticles.length);
      setGroupLoadError(null);
      setPressReleaseGroups((prev) =>
        prev.map((group) =>
          group.id === groupId
            ? {
              ...group,
              articles: nextArticles,
              articleRangeRelatedArticleCount: totalCount,
              negativeRelatedArticleCount: nextArticles.filter(matchesNegativeArticle).length,
              revisionDetectedArticleCount: nextArticles.filter(matchesRevisionDetectedArticle).length,
              articlesLoaded: true,
              loadError: null,
            }
            : group,
        ),
      );
      return nextArticles;
    } finally {
      if (groupArticleActiveRequestRef.current[groupId] === requestSeq) {
        setGroupArticleLoadingIds((prev) => prev.filter((id) => id !== groupId));
      }
    }
  };

  const togglePressReleaseGroup = (groupId: string) => {
    const willExpand = !expandedGroupIds.includes(groupId);
    setExpandedGroupIds((prev) => (prev.includes(groupId) ? prev.filter((id) => id !== groupId) : [...prev, groupId]));
    if (willExpand) {
      loadPressReleaseGroupArticles(groupId).catch(() => undefined);
    }
  };

  const refreshPressReleaseGroupArticles = async (groupId: string) => {
    return loadPressReleaseGroupArticles(groupId, true);
  };

  const openCandidateDialog = (group: PressReleaseGroup) => {
    setCandidateSearchQueries((prev) => ({ ...prev, [group.id]: prev[group.id] ?? group.suggestedSearchQuery ?? "" }));
    setCandidateDialogGroupId(group.id);
  };

  const searchPressReleaseCandidates = async (group: PressReleaseGroup, queryInput: string) => {
    const query = queryInput.trim();
    if (!query) return;
    setCandidateSearchStates((prev) => ({ ...prev, [group.id]: { ...(prev[group.id] ?? { items: [] }), loading: true, searched: true, error: null } }));
    try {
      const response = await monitoringApi.post({ command: "search_press_release_related_candidates", pressReleaseId: group.id, query });
      if (response?.code !== "200") {
        throw new Error(response?.response?.error_msg ?? "보조 후보 기사를 검색하지 못했습니다.");
      }
      setCandidateSearchStates((prev) => ({
        ...prev,
        [group.id]: {
          loading: false,
          approvingArticleIds: [],
          searched: true,
          error: null,
          items: ((response.response?.candidates ?? []) as any[]).map(mapCandidateItem),
        },
      }));
    } catch (error) {
      setCandidateSearchStates((prev) => ({
        ...prev,
        [group.id]: {
          ...(prev[group.id] ?? { items: [] }),
          loading: false,
          approvingArticleIds: [],
          searched: true,
          error: error instanceof Error ? error.message : "보조 후보 기사를 검색하지 못했습니다.",
        },
      }));
    }
  };

  const approvePressReleaseCandidate = async (group: PressReleaseGroup, candidate: PressReleaseCandidate) => {
    setCandidateSearchStates((prev) => ({
      ...prev,
      [group.id]: {
        ...(prev[group.id] ?? { items: [] }),
        loading: false,
        searched: true,
        approvingArticleIds: Array.from(new Set([...(prev[group.id]?.approvingArticleIds ?? []), candidate.id])),
        error: null,
      },
    }));
    try {
      const response = await monitoringApi.post({
        command: "approve_press_release_related_candidates",
        pressReleaseId: group.id,
        candidates: [{
          articleId: candidate.id,
          title: candidate.title,
          url: candidate.url,
          description: candidate.body,
          body: candidate.body,
          bodyPreview: candidate.body,
          publisher: candidate.publisher,
          reporter: candidate.reporter,
          reporterEmail: candidate.reporterEmail,
          publishedAt: candidate.publishedAt,
          modifiedAt: candidate.modifiedAt,
        }],
      });
      if (response?.code !== "200") {
        throw new Error(response?.response?.error_msg ?? "후보 기사를 연관 기사로 반영하지 못했습니다.");
      }
      await refreshPressReleaseGroupArticles(group.id);
      setCandidateSearchStates((prev) => ({
        ...prev,
        [group.id]: {
          ...(prev[group.id] ?? { items: [] }),
          loading: false,
          approvingArticleIds: (prev[group.id]?.approvingArticleIds ?? []).filter((id) => id !== candidate.id),
          searched: true,
          error: null,
          items: (prev[group.id]?.items ?? []).map((item) => (item.id === candidate.id ? { ...item, alreadyRelated: true } : item)),
        },
      }));
    } catch (error) {
      setCandidateSearchStates((prev) => ({
        ...prev,
        [group.id]: {
          ...(prev[group.id] ?? { items: [] }),
          loading: false,
          approvingArticleIds: (prev[group.id]?.approvingArticleIds ?? []).filter((id) => id !== candidate.id),
          searched: true,
          error: error instanceof Error ? error.message : "후보 기사를 연관 기사로 반영하지 못했습니다.",
        },
      }));
    }
  };

  const deleteSelectedMonitor = async () => {
    if (!selectedMonitor) return;
    setSavingMonitor(true);
    setLoadError(null);
    try {
      const response = await monitoringApi.post({ command: "delete_monitoring", userId: props.userState.id, monitoringId: selectedMonitor.id });
      if (response?.code !== "200") {
        setLoadError(response?.response?.error_msg ?? "모니터링 삭제에 실패했습니다.");
        return;
      }
      navigate("/pressRelease", { replace: true });
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : "모니터링 삭제에 실패했습니다.");
    } finally {
      setSavingMonitor(false);
    }
  };

  const runSelectedMonitorManually = async () => {
    if (!selectedMonitor || rerunDisabled) return;
    setLoadError(null);
    setManualRunMessage(null);
    setRerunningMonitorIds((prev) => (prev.includes(selectedMonitor.id) ? prev : [...prev, selectedMonitor.id]));
    try {
      const response = await monitoringApi.post({ command: "run_monitorings_manually", monitoringIds: [selectedMonitor.id] });
      if (response?.code !== "200") {
        setLoadError(response?.response?.error_msg ?? "수동 실행 요청에 실패했습니다.");
        return;
      }
      const jobs = Array.isArray(response.response?.jobs) ? response.response.jobs : [];
      const enqueuedJobCount = toFiniteNumber(response.response?.enqueuedJobCount ?? jobs.length, 0);
      setManualRunMessage(["수동 실행 요청됨", enqueuedJobCount ? `검색 작업 ${enqueuedJobCount}건` : ""].filter(Boolean).join(" · "));
      setRerunRequestedAt(formatRunDateTime(new Date()));
      updateViewMode("runs");
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : "수동 실행 요청에 실패했습니다.");
    } finally {
      setRerunningMonitorIds((prev) => prev.filter((id) => id !== selectedMonitor.id));
    }
  };

  const retryNotificationDelivery = async (run: RunHistoryItem) => {
    if (!selectedMonitor?.id || !run.notificationSnapshotAvailable) return;
    const retryTargetCount = getManualRetryEligibleNotificationRecipients(run).length;
    if (retryTargetCount === 0 || getNotificationDeliveryInProgressCount(run) > 0) return;

    const requestKey = getNotificationRetryRequestKey(selectedMonitor.id, run);
    const manualRetryRequestId = notificationRetryRequestIdsRef.current.get(requestKey) ?? crypto.randomUUID();
    notificationRetryRequestIdsRef.current.set(requestKey, manualRetryRequestId);
    const request = {
      command: "retry_monitoring_notification_delivery",
      monitoringId: selectedMonitor.id,
      runId: run.id,
      scheduledAt: run.scheduledAt,
      manualRetryRequestId,
    };
    setNotificationRetryingRunIds((prev) => (prev.includes(run.id) ? prev : [...prev, run.id]));
    setNotificationRetryErrors((prev) => {
      const next = { ...prev };
      delete next[run.id];
      return next;
    });
    try {
      let response: any;
      try {
        response = await monitoringApi.post(request);
      } catch {
        response = await monitoringApi.post(request);
      }
      if (response?.code !== "200") {
        notificationRetryRequestIdsRef.current.delete(requestKey);
        setNotificationRetryErrors((prev) => ({
          ...prev,
          [run.id]: "알림 재시도 요청을 완료하지 못했습니다. 잠시 후 다시 시도하세요.",
        }));
        return;
      }
      notificationRetryRequestIdsRef.current.delete(requestKey);
      setNotificationRetryMessages((prev) => ({
        ...prev,
        [run.id]: getNotificationRetryQueuedMessage(response.response),
      }));
      await loadRunHistory(selectedMonitor.id);
    } catch {
      setNotificationRetryErrors((prev) => ({
        ...prev,
        [run.id]: "알림 재시도 요청을 완료하지 못했습니다. 잠시 후 다시 시도하세요.",
      }));
    } finally {
      setNotificationRetryingRunIds((prev) => prev.filter((id) => id !== run.id));
    }
  };

  const toggleRevisionArticleSelection = (article: ResultItem) => {
    if (article.revisionTrackingStatus === "ACTIVE") return;
    if (selectedMonitor?.monitoringType === "PRESS_RELEASE" && !article.pressReleaseId) {
      setLoadError("보도자료 연관 기사 정보를 확인하지 못했습니다.");
      return;
    }
    const articleKey = getRevisionArticleSelectionKey(article, selectedMonitor?.monitoringType === "PRESS_RELEASE");
    if (selectedRevisionArticleKeys.includes(articleKey)) {
      setSelectedRevisionArticleKeys((prev) => prev.filter((key) => key !== articleKey));
      return;
    }
    if (selectedMonitor?.monitoringType === "PRESS_RELEASE" && selectedRevisionPressReleaseId && selectedRevisionPressReleaseId !== article.pressReleaseId) {
      setManualRunMessage("다른 보도자료의 기사를 선택해 기존 선택을 초기화했습니다.");
      setSelectedRevisionArticleKeys([articleKey]);
      return;
    }
    setSelectedRevisionArticleKeys((prev) => (prev.includes(articleKey) ? prev : [...prev, articleKey]));
  };

  const selectAllVisibleRevisionArticles = () => {
    setSelectedRevisionArticleKeys(selectableRevisionArticleKeys);
  };

  const clearSelectedRevisionArticles = () => {
    setSelectedRevisionArticleKeys([]);
  };

  const applyArticleRevisionTrackingState = (articleId: string, tracking: any, pressReleaseId?: string) => {
    const nextStatus: ArticleRevisionTrackingStatus =
      tracking?.status === "ACTIVE" || tracking?.status === "EXPIRED" || tracking?.status === "STOPPED"
        ? tracking.status
        : "";
    const patch = {
      revisionTrackingStatus: nextStatus,
      revisionTrackingHours: toFiniteNumber(tracking?.trackingHours, 0),
      revisionTrackingStartedAt: tracking?.trackingStartedAt || "",
      revisionTrackingExpiresAt: tracking?.trackingExpiresAt || "",
      revisionTrackingNotifyEnabled: tracking?.notifyOnRevisionDetected === true,
    };
    const pdfArtifacts = mapRevisionPdfArtifacts(tracking?.pdfArtifacts);
    const applyPatch = (item: ResultItem): ResultItem => ({
      ...item,
      ...patch,
      ...(pdfArtifacts ? { revisionPdfArtifacts: mergeRevisionPdfArtifacts(item.revisionPdfArtifacts, pdfArtifacts) } : {}),
    });
    setResultItems((prev) => prev.map((item) => (item.id === articleId && (!pressReleaseId || item.pressReleaseId === pressReleaseId) ? applyPatch(item) : item)));
    setPressReleaseGroups((prev) =>
      prev.map((group) => ({
        ...group,
        articles: group.articles.map((item) =>
          item.id === articleId && (!pressReleaseId || (item.pressReleaseId || group.id) === pressReleaseId)
            ? applyPatch({ ...item, pressReleaseId: item.pressReleaseId || group.id })
            : item,
        ),
      })),
    );
  };

  const applyArticleRevisionPdfArtifacts = useCallback((articleId: string, pdfArtifacts?: ArticleRevisionPdfArtifacts, pressReleaseId?: string) => {
    if (!pdfArtifacts) return;
    const applyPatch = (item: ResultItem): ResultItem => ({
      ...item,
      revisionPdfArtifacts: mergeRevisionPdfArtifacts(item.revisionPdfArtifacts, pdfArtifacts),
    });
    setResultItems((prev) => prev.map((item) => (item.id === articleId && (!pressReleaseId || item.pressReleaseId === pressReleaseId) ? applyPatch(item) : item)));
    setPressReleaseGroups((prev) =>
      prev.map((group) => ({
        ...group,
        articles: group.articles.map((item) =>
          item.id === articleId && (!pressReleaseId || (item.pressReleaseId || group.id) === pressReleaseId)
            ? applyPatch({ ...item, pressReleaseId: item.pressReleaseId || group.id })
            : item,
        ),
      })),
    );
  }, []);

  const openRevisionTrackingDialog = (article: ResultItem) => {
    if (!selectedMonitor || !supportsRevisionTracking(selectedMonitor)) return;
    if (selectedMonitor.monitoringType === "PRESS_RELEASE" && !article.pressReleaseId) {
      setLoadError("보도자료 연관 기사 정보를 확인하지 못했습니다.");
      return;
    }
    setRevisionTrackingTargetArticle(article);
    setRevisionTrackingHours(article.revisionTrackingHours || 6);
    setRevisionTrackingNotifyEnabled(article.revisionTrackingNotifyEnabled === true);
    setRevisionTrackingError(null);
    setRevisionTrackingDialogOpen(true);
  };

  const startArticleRevisionTracking = async () => {
    if (!selectedMonitor?.id || !revisionTrackingTargetArticle) return;
    setRevisionTrackingSaving(true);
    setRevisionTrackingError(null);
    try {
      const pressReleaseId = selectedMonitor.monitoringType === "PRESS_RELEASE" ? revisionTrackingTargetArticle.pressReleaseId : "";
      if (selectedMonitor.monitoringType === "PRESS_RELEASE" && !pressReleaseId) {
        throw new Error("보도자료 연관 기사 정보를 확인하지 못했습니다.");
      }
      const response = await monitoringApi.post({
        command: "start_article_revision_tracking",
        monitoringId: selectedMonitor.id,
        ...(pressReleaseId ? { pressReleaseId } : {}),
        articleId: revisionTrackingTargetArticle.id,
        trackingHours: revisionTrackingHours,
        notifyOnRevisionDetected: revisionTrackingNotifyEnabled,
        userId: props.userState.id,
      });
      if (response?.code !== "200") {
        throw new Error(response?.response?.error_msg ?? "기사 수정 추적을 시작하지 못했습니다.");
      }
      applyArticleRevisionTrackingState(revisionTrackingTargetArticle.id, response.response?.revisionTracking, pressReleaseId || undefined);
      setManualRunMessage("기사 수정 추적을 시작했습니다.");
      setRevisionTrackingDialogOpen(false);
    } catch (error) {
      setRevisionTrackingError(error instanceof Error ? error.message : "기사 수정 추적을 시작하지 못했습니다.");
    } finally {
      setRevisionTrackingSaving(false);
    }
  };

  const openBatchRevisionTrackingDialog = () => {
    if (!selectedRevisionArticleKeys.length) return;
    setBatchRevisionTrackingHours(6);
    setBatchRevisionTrackingNotifyEnabled(false);
    setBatchRevisionTrackingError(null);
    setBatchRevisionTrackingSummary(null);
    setBatchRevisionDialogOpen(true);
  };

  const startBatchArticleRevisionTracking = async () => {
    if (!selectedMonitor?.id || !selectedRevisionArticleKeys.length) return;
    setBatchRevisionTrackingSaving(true);
    setBatchRevisionTrackingError(null);
    setBatchRevisionTrackingSummary(null);
    try {
      const pressReleaseId = selectedMonitor.monitoringType === "PRESS_RELEASE" ? selectedRevisionPressReleaseId : "";
      if (selectedMonitor.monitoringType === "PRESS_RELEASE" && !pressReleaseId) {
        throw new Error("보도자료를 하나 선택한 뒤 일괄 추적을 시작하세요.");
      }
      const articleIds = Array.from(new Set(
        selectedRevisionArticleKeys
          .map(parseRevisionArticleSelectionKey)
          .filter((item) => !pressReleaseId || item.pressReleaseId === pressReleaseId)
          .map((item) => item.articleId),
      ));
      if (!articleIds.length) {
        throw new Error("수정 추적을 시작할 기사가 없습니다.");
      }
      const response = await monitoringApi.post({
        command: "start_article_revision_tracking_batch",
        monitoringId: selectedMonitor.id,
        ...(pressReleaseId ? { pressReleaseId } : {}),
        articleIds,
        trackingHours: batchRevisionTrackingHours,
        notifyOnRevisionDetected: batchRevisionTrackingNotifyEnabled,
        userId: props.userState.id,
      });
      if (response?.code !== "200") {
        throw new Error(response?.response?.error_msg ?? "선택 기사 수정 추적을 시작하지 못했습니다.");
      }
      const batch = response.response?.revisionTrackingBatch ?? {};
      const results = Array.isArray(batch.results) ? batch.results : [];
      results.forEach((result: any) => {
        if (result?.status === "STARTED" && result.revisionTracking) {
          applyArticleRevisionTrackingState(result.articleId, result.revisionTracking, pressReleaseId || undefined);
          applyArticleRevisionPdfArtifacts(result.articleId, mapRevisionPdfArtifacts(result.pdfArtifacts), pressReleaseId || undefined);
        }
      });
      const completedArticleKeys = new Set(
        results
          .filter((result: any) => result?.status === "STARTED" || String(result?.status || "").startsWith("SKIPPED"))
          .map((result: any) => (pressReleaseId ? getRevisionArticleSelectionKey({ id: result.articleId, pressReleaseId }, true) : result.articleId)),
      );
      setSelectedRevisionArticleKeys((prev) => prev.filter((key) => !completedArticleKeys.has(key)));
      const summary = `시작 ${toFiniteNumber(batch.succeededCount, 0)}건, 제외 ${toFiniteNumber(batch.skippedCount, 0)}건, 실패 ${toFiniteNumber(batch.failedCount, 0)}건`;
      setManualRunMessage(null);
      setBatchRevisionTrackingToast({ id: Date.now(), message: `선택 기사 수정 추적 처리 완료 · ${summary}` });
      if (toFiniteNumber(batch.failedCount, 0) > 0 || toFiniteNumber(batch.skippedCount, 0) > 0) {
        setBatchRevisionTrackingSummary(summary);
      } else {
        setBatchRevisionDialogOpen(false);
      }
    } catch (error) {
      setBatchRevisionTrackingError(error instanceof Error ? error.message : "선택 기사 수정 추적을 시작하지 못했습니다.");
    } finally {
      setBatchRevisionTrackingSaving(false);
    }
  };

  const stopArticleRevisionTracking = async (article: ResultItem) => {
    if (!selectedMonitor?.id || !article.id) return;
    if (selectedMonitor.monitoringType === "PRESS_RELEASE" && !article.pressReleaseId) {
      setLoadError("보도자료 연관 기사 정보를 확인하지 못했습니다.");
      return;
    }
    const articleKey = getRevisionArticleSelectionKey(article, selectedMonitor.monitoringType === "PRESS_RELEASE");
    setRevisionTrackingStoppingKeys((prev) => (prev.includes(articleKey) ? prev : [...prev, articleKey]));
    try {
      const pressReleaseId = selectedMonitor.monitoringType === "PRESS_RELEASE" ? article.pressReleaseId : "";
      const response = await monitoringApi.post({
        command: "stop_article_revision_tracking",
        monitoringId: selectedMonitor.id,
        ...(pressReleaseId ? { pressReleaseId } : {}),
        articleId: article.id,
        userId: props.userState.id,
      });
      if (response?.code !== "200") {
        setLoadError(response?.response?.error_msg ?? "기사 수정 추적을 중지하지 못했습니다.");
        return;
      }
      applyArticleRevisionTrackingState(article.id, response.response?.revisionTracking, pressReleaseId || undefined);
      setManualRunMessage("기사 수정 추적을 중지했습니다.");
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : "기사 수정 추적을 중지하지 못했습니다.");
    } finally {
      setRevisionTrackingStoppingKeys((prev) => prev.filter((key) => key !== articleKey));
    }
  };

  const openRevisionDiff = async (article: ResultItem) => {
    if (!selectedMonitor?.id || !supportsRevisionTracking(selectedMonitor)) return;
    setRevisionDialogOpen(true);
    setRevisionTargetArticle(article);
    setRevisionDiff(null);
    setRevisionDiffError(null);
    setRevisionDiffLoading(true);
    try {
      const pressReleaseId = selectedMonitor.monitoringType === "PRESS_RELEASE" ? article.pressReleaseId : "";
      if (selectedMonitor.monitoringType === "PRESS_RELEASE" && !pressReleaseId) {
        throw new Error("보도자료 연관 기사 정보를 확인하지 못했습니다.");
      }
      const response = await monitoringApi.post({
        command: "get_article_revision_diff",
        monitoringId: selectedMonitor.id,
        ...(pressReleaseId ? { pressReleaseId } : {}),
        articleId: article.id,
      });
      if (response?.code !== "200") {
        setRevisionDiffError(response?.response?.error_msg ?? "수정 비교 정보를 불러오지 못했습니다.");
        return;
      }
      setRevisionDiff(response.response?.revision ?? null);
    } catch (error) {
      setRevisionDiffError(error instanceof Error ? error.message : "수정 비교 정보를 불러오지 못했습니다.");
    } finally {
      setRevisionDiffLoading(false);
    }
  };

  const currentRevisionTargetArticle = useMemo(() => {
    if (!revisionTargetArticle) return null;
    if (selectedMonitor?.monitoringType === "PRESS_RELEASE") {
      return pressReleaseGroups
        .flatMap((group) => group.articles.map((article) => ({ ...article, pressReleaseId: article.pressReleaseId || group.id })))
        .find((article) => article.id === revisionTargetArticle.id && article.pressReleaseId === revisionTargetArticle.pressReleaseId)
        ?? revisionTargetArticle;
    }
    return resultItems.find((item) => item.id === revisionTargetArticle.id) ?? revisionTargetArticle;
  }, [pressReleaseGroups, resultItems, revisionTargetArticle, selectedMonitor?.monitoringType]);

  const currentRevisionTargetArticleKey = currentRevisionTargetArticle
    ? getRevisionArticleSelectionKey(currentRevisionTargetArticle, selectedMonitor?.monitoringType === "PRESS_RELEASE")
    : "";

  const loadRevisionPdfArtifactsForArticle = useCallback(async (
    article: ResultItem,
    usePressReleaseContext: boolean,
    options: { force?: boolean; silent?: boolean } = {},
  ) => {
    if (!selectedMonitor?.id) return;
    const articleWithScope = { ...article, pressReleaseId: usePressReleaseContext ? article.pressReleaseId : "" };
    const articleKey = getRevisionArticleSelectionKey(articleWithScope, usePressReleaseContext);
    if (!options.force && revisionPdfFetchedKeysRef.current.has(articleKey)) return;
    if (usePressReleaseContext && !articleWithScope.pressReleaseId) {
      setRevisionPdfErrors((prev) => ({ ...prev, [articleKey]: "보도자료 연관 기사 정보를 확인하지 못했습니다." }));
      return;
    }
    revisionPdfFetchedKeysRef.current.add(articleKey);
    if (!options.silent) {
      setRevisionPdfLoadingKeys((prev) => (prev.includes(articleKey) ? prev : [...prev, articleKey]));
    }
    try {
      const response = await monitoringApi.post({
        command: "list_article_revision_pdf_artifacts",
        monitoringId: selectedMonitor.id,
        articleId: articleWithScope.id,
        ...(articleWithScope.pressReleaseId ? { pressReleaseId: articleWithScope.pressReleaseId } : {}),
      });
      if (response?.code !== "200") {
        revisionPdfFetchedKeysRef.current.delete(articleKey);
        setRevisionPdfErrors((prev) => ({
          ...prev,
          [articleKey]: response?.response?.error_msg ?? "PDF 증빙 상태를 불러오지 못했습니다.",
        }));
        return;
      }
      setRevisionPdfErrors((prev) => {
        const { [articleKey]: _removed, ...rest } = prev;
        return rest;
      });
      applyArticleRevisionPdfArtifacts(articleWithScope.id, mapRevisionPdfArtifacts(response.response?.artifacts), articleWithScope.pressReleaseId || undefined);
    } catch (error) {
      revisionPdfFetchedKeysRef.current.delete(articleKey);
      setRevisionPdfErrors((prev) => ({
        ...prev,
        [articleKey]: error instanceof Error ? error.message : "PDF 증빙 상태를 불러오지 못했습니다.",
      }));
    } finally {
      setRevisionPdfLoadingKeys((prev) => prev.filter((key) => key !== articleKey));
    }
  }, [applyArticleRevisionPdfArtifacts, selectedMonitor?.id]);

  useEffect(() => {
    if (!selectedMonitor?.id || !revisionTrackingEnabled || viewMode !== "articles") return;
    const usePressReleaseContext = selectedMonitor.monitoringType === "PRESS_RELEASE";
    const targets = visibleArticles
      .filter((article) => article.revisionTrackingStatus || article.revisionDetected)
      .map((article) => ({ ...article, pressReleaseId: usePressReleaseContext ? article.pressReleaseId : "" }))
      .filter((article) => !usePressReleaseContext || Boolean(article.pressReleaseId))
      .filter((article) => {
        if (hasRevisionPdfArtifacts(article.revisionPdfArtifacts) && !hasInFlightRevisionPdfArtifacts(article.revisionPdfArtifacts)) {
          return false;
        }
        const key = getRevisionArticleSelectionKey(article, usePressReleaseContext);
        return !revisionPdfFetchedKeysRef.current.has(key);
      });
    if (!targets.length) return;
    targets.forEach((article) => {
      loadRevisionPdfArtifactsForArticle(article, usePressReleaseContext).catch(() => undefined);
    });
  }, [loadRevisionPdfArtifactsForArticle, revisionTrackingEnabled, selectedMonitor?.id, selectedMonitor?.monitoringType, viewMode, visibleArticles]);

  const downloadRevisionPdfArtifact = async (article: ResultItem, artifact: ArticleRevisionPdfArtifact) => {
    if (!selectedMonitor?.id) return;
    const usePressReleaseContext = selectedMonitor.monitoringType === "PRESS_RELEASE";
    const articleKey = getRevisionArticleSelectionKey(article, usePressReleaseContext);
    const actionKey = getRevisionPdfArtifactActionKey(article, artifact, usePressReleaseContext);
    setRevisionPdfActionKeys((prev) => (prev.includes(actionKey) ? prev : [...prev, actionKey]));
    setRevisionPdfErrors((prev) => {
      const { [articleKey]: _removed, ...rest } = prev;
      return rest;
    });
    try {
      const pressReleaseId = usePressReleaseContext ? article.pressReleaseId : "";
      if (usePressReleaseContext && !pressReleaseId) {
        throw new Error("보도자료 연관 기사 정보를 확인하지 못했습니다.");
      }
      const response = await monitoringApi.post({
        command: "get_article_revision_pdf_download_url",
        monitoringId: selectedMonitor.id,
        articleId: article.id,
        artifactId: artifact.artifactId,
        ...(pressReleaseId ? { pressReleaseId } : {}),
      });
      if (response?.code !== "200") {
        const responseErrorMessage = response?.response?.error_msg;
        throw new Error(
          typeof responseErrorMessage === "string" && responseErrorMessage.trim()
            ? responseErrorMessage.trim()
            : "PDF 증빙 다운로드 URL을 발급하지 못했습니다."
        );
      }
      applyArticleRevisionPdfArtifacts(article.id, mapRevisionPdfArtifacts([response.response?.artifact]), pressReleaseId || undefined);
      const downloadUrl = response.response?.downloadUrl || "";
      if (!downloadUrl) {
        const artifactMessage = response.response?.artifact?.message;
        throw new Error(
          typeof artifactMessage === "string" && artifactMessage.trim()
            ? artifactMessage.trim()
            : "아직 다운로드할 수 있는 PDF가 없습니다."
        );
      }
      window.open(downloadUrl, "_self", "noopener,noreferrer");
    } catch (error) {
      setRevisionPdfErrors((prev) => ({
        ...prev,
        [articleKey]: error instanceof Error ? error.message : "PDF 증빙을 다운로드하지 못했습니다.",
      }));
    } finally {
      setRevisionPdfActionKeys((prev) => prev.filter((key) => key !== actionKey));
    }
  };

  const retryRevisionPdfArtifact = async (article: ResultItem, artifact: ArticleRevisionPdfArtifact) => {
    if (!selectedMonitor?.id) return;
    const usePressReleaseContext = selectedMonitor.monitoringType === "PRESS_RELEASE";
    const articleKey = getRevisionArticleSelectionKey(article, usePressReleaseContext);
    const actionKey = getRevisionPdfArtifactActionKey(article, artifact, usePressReleaseContext);
    setRevisionPdfActionKeys((prev) => (prev.includes(actionKey) ? prev : [...prev, actionKey]));
    setRevisionPdfErrors((prev) => {
      const { [articleKey]: _removed, ...rest } = prev;
      return rest;
    });
    try {
      const pressReleaseId = usePressReleaseContext ? article.pressReleaseId : "";
      if (usePressReleaseContext && !pressReleaseId) {
        throw new Error("보도자료 연관 기사 정보를 확인하지 못했습니다.");
      }
      const response = await monitoringApi.post({
        command: "retry_article_revision_pdf_generation",
        monitoringId: selectedMonitor.id,
        articleId: article.id,
        artifactId: artifact.artifactId,
        ...(pressReleaseId ? { pressReleaseId } : {}),
      });
      if (response?.code !== "200") {
        throw new Error(response?.response?.error_msg ?? "PDF 증빙 생성을 재시도하지 못했습니다.");
      }
      applyArticleRevisionPdfArtifacts(article.id, mapRevisionPdfArtifacts([response.response?.artifact]), pressReleaseId || undefined);
      setManualRunMessage("PDF 증빙 생성을 다시 요청했습니다.");
    } catch (error) {
      setRevisionPdfErrors((prev) => ({
        ...prev,
        [articleKey]: error instanceof Error ? error.message : "PDF 증빙 생성을 재시도하지 못했습니다.",
      }));
    } finally {
      setRevisionPdfActionKeys((prev) => prev.filter((key) => key !== actionKey));
    }
  };

  const refreshRevisionPdfArtifacts = async (article: ResultItem) => {
    if (!selectedMonitor?.id) return;
    const usePressReleaseContext = selectedMonitor.monitoringType === "PRESS_RELEASE";
    await loadRevisionPdfArtifactsForArticle(article, usePressReleaseContext, { force: true });
  };

  if (!selectedMonitor) {
    return (
      <Box sx={pageSx}>
        <Paper variant="outlined" sx={{ ...panelSx, maxWidth: 720, mx: "auto", mt: 6 }}>
          <Stack spacing={1.25} sx={{ p: 2.5 }}>
            <Typography sx={pageTitleSx}>모니터링 상세</Typography>
            <Typography sx={mutedTextSx}>{loading ? "모니터링 정보를 확인하는 중입니다." : loadError ?? "유효한 모니터링을 찾지 못했습니다."}</Typography>
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" sx={outlineButtonSx} onClick={() => navigate("/pressRelease", { replace: true })}>목록으로</Button>
              <Button variant="contained" sx={primaryButtonSx} onClick={() => navigate("/monitoringDashboard")}>대시보드</Button>
            </Stack>
          </Stack>
        </Paper>
      </Box>
    );
  }

  const isPressReleaseMonitor = selectedMonitor.monitoringType === "PRESS_RELEASE";
  const usesUnifiedDetailStyle = isPressReleaseMonitor || selectedMonitor.monitoringType === "KEYWORD";
  const groupCountLabel = isPressReleaseMonitor
    ? `${filteredPressReleaseGroups.length}/${pressReleaseGroups.length}개 그룹`
    : "키워드 모니터링";
  const latestRun = runHistory[0];
  const articleRangeLabel = getArticleRangeLabel(articleRangeDays, articleDate);
  const articlePanelTitle = `${articleRangeLabel} 표시 대상`;
  const executionPolicyRows = [
    { label: "실행 주기", value: selectedMonitor.status === "ACTIVE" ? `${selectedMonitor.intervalMinutes}분마다` : "중지됨" },
    { label: "실행 기간", value: formatMonitoringActivePeriod(selectedMonitor) },
    ...(selectedMonitor.deactivatedAt ? [{ label: "비활성화 시각", value: formatDateTime(selectedMonitor.deactivatedAt) }] : []),
    { label: "보관 기간", value: `${selectedMonitor.retentionDays}일` },
    { label: "긍부정 판단", value: selectedMonitor.sentimentEnabled ? "사용" : "미사용" },
    { label: "수정 추적", value: revisionTrackingEnabled ? "기사별 수동 시작" : "미지원" },
  ];
  const notificationPolicyRows = [
    { label: "알림", value: selectedMonitor.notificationEnabled ? getNotificationAudienceLabel(selectedMonitor) : "미사용" },
    { label: "모니터링 타입", value: selectedMonitor.monitoringType === "PRESS_RELEASE" ? "보도자료" : "키워드" },
  ];
  const sentimentKeywordDisplay = buildSentimentKeywordDisplayState(selectedMonitor);
  const navigateToMonitor = (monitoringId: string) => {
    if (!monitoringId || monitoringId === selectedMonitor.id) return;
    navigate(buildMonitoringDetailUrl({
      monitoringId,
      tab: viewMode,
      date: articleDate,
      articleRangeDays,
    }));
  };
  const isQuickViewActive = (preset: "ALL" | "NEGATIVE" | "REVISION" | "NEGATIVE_OR_REVISION") => {
    if (preset === "ALL") return !linkedArticleFilter && toneFilter === "ALL" && revisionFilter === "ALL";
    if (preset === "NEGATIVE") return linkedArticleFilter === "NEGATIVE_ONLY" || (!linkedArticleFilter && toneFilter === "NEGATIVE" && revisionFilter === "ALL");
    if (preset === "REVISION") return linkedArticleFilter === "REVISION_ONLY" || (!linkedArticleFilter && toneFilter === "ALL" && revisionFilter === "DETECTED");
    return linkedArticleFilter === "NEGATIVE_OR_REVISION" || (!linkedArticleFilter && toneFilter === "NEGATIVE" && revisionFilter === "DETECTED");
  };

  return (
    <Box sx={pageSx}>
      <Box sx={pageContentSx}>
        <Stack spacing={1.5}>
          <Box sx={topActionRowSx}>
            <Button
              startIcon={<ArrowBackIcon fontSize="small" />}
              variant="outlined"
              sx={topBackButtonSx}
              onClick={() => navigate("/pressRelease", { replace: true })}
            >
              모니터링 목록 보기
            </Button>
            <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ xs: "stretch", md: "center" }} sx={topActionControlsSx}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }} sx={monitorSwitchWrapSx}>
                <Typography sx={topActionLabelSx}>모니터링 이동</Typography>
                <TextField
                  select
                  value={selectedMonitor.id}
                  size="small"
                  onChange={(event) => navigateToMonitor(event.target.value)}
                  SelectProps={{ inputProps: { "aria-label": "모니터링 이동" } }}
                  sx={monitorSwitchFieldSx}
                >
                  {monitors.map((monitor) => (
                    <MenuItem key={monitor.id} value={monitor.id}>
                      {monitor.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
              <Button variant="outlined" sx={topActionButtonSx} onClick={() => navigate("/monitoringCreate", { state: { mode: "edit", monitoringId: selectedMonitor.id, monitoring: selectedMonitor } })}>설정 편집</Button>
              <Button
                startIcon={<RerunPlayIcon />}
                variant="contained"
                sx={rerunButtonSx}
                disabled={rerunDisabled}
                title={selectedMonitorExpired ? "기간 만료로 중지된 모니터링입니다. 종료일을 수정한 뒤 다시 실행할 수 있습니다." : undefined}
                onClick={runSelectedMonitorManually}
              >
                {selectedMonitorRerunning ? "실행 요청 중" : "다시 실행"}
              </Button>
            </Stack>
          </Box>

          <Paper variant="outlined" sx={headerPanelSx}>
            <Box sx={headerTopSx}>
              <Box sx={headerInfoSx}>
                <Stack direction="row" spacing={1} alignItems="center" sx={headerTitleRowSx}>
                  <Typography sx={pageTitleSx}>{selectedMonitor.name}</Typography>
                  <Chip label={selectedMonitor.monitoringType === "PRESS_RELEASE" ? "보도자료" : "키워드"} size="small" sx={monitorTypeBadgeSx} />
                  <StatusChip status={selectedMonitor.status} />
                  {activeFilterCount > 0 && <Chip label={`필터 ${activeFilterCount}개`} size="small" sx={filterCountBadgeSx} />}
                </Stack>
                <Typography sx={mutedTextSx}>{selectedMonitor.description || "설명 없이 생성된 모니터링입니다."}</Typography>
              </Box>
            </Box>
            <Box sx={metricRailSx}>
              <MetricCard label="표시 기사" value={`${visibleArticles.length}건`} secondaryValue={`/ ${totalArticleCount}건`} sub={groupCountLabel} />
              <MetricCard label="부정" value={`${negativeArticleCount}건`} tone={negativeArticleCount ? "danger" : "normal"} />
              <MetricCard label="수정 감지" value={`${revisionArticleCount}건`} tone={revisionArticleCount ? "warning" : "normal"} />
              {failedRunCount > 0 && <MetricCard label="실행 실패" value={`${failedRunCount}건`} tone="danger" />}
              {notificationFailureCount > 0 && <MetricCard label="알림 실패" value={`${notificationFailureCount}건`} tone="danger" />}
            </Box>
            {(loadError || manualRunMessage || groupLoadError || runHistoryLoadError) && (
              <Typography sx={{ ...noticeTextSx, color: loadError || groupLoadError || runHistoryLoadError ? "#b91c1c" : "#047857" }}>
                {[loadError, manualRunMessage, groupLoadError, runHistoryLoadError].filter(Boolean).join(" / ")}
              </Typography>
            )}
            {selectedMonitorExpired && (
              <Typography sx={{ ...noticeTextSx, color: "#b45309" }}>
                기간 만료로 중지된 모니터링입니다. 종료일을 미래 날짜로 수정하거나 무기한으로 변경한 뒤 활성화할 수 있습니다.
              </Typography>
            )}
          </Paper>

        <Box sx={usesUnifiedDetailStyle ? pressReleaseWorkbenchSx : workbenchSx}>
          <Paper variant="outlined" sx={usesUnifiedDetailStyle ? pressReleaseFilterPanelSx : filterPanelSx}>
            <Stack spacing={usesUnifiedDetailStyle ? 0 : 1.1} sx={usesUnifiedDetailStyle ? pressReleaseFilterStackSx : undefined}>
              <Stack direction="row" spacing={0.7} alignItems="center">
                <Box component="img" src={monitoringDetailIcons.filter} alt="" aria-hidden sx={pressReleaseFilterIconSx} />
                <Typography sx={sectionTitleSx}>필터</Typography>
              </Stack>
              <Box sx={usesUnifiedDetailStyle ? pressReleaseFilterSectionSx : undefined}>
                {usesUnifiedDetailStyle && <Typography sx={pressReleaseFilterLabelSx}>검색</Typography>}
                <TextField
                  value={filter}
                  onChange={(event) => setFilter(event.target.value)}
                  placeholder="제목, 본문, 매체, 기자 검색"
                  size="small"
                  fullWidth
                  sx={usesUnifiedDetailStyle ? pressReleaseFilterFieldSx : filterFieldSx}
                />
              </Box>
              <Box sx={usesUnifiedDetailStyle ? pressReleaseFilterSectionSx : undefined}>
                <Typography sx={usesUnifiedDetailStyle ? pressReleaseFilterLabelSx : filterLabelSx}>조회 기준</Typography>
                <Stack spacing={usesUnifiedDetailStyle ? 1 : 0.75} sx={usesUnifiedDetailStyle ? pressReleaseDateControlSx : undefined}>
                  {usesUnifiedDetailStyle ? (
                    <Box sx={pressReleaseRangeOptionsSx}>
                      {articleRangeDayOptions.map((option) => (
                        <Button
                          key={option.value}
                          size="small"
                          sx={pressReleaseRadioFilterButtonSx(articleRangeDays === option.value)}
                          onClick={() => updateArticleRangeDays(option.value)}
                        >
                          {getArticleRangeOptionLabel(option.value, articleDate)}
                        </Button>
                      ))}
                    </Box>
                  ) : (
                    <>
                      <TextField
                        type="date"
                        value={articleDate}
                        onChange={(event) => updateArticleDate(event.target.value)}
                        size="small"
                        label="기준일"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        sx={filterFieldSx}
                      />
                      <TextField
                        select
                        value={articleRangeDays}
                        onChange={(event) => updateArticleRangeDays(event.target.value)}
                        size="small"
                        label="기사 조회 기간"
                        fullWidth
                        sx={filterFieldSx}
                      >
                        {articleRangeDayOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </>
                  )}
                </Stack>
              </Box>
              <Box sx={usesUnifiedDetailStyle ? pressReleaseFilterSectionSx : undefined}>
                <Typography sx={usesUnifiedDetailStyle ? pressReleaseFilterLabelSx : filterLabelSx}>빠른 보기</Typography>
                <Stack direction="row" spacing={usesUnifiedDetailStyle ? 0 : 0.5} sx={usesUnifiedDetailStyle ? { ...pressReleaseRangeOptionsSx, alignItems: "flex-start" } : { flexWrap: "wrap", rowGap: 0.5 }}>
                  <Button size="small" sx={usesUnifiedDetailStyle ? pressReleaseRadioFilterButtonSx(isQuickViewActive("ALL")) : radioFilterButtonSx(isQuickViewActive("ALL"))} onClick={() => applyQuickView("ALL")}>전체</Button>
                  {selectedMonitor.sentimentEnabled && (
                    <Button size="small" sx={usesUnifiedDetailStyle ? pressReleaseRadioFilterButtonSx(isQuickViewActive("NEGATIVE")) : radioFilterButtonSx(isQuickViewActive("NEGATIVE"))} onClick={() => applyQuickView("NEGATIVE")}>부정</Button>
                  )}
                  {revisionTrackingEnabled && (
                    <Button size="small" sx={usesUnifiedDetailStyle ? pressReleaseRadioFilterButtonSx(isQuickViewActive("REVISION")) : radioFilterButtonSx(isQuickViewActive("REVISION"))} onClick={() => applyQuickView("REVISION")}>수정</Button>
                  )}
                  {revisionTrackingEnabled && (
                    <Button
                      size="small"
                      sx={usesUnifiedDetailStyle ? pressReleaseRadioFilterButtonSx(revisionTrackingFilter === "ACTIVE") : radioFilterButtonSx(revisionTrackingFilter === "ACTIVE")}
                      onClick={() => setRevisionTrackingFilter((prev) => (prev === "ACTIVE" ? "ALL" : "ACTIVE"))}
                    >
                      추적 중
                    </Button>
                  )}
                  {selectedMonitor.sentimentEnabled && revisionTrackingEnabled && (
                    <Button size="small" sx={usesUnifiedDetailStyle ? pressReleaseRadioFilterButtonSx(isQuickViewActive("NEGATIVE_OR_REVISION")) : radioFilterButtonSx(isQuickViewActive("NEGATIVE_OR_REVISION"))} onClick={() => applyQuickView("NEGATIVE_OR_REVISION")}>부정 또는 수정</Button>
                  )}
                </Stack>
              </Box>
              {selectedMonitor.monitoringType === "KEYWORD" && keywordFilterOptions.length > 0 && (
                <Box sx={usesUnifiedDetailStyle ? pressReleaseFilterSectionSx : undefined}>
                  <Typography sx={usesUnifiedDetailStyle ? pressReleaseFilterLabelSx : filterLabelSx}>키워드</Typography>
                  <Box sx={usesUnifiedDetailStyle ? keywordFilterChipListSx : legacyKeywordFilterChipListSx}>
                    {keywordFilterOptions.map((keyword) => {
                      const selected = selectedKeywordFilters.includes(keyword);
                      return (
                        <Button
                          key={keyword}
                          size="small"
                          sx={usesUnifiedDetailStyle
                            ? selected ? pressReleaseFilterPillActiveButtonSx : pressReleaseFilterPillButtonSx
                            : selected ? filterActiveButtonSx : filterButtonSx}
                          onClick={() => setSelectedKeywordFilters((prev) => (selected ? prev.filter((item) => item !== keyword) : [...prev, keyword]))}
                        >
                          {keyword}
                        </Button>
                      );
                    })}
                  </Box>
                </Box>
              )}
              {(selectedMonitor.sentimentEnabled || revisionTrackingEnabled) && (
                <Box>
                  <Button
                    fullWidth
                    size="small"
                    endIcon={usesUnifiedDetailStyle ? <ExpandMoreIcon sx={{ fontSize: 18, transform: advancedFilterOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform .2s ease" }} /> : undefined}
                    sx={usesUnifiedDetailStyle ? pressReleaseAdvancedButtonSx : advancedFilterOpen ? viewActiveButtonSx : viewButtonSx}
                    onClick={() => setAdvancedFilterOpen((prev) => !prev)}
                  >
                    {advancedFilterOpen ? "고급 필터 접기" : "고급 필터 열기"}
                  </Button>
                  <Collapse in={advancedFilterOpen} timeout="auto" unmountOnExit>
                    <Stack spacing={1} sx={{ pt: 1 }}>
                      {selectedMonitor.sentimentEnabled && (
                        <Box>
                          <Typography sx={usesUnifiedDetailStyle ? pressReleaseFilterLabelSx : filterLabelSx}>부정 판정</Typography>
                          <Stack direction="row" spacing={usesUnifiedDetailStyle ? 0 : 0.45} sx={usesUnifiedDetailStyle ? pressReleaseRangeOptionsSx : { flexWrap: "wrap", rowGap: 0.45 }}>
                            {toneFilterOptions.map((option) => (
                              <Button key={option.value} size="small" sx={usesUnifiedDetailStyle ? pressReleaseRadioFilterButtonSx(toneFilter === option.value) : radioFilterButtonSx(toneFilter === option.value)} onClick={() => { setLinkedArticleFilter(null); setToneFilter(option.value); }}>
                                {option.label}
                              </Button>
                            ))}
                          </Stack>
                        </Box>
                      )}
                      {revisionTrackingEnabled && (
                        <Box>
                          <Typography sx={usesUnifiedDetailStyle ? pressReleaseFilterLabelSx : filterLabelSx}>수정 추적</Typography>
                          <Stack direction="row" spacing={usesUnifiedDetailStyle ? 0 : 0.45} sx={usesUnifiedDetailStyle ? pressReleaseRangeOptionsSx : { flexWrap: "wrap", rowGap: 0.45 }}>
                            {revisionFilterOptions.map((option) => (
                              <Button key={option.value} size="small" sx={usesUnifiedDetailStyle ? pressReleaseRadioFilterButtonSx(revisionFilter === option.value) : radioFilterButtonSx(revisionFilter === option.value)} onClick={() => { setLinkedArticleFilter(null); setRevisionFilter(option.value); }}>
                                {option.label}
                              </Button>
                            ))}
                          </Stack>
                        </Box>
                      )}
                    </Stack>
                  </Collapse>
                </Box>
              )}
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={usesUnifiedDetailStyle ? pressReleaseResultPanelSx : resultPanelSx}>
            <ViewModeTabs
              viewMode={viewMode}
              articleCount={visibleArticles.length}
              runCount={runHistory.length}
              onChange={updateViewMode}
              pressRelease={usesUnifiedDetailStyle}
            />
            {viewMode === "articles" && (
              <Box>
                {revisionTrackingEnabled && usesUnifiedDetailStyle ? (
                  <ArticleRevisionPanelHeader
                    title={articlePanelTitle}
                    selectedCount={selectedRevisionArticleCount}
                    selectableCount={selectableRevisionArticleKeys.length}
                    onSelectAll={selectAllVisibleRevisionArticles}
                    onClear={clearSelectedRevisionArticles}
                    onOpenStartDialog={openBatchRevisionTrackingDialog}
                  />
                ) : (
                  <>
                    <PanelTop title={articlePanelTitle} description={`${visibleArticles.length}건 표시 · ${activeFilterCount ? `필터 ${activeFilterCount}개 적용` : "필터 없음"}`} pressRelease={usesUnifiedDetailStyle} />
                    {revisionTrackingEnabled && (
                      <BatchRevisionActionBar
                        selectedCount={selectedRevisionArticleCount}
                        selectableCount={selectableRevisionArticleKeys.length}
                        onSelectAll={selectAllVisibleRevisionArticles}
                        onClear={clearSelectedRevisionArticles}
                        onOpenStartDialog={openBatchRevisionTrackingDialog}
                        pressRelease={usesUnifiedDetailStyle}
                      />
                    )}
                  </>
                )}
                <Box sx={usesUnifiedDetailStyle ? pressReleaseDenseListSx : denseListSx}>
                  {isPressReleaseMonitor && pressReleaseGroupLoading ? (
                    <Typography sx={emptyStateSx}>보도자료 그룹을 불러오는 중입니다.</Typography>
                  ) : !isPressReleaseMonitor && articleLoading && resultItems.length === 0 ? (
                    <Typography sx={emptyStateSx}>수집 기사 목록을 불러오는 중입니다.</Typography>
                  ) : isPressReleaseMonitor ? (
                    filteredPressReleaseGroups.length === 0 ? (
                      <Typography sx={emptyStateSx}>조회된 {articleRangeLabel} 보도자료 연관 기사가 없습니다.</Typography>
                    ) : filteredPressReleaseGroups.map((group) => (
                      <PressReleaseDenseGroup
                        key={group.id}
                        group={group}
                        expanded={expandedGroupIds.includes(group.id)}
                        onToggle={() => togglePressReleaseGroup(group.id)}
                        showTone={selectedMonitor.sentimentEnabled}
                        showRevisionDiff={revisionTrackingEnabled}
                        onOpenRevisionDiff={openRevisionDiff}
                        onStartRevisionTracking={openRevisionTrackingDialog}
                        onStopRevisionTracking={stopArticleRevisionTracking}
                        revisionTrackingStoppingKeys={revisionTrackingStoppingKeys}
                        selectedRevisionArticleKeys={selectedRevisionArticleKeys}
                        onToggleRevisionTrackingSelected={toggleRevisionArticleSelection}
                        revisionPdfLoadingKeys={revisionPdfLoadingKeys}
                        revisionPdfActionKeys={revisionPdfActionKeys}
                        revisionPdfErrors={revisionPdfErrors}
                        onDownloadRevisionPdfArtifact={downloadRevisionPdfArtifact}
                        onRetryRevisionPdfArtifact={retryRevisionPdfArtifact}
                        onRefreshRevisionPdfArtifacts={refreshRevisionPdfArtifacts}
                        candidateState={candidateSearchStates[group.id] ?? { loading: false, items: [] }}
                        onOpenCandidates={() => openCandidateDialog(group)}
                        articlesLoading={groupArticleLoadingIds.includes(group.id)}
                        articleRangeLabel={articleRangeLabel}
                      />
                    ))
                  ) : filteredResults.length === 0 ? (
                    <Typography sx={emptyStateSx}>
                      {selectedMonitor.monitoringType === "KEYWORD"
                        ? `조회된 ${articleRangeLabel} 기사가 없습니다.`
                        : "조회된 수집 기사가 없습니다."}
                    </Typography>
                  ) : (
                    filteredResults.map((item) => (
                      <ArticleDenseRow
                        key={item.id}
                        item={item}
                        showTone={selectedMonitor.sentimentEnabled}
                        showKeywordMatch={selectedMonitor.monitoringType === "KEYWORD"}
                        showRevisionDiff={revisionTrackingEnabled}
                        onOpenRevisionDiff={openRevisionDiff}
                        onStartRevisionTracking={openRevisionTrackingDialog}
                        onStopRevisionTracking={stopArticleRevisionTracking}
                        revisionTrackingStopping={revisionTrackingStoppingKeys.includes(getRevisionArticleSelectionKey(item))}
                        selectableForRevisionTracking={item.revisionTrackingStatus !== "ACTIVE"}
                        revisionTrackingSelected={selectedRevisionArticleKeys.includes(getRevisionArticleSelectionKey(item))}
                        onToggleRevisionTrackingSelected={toggleRevisionArticleSelection}
                        revisionPdfLoading={revisionPdfLoadingKeys.includes(getRevisionArticleSelectionKey(item))}
                        revisionPdfActionKeys={revisionPdfActionKeys}
                        revisionPdfError={revisionPdfErrors[getRevisionArticleSelectionKey(item)]}
                        onDownloadRevisionPdfArtifact={downloadRevisionPdfArtifact}
                        onRetryRevisionPdfArtifact={retryRevisionPdfArtifact}
                        onRefreshRevisionPdfArtifacts={refreshRevisionPdfArtifacts}
                        usePressReleaseContext={false}
                      />
                    ))
                  )}
                </Box>
              </Box>
            )}

            {viewMode === "runs" && (
              <Box>
                {usesUnifiedDetailStyle ? (
                  <RunHistoryPanelHeader title="최근 실행 흐름" description={latestRun ? `최근 실행 ${formatDateTime(latestRun.scheduledAt)}` : "최근 실행 없음"} />
                ) : (
                  <PanelTop title="최근 실행 흐름" description={latestRun ? `최근 실행 ${formatDateTime(latestRun.scheduledAt)}` : "최근 실행 없음"} pressRelease={usesUnifiedDetailStyle} />
                )}
                <Box sx={runHistoryPanelSx}>
                  {runHistoryLoadError && <Typography sx={errorStateSx}>{runHistoryLoadError}</Typography>}
                  {runHistoryLoading ? (
                    <Typography sx={emptyStateSx}>실행 기록을 불러오는 중입니다.</Typography>
                  ) : runHistory.length === 0 ? (
                    <Typography sx={emptyStateSx}>실행 기록이 없습니다.</Typography>
                  ) : (
                    <RunHistoryTable
                      items={runHistory}
                      retryingRunIds={notificationRetryingRunIds}
                      retryMessages={notificationRetryMessages}
                      retryErrors={notificationRetryErrors}
                      onRetry={retryNotificationDelivery}
                    />
                  )}
                </Box>
              </Box>
            )}

            {viewMode === "settings" && (
              <Box>
                {usesUnifiedDetailStyle ? (
                  <Box sx={settingsContentSx}>
                    <Box sx={settingsPolicySectionSx}>
                      <SettingsPanelHeader title="현재 적용 정책" description="저장 조건과 실행 정책입니다." />
                      <Box sx={settingsPolicyStackSx}>
                        <Box sx={settingsTermRowSx}>
                          <PolicyBlock label="검색어" values={selectedMonitor.includeTerms} empty="등록된 검색어 없음" />
                          {selectedMonitor.monitoringType === "KEYWORD" ? (
                            <SearchTermExcludePolicyBlock rows={selectedSearchTermExcludeRows} commonExcludeTerms={selectedMonitor.excludeTerms} />
                          ) : (
                            <PolicyBlock label="제외어" values={selectedMonitor.excludeTerms} empty="등록된 제외어 없음" />
                          )}
                        </Box>
                        <Box sx={settingsDetailRowSx}>
                          <PolicyRowsCard>
                            {executionPolicyRows.map((row) => <PolicyRow key={row.label} label={row.label} value={row.value} />)}
                          </PolicyRowsCard>
                          <PolicyRowsCard>
                            {notificationPolicyRows.map((row) => <PolicyRow key={row.label} label={row.label} value={row.value} />)}
                            <SentimentKeywordPolicyBlock state={sentimentKeywordDisplay} />
                          </PolicyRowsCard>
                        </Box>
                      </Box>
                    </Box>
                    <Box sx={settingsDangerRowSx}>
                      <Button variant="contained" sx={settingsDeleteButtonSx} disabled={savingMonitor} onClick={() => setDeleteDialogOpen(true)}>
                        {savingMonitor ? "삭제 중" : "모니터링 삭제"}
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <>
                    <PanelTop title="현재 적용 정책" description="저장 조건과 실행 정책입니다." pressRelease={usesUnifiedDetailStyle} />
                    <Box sx={settingsGridSx}>
                      <PolicyBlock label="검색어" values={selectedMonitor.includeTerms} empty="등록된 검색어 없음" />
                      {selectedMonitor.monitoringType === "KEYWORD" ? (
                        <SearchTermExcludePolicyBlock rows={selectedSearchTermExcludeRows} commonExcludeTerms={selectedMonitor.excludeTerms} />
                      ) : (
                        <PolicyBlock label="제외어" values={selectedMonitor.excludeTerms} empty="등록된 제외어 없음" />
                      )}
                      <Box sx={policyBoxSx}>
                        {executionPolicyRows.map((row) => <PolicyRow key={row.label} label={row.label} value={row.value} />)}
                      </Box>
                      <SentimentKeywordPolicyBlock state={sentimentKeywordDisplay} />
                      <Box sx={policyBoxSx}>
                        {notificationPolicyRows.map((row) => <PolicyRow key={row.label} label={row.label} value={row.value} />)}
                      </Box>
                      <Box sx={dangerZoneSx}>
                        <Typography sx={sectionTitleSx}>위험 작업</Typography>
                        <Typography sx={mutedTextSx}>삭제하면 이 모니터링 설정을 되돌릴 수 없습니다.</Typography>
                        <Button variant="outlined" sx={dangerButtonSx} disabled={savingMonitor} onClick={() => setDeleteDialogOpen(true)}>
                          {savingMonitor ? "삭제 중" : "모니터링 삭제"}
                        </Button>
                      </Box>
                    </Box>
                  </>
                )}
              </Box>
            )}
          </Paper>
        </Box>
        </Stack>
      </Box>

      <Snackbar
        key={batchRevisionTrackingToast?.id}
        open={Boolean(batchRevisionTrackingToast)}
        autoHideDuration={4000}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        onClose={() => setBatchRevisionTrackingToast(null)}
        sx={batchRevisionTrackingSnackbarSx}
      >
        <Box role="status" aria-live="polite" sx={batchRevisionTrackingToastSx}>
          <CheckCircleOutlineIcon aria-hidden sx={batchRevisionTrackingToastIconSx} />
          <Typography component="span" sx={batchRevisionTrackingToastTextSx}>
            {batchRevisionTrackingToast?.message}
          </Typography>
        </Box>
      </Snackbar>

      <RevisionDiffDialog
        open={revisionDialogOpen}
        article={currentRevisionTargetArticle}
        data={revisionDiff}
        loading={revisionDiffLoading}
        error={revisionDiffError}
        pdfLoading={Boolean(currentRevisionTargetArticleKey && revisionPdfLoadingKeys.includes(currentRevisionTargetArticleKey))}
        pdfError={currentRevisionTargetArticleKey ? revisionPdfErrors[currentRevisionTargetArticleKey] : undefined}
        pdfActionKeys={revisionPdfActionKeys}
        highlightEnabled={revisionHighlightEnabled}
        onToggleHighlight={() => setRevisionHighlightEnabled((prev) => !prev)}
        onDownloadRevisionPdfArtifact={downloadRevisionPdfArtifact}
        onRetryRevisionPdfArtifact={retryRevisionPdfArtifact}
        onRefreshRevisionPdfArtifacts={refreshRevisionPdfArtifacts}
        usePressReleaseContext={selectedMonitor.monitoringType === "PRESS_RELEASE"}
        onClose={() => setRevisionDialogOpen(false)}
      />
      <ArticleRevisionTrackingDialog
        open={revisionTrackingDialogOpen}
        article={revisionTrackingTargetArticle}
        trackingHours={revisionTrackingHours}
        notifyOnRevisionDetected={revisionTrackingNotifyEnabled}
        saving={revisionTrackingSaving}
        error={revisionTrackingError}
        onChangeHours={setRevisionTrackingHours}
        onChangeNotify={setRevisionTrackingNotifyEnabled}
        onClose={() => {
          if (!revisionTrackingSaving) setRevisionTrackingDialogOpen(false);
        }}
        onSubmit={startArticleRevisionTracking}
      />
      <BatchArticleRevisionTrackingDialog
        open={batchRevisionDialogOpen}
        selectedCount={selectedRevisionArticleCount}
        trackingHours={batchRevisionTrackingHours}
        notifyOnRevisionDetected={batchRevisionTrackingNotifyEnabled}
        saving={batchRevisionTrackingSaving}
        error={batchRevisionTrackingError}
        summary={batchRevisionTrackingSummary}
        onChangeHours={setBatchRevisionTrackingHours}
        onChangeNotify={setBatchRevisionTrackingNotifyEnabled}
        onClose={() => {
          if (!batchRevisionTrackingSaving) setBatchRevisionDialogOpen(false);
        }}
        onSubmit={startBatchArticleRevisionTracking}
      />
      <PressReleaseCandidateDialog
        group={candidateDialogGroup}
        state={candidateDialogGroup ? candidateSearchStates[candidateDialogGroup.id] ?? { loading: false, items: [] } : { loading: false, items: [] }}
        query={candidateDialogQuery}
        onClose={() => setCandidateDialogGroupId(null)}
        onQueryChange={(value) => {
          if (!candidateDialogGroup) return;
          setCandidateSearchQueries((prev) => ({ ...prev, [candidateDialogGroup.id]: value }));
        }}
        onResetQuery={() => {
          if (!candidateDialogGroup) return;
          setCandidateSearchQueries((prev) => ({ ...prev, [candidateDialogGroup.id]: candidateDialogGroup.suggestedSearchQuery ?? "" }));
        }}
        onSearch={() => candidateDialogGroup && searchPressReleaseCandidates(candidateDialogGroup, candidateDialogQuery)}
        onApprove={(candidate) => candidateDialogGroup && approvePressReleaseCandidate(candidateDialogGroup, candidate)}
      />
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          if (!savingMonitor) setDeleteDialogOpen(false);
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: deleteDialogPaperSx }}
        BackdropProps={{ sx: deleteDialogBackdropSx }}
      >
        <DialogTitle sx={deleteDialogTitleSx}>
          <Typography sx={deleteDialogHeadingSx}>모니터링을 삭제할까요?</Typography>
          <Typography sx={deleteDialogMessageSx}>삭제 후에는 다시 복구할 수 없습니다.</Typography>
        </DialogTitle>
        {loadError && (
          <DialogContent sx={deleteDialogContentSx}>
            <Typography sx={errorTextSx}>{loadError}</Typography>
          </DialogContent>
        )}
        <DialogActions sx={deleteDialogActionsSx}>
          <Button variant="outlined" sx={deleteCancelButtonSx} disabled={savingMonitor} onClick={() => setDeleteDialogOpen(false)}>
            취소
          </Button>
          <Button variant="contained" sx={deleteConfirmButtonSx} disabled={savingMonitor} onClick={deleteSelectedMonitor}>
            {savingMonitor ? "삭제 중" : "삭제"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const PanelTop = ({ title, description, pressRelease = false }: { title: string; description: string; pressRelease?: boolean }) => (
  <Box sx={pressRelease ? pressReleasePanelTopSx : panelTopSx}>
    <Typography sx={pressRelease ? pressReleasePanelTitleSx : panelTitleSx}>{title}</Typography>
    {!pressRelease && <Typography sx={mutedTextSx}>{description}</Typography>}
  </Box>
);

const RunHistoryPanelHeader = ({ title, description }: { title: string; description: string }) => (
  <Box sx={runHistoryPanelHeaderSx}>
    <Typography sx={pressReleasePanelTitleSx}>{title}</Typography>
    <Typography sx={runHistoryPanelDescriptionSx}>{description}</Typography>
  </Box>
);

const SettingsPanelHeader = ({ title, description }: { title: string; description: string }) => (
  <Box sx={settingsPanelHeaderSx}>
    <Typography sx={pressReleasePanelTitleSx}>{title}</Typography>
    <Typography sx={settingsPanelDescriptionSx}>{description}</Typography>
  </Box>
);

const PolicyRowsCard = ({ children }: { children: React.ReactNode }) => (
  <Box sx={settingsRowsCardSx}>{children}</Box>
);

const RerunPlayIcon = () => (
  <Box component="img" src={monitoringDetailIcons.rerunPlay} alt="" aria-hidden draggable={false} sx={rerunPlayIconSx} />
);

const MetricCard = ({
  label,
  value,
  secondaryValue,
  sub,
  tone = "normal",
}: {
  label: string;
  value: string;
  secondaryValue?: string;
  sub?: string;
  tone?: "normal" | "danger" | "warning";
}) => (
  <Box sx={metricCardSx(tone, !secondaryValue && !sub)}>
    <Box sx={metricContentSx(Boolean(secondaryValue || sub))}>
      <Typography sx={metricLabelSx}>{label}</Typography>
      <Box sx={metricValueRowSx}>
        <Typography sx={metricValueSx}>{value}</Typography>
        {secondaryValue && <Typography sx={metricSecondaryValueSx}>{secondaryValue}</Typography>}
      </Box>
    </Box>
    {sub && <Typography sx={metricSubSx}>{sub}</Typography>}
  </Box>
);

const ViewModeTabs = ({
  viewMode,
  articleCount,
  runCount,
  onChange,
  pressRelease = false,
}: {
  viewMode: ViewMode;
  articleCount: number;
  runCount: number;
  onChange: (mode: ViewMode) => void;
  pressRelease?: boolean;
}) => (
  <Box sx={pressRelease ? pressReleaseViewTabBarSx : viewTabBarSx}>
    {([
      { mode: "articles" as const, label: "수집 기사", count: articleCount },
      { mode: "runs" as const, label: "실행 기록", count: runCount },
      { mode: "settings" as const, label: "설정" },
    ]).map((item) => (
      <Box
        component="button"
        key={item.mode}
        sx={pressRelease
          ? getPressReleaseViewTabSx(item.mode, viewMode === item.mode)
          : viewMode === item.mode
            ? viewTabActiveSx
            : viewTabButtonSx}
        onClick={() => onChange(item.mode)}
      >
        <Box component="span">{item.label}</Box>
        {item.count !== undefined && <Box component="span" sx={pressRelease ? pressReleaseViewTabCountSx : viewTabCountSx}>{item.count}</Box>}
      </Box>
    ))}
  </Box>
);

const keywordMatchLevelLabel: Record<KeywordMatchLevel, string> = {
  HIGH: "높음",
  MEDIUM: "보통",
  LOW: "낮음",
};

const getKeywordMatchTooltip = (item: ResultItem) => {
  if (!item.keywordMatchDetails.length) return "검색어 매칭 정도";
  return item.keywordMatchDetails
    .map((detail) => {
      const basis = [
        detail.titleMatched ? "제목 일치" : "",
        detail.bodyMatched ? `본문 ${detail.bodyHitCount > 0 ? `${detail.bodyHitCount}회` : "일치"}` : "",
      ].filter(Boolean).join(" / ") || "직접 일치 낮음";
      return `${detail.keyword}: ${detail.score}점 (${basis})`;
    })
    .join("\n");
};

const KeywordMatchGauge = ({ item }: { item: ResultItem }) => {
  if (item.keywordMatchScore === null || item.keywordMatchScore === undefined || !item.keywordMatchLevel) return null;
  return (
    <Box sx={keywordMatchGaugeSx(item.keywordMatchLevel)} title={getKeywordMatchTooltip(item)} aria-label={`관련도 ${keywordMatchLevelLabel[item.keywordMatchLevel]}`}>
      <Box component="span" sx={keywordMatchGaugeLabelSx}>관련도</Box>
      <Box component="span" sx={keywordMatchGaugeValueSx(item.keywordMatchLevel)}>{keywordMatchLevelLabel[item.keywordMatchLevel]}</Box>
    </Box>
  );
};

const ArticleRevisionPanelHeader = ({
  title,
  selectedCount,
  selectableCount,
  onSelectAll,
  onClear,
  onOpenStartDialog,
}: {
  title: string;
  selectedCount: number;
  selectableCount: number;
  onSelectAll: () => void;
  onClear: () => void;
  onOpenStartDialog: () => void;
}) => (
  <Box sx={articleRevisionPanelHeaderSx}>
    <Box sx={articleRevisionHeaderTextSx}>
      <Typography sx={pressReleasePanelTitleSx}>{title}</Typography>
      <Typography sx={articleRevisionHeaderDescriptionSx}>
        {selectedCount > 0 ? `${selectedCount}개 기사 선택됨` : "수정 추적할 기사를 선택할 수 있습니다."}
      </Typography>
    </Box>
    <Stack direction="row" sx={articleRevisionHeaderActionsSx}>
      <Button
        size="small"
        variant="outlined"
        sx={articleRevisionHeaderButtonSx}
        disabled={selectableCount === 0 || selectedCount === selectableCount}
        onClick={onSelectAll}
      >
        전체 선택
      </Button>
      <Button size="small" variant="outlined" sx={articleRevisionHeaderButtonSx} disabled={selectedCount === 0} onClick={onClear}>
        선택 해제
      </Button>
      <Button size="small" variant="outlined" sx={articleRevisionHeaderStartButtonSx} disabled={selectedCount === 0} onClick={onOpenStartDialog}>
        수정 추적 시작
      </Button>
    </Stack>
  </Box>
);

const BatchRevisionActionBar = ({
  selectedCount,
  selectableCount,
  onSelectAll,
  onClear,
  onOpenStartDialog,
  pressRelease = false,
}: {
  selectedCount: number;
  selectableCount: number;
  onSelectAll: () => void;
  onClear: () => void;
  onOpenStartDialog: () => void;
  pressRelease?: boolean;
}) => (
  <Box sx={batchActionBarSx(pressRelease)}>
    <Typography sx={batchActionTextSx}>
      {selectedCount > 0 ? `${selectedCount}개 기사 선택됨` : "수정 추적할 기사를 선택할 수 있습니다."}
    </Typography>
    <Stack direction="row" spacing={0.6} sx={{ flexWrap: "wrap", rowGap: 0.5, justifyContent: "flex-end" }}>
      <Button
        size="small"
        variant="outlined"
        sx={outlineButtonSx}
        disabled={selectableCount === 0 || selectedCount === selectableCount}
        onClick={onSelectAll}
      >
        현재 필터 전체 선택
      </Button>
      <Button size="small" variant="outlined" sx={outlineButtonSx} disabled={selectedCount === 0} onClick={onClear}>
        선택 해제
      </Button>
      <Button size="small" variant="contained" sx={primaryButtonSx} disabled={selectedCount === 0} onClick={onOpenStartDialog}>
        수정 추적 시작
      </Button>
    </Stack>
  </Box>
);

const ArticleEmptyCheckboxIcon = () => <Box component="span" sx={articleCheckboxEmptyIconSx} />;

const ArticleCheckedCheckboxIcon = () => (
  <Box component="span" sx={articleCheckboxCheckedIconSx}>
    <Box component="span" sx={articleCheckboxCheckMarkSx} />
  </Box>
);

const ArticleRevisionTrackingStateControl = ({
  status,
}: {
  status?: ArticleRevisionTrackingStatus;
}) => {
  const active = status === "ACTIVE";
  const stopped = status === "STOPPED";
  const expired = status === "EXPIRED";
  const label = active ? "추적 중" : expired ? "추적 만료됨" : stopped ? "추적 중지됨" : "추적 전";
  const tone = active ? "active" : expired ? "expired" : stopped ? "stopped" : "idle";
  return (
    <Box sx={articleRevisionTrackingStateSx}>
      <Box sx={articleRevisionTrackingStateLabelRowSx(tone)}>
        {tone !== "idle" && (
          <Box
            component="img"
            src={articleRevisionTrackingStateIconSrc[tone]}
            alt=""
            aria-hidden
            draggable={false}
            sx={articleRevisionTrackingStateIconSx}
          />
        )}
        <Typography component="span" sx={articleRevisionTrackingStateLabelSx(tone)}>{label}</Typography>
      </Box>
    </Box>
  );
};

const ArticleDenseRow = ({
  item,
  variant = "default",
  showTone,
  showKeywordMatch,
  showRevisionDiff,
  onOpenRevisionDiff,
  onStartRevisionTracking,
  onStopRevisionTracking,
  revisionTrackingStopping,
  selectableForRevisionTracking,
  revisionTrackingSelected,
  onToggleRevisionTrackingSelected,
  revisionPdfLoading,
  revisionPdfActionKeys = [],
  revisionPdfError,
  onDownloadRevisionPdfArtifact,
  onRetryRevisionPdfArtifact,
  onRefreshRevisionPdfArtifacts,
  usePressReleaseContext = false,
}: {
  item: ResultItem;
  variant?: ArticleDenseRowVariant;
  showTone: boolean;
  showKeywordMatch?: boolean;
  showRevisionDiff?: boolean;
  onOpenRevisionDiff?: (item: ResultItem) => void;
  onStartRevisionTracking?: (item: ResultItem) => void;
  onStopRevisionTracking?: (item: ResultItem) => void;
  revisionTrackingStopping?: boolean;
  selectableForRevisionTracking?: boolean;
  revisionTrackingSelected?: boolean;
  onToggleRevisionTrackingSelected?: (item: ResultItem) => void;
  revisionPdfLoading?: boolean;
  revisionPdfActionKeys?: string[];
  revisionPdfError?: string;
  onDownloadRevisionPdfArtifact?: (item: ResultItem, artifact: ArticleRevisionPdfArtifact) => void;
  onRetryRevisionPdfArtifact?: (item: ResultItem, artifact: ArticleRevisionPdfArtifact) => void;
  onRefreshRevisionPdfArtifacts?: (item: ResultItem) => void;
  usePressReleaseContext?: boolean;
}) => {
  const pressRelated = variant === "pressRelated";
  const articleActionButtonSx = pressRelated ? pressRelatedArticleButtonSx : keywordArticleButtonSx;
  const articleMetaText = [
    formatDateTime(item.publishedAt),
    item.publisher,
    item.reporter || "기자 미확인",
    item.matchedKeywords.length ? item.matchedKeywords.slice(0, 3).join(", ") : "",
  ].filter(Boolean).join(" · ");
  const canShowRevisionCompare = showRevisionDiff && (item.revisionDetected || Boolean(item.revisionTrackingStatus));
  const showsRevisionPdfStatus = Boolean(showRevisionDiff && (revisionPdfLoading || revisionPdfError || hasRevisionPdfArtifacts(item.revisionPdfArtifacts)));
  const showTitleOriginalLink = showRevisionDiff && !pressRelated && Boolean(item.url);
  const showArticleTitleRow = item.revisionDetected === true || showTitleOriginalLink;
  const revisionPrimaryActionLabel = item.revisionTrackingStatus === "ACTIVE"
    ? (revisionTrackingStopping ? "중지 중" : "추적 중지")
    : item.revisionTrackingStatus
      ? "다시 추적"
      : "추적 시작";
  return (
    <Box sx={articleRowSx(variant, showRevisionDiff === true, showTone, showKeywordMatch === true)}>
      {pressRelated ? (
        <Box sx={articleSignalSx(variant, showRevisionDiff === true)}>
          {showRevisionDiff && (
            <Checkbox
              size="small"
              checked={revisionTrackingSelected === true}
              disabled={!selectableForRevisionTracking}
              onChange={() => onToggleRevisionTrackingSelected?.(item)}
              onClick={(event) => event.stopPropagation()}
              icon={<ArticleEmptyCheckboxIcon />}
              checkedIcon={<ArticleCheckedCheckboxIcon />}
              sx={revisionSelectCheckboxSx}
              inputProps={{ "aria-label": `${item.title} 수정 추적 선택` }}
            />
          )}
          {showTone && <Chip label={toneMeta[item.tone].label} size="small" sx={pressRelatedToneChipSx(item.tone)} />}
        </Box>
      ) : (
        <>
          {showRevisionDiff && (
            <Box sx={articleCheckboxColumnSx}>
              <Checkbox
                size="small"
                checked={revisionTrackingSelected === true}
                disabled={!selectableForRevisionTracking}
                onChange={() => onToggleRevisionTrackingSelected?.(item)}
                onClick={(event) => event.stopPropagation()}
                icon={<ArticleEmptyCheckboxIcon />}
                checkedIcon={<ArticleCheckedCheckboxIcon />}
                sx={revisionSelectCheckboxSx}
                inputProps={{ "aria-label": `${item.title} 수정 추적 선택` }}
              />
            </Box>
          )}
          {showTone && (
            <Box sx={articleToneColumnSx}>
              <Chip label={toneMeta[item.tone].label} size="small" sx={toneChipSx(item.tone)} />
            </Box>
          )}
          {showKeywordMatch && (
            <Box sx={articleKeywordMatchColumnSx}>
              <KeywordMatchGauge item={item} />
            </Box>
          )}
        </>
      )}
      <Box sx={articleContentSx(variant)}>
        <Box sx={showArticleTitleRow ? articleTitleRowSx : undefined}>
          {item.revisionDetected && <Box component="span" sx={articleRevisionDetectedBadgeSx}>수정됨</Box>}
          <Typography sx={pressRelated ? pressRelatedArticleTitleSx : articleTitleSx}>{item.title}</Typography>
          {showTitleOriginalLink && (
            <IconButton
              component="a"
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="원문"
              size="small"
              sx={articleTitleLinkButtonSx}
            >
              <OpenInNewIcon sx={articleTitleLinkIconSx} />
            </IconButton>
          )}
        </Box>
        <Typography sx={pressRelated ? pressRelatedArticleMetaSx : articleMetaSx}>{articleMetaText}</Typography>
        {item.body && !pressRelated && <Typography sx={articleBodySx}>{item.body}</Typography>}
        {showsRevisionPdfStatus && (
          <RevisionPdfArtifactStatusList
            article={item}
            artifacts={item.revisionPdfArtifacts}
            loading={revisionPdfLoading === true}
            error={revisionPdfError}
            actionKeys={revisionPdfActionKeys}
            onDownload={onDownloadRevisionPdfArtifact}
            onRetry={onRetryRevisionPdfArtifact}
            onRefresh={onRefreshRevisionPdfArtifacts}
            usePressReleaseContext={usePressReleaseContext}
          />
        )}
      </Box>
      {showRevisionDiff && (
        <ArticleRevisionTrackingStateControl status={item.revisionTrackingStatus} />
      )}
      <Stack direction="row" spacing={0.5} sx={articleActionRowSx(showRevisionDiff === true, variant)}>
        {item.url && !showTitleOriginalLink && (
          <Button
            size="small"
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<OpenInNewIcon sx={pressRelated ? pressRelatedArticleButtonIconSx : keywordArticleButtonIconSx} />}
            sx={articleActionButtonSx}
          >
            원문
          </Button>
        )}
        {showRevisionDiff && (
          <Button
            size="small"
            sx={articleActionButtonSx}
            disabled={revisionTrackingStopping}
            onClick={() => {
              if (item.revisionTrackingStatus === "ACTIVE") {
                onStopRevisionTracking?.(item);
                return;
              }
              onStartRevisionTracking?.(item);
            }}
          >
            {revisionPrimaryActionLabel}
          </Button>
        )}
        {canShowRevisionCompare && (
          <Button size="small" sx={articleActionButtonSx} onClick={() => onOpenRevisionDiff?.(item)}>
            비교
          </Button>
        )}
      </Stack>
    </Box>
  );
};

const revisionPdfArtifactKindLabel: Record<ArticleRevisionPdfArtifactKind, string> = {
  baseline: "기준 PDF",
  revision: "수정 PDF",
};

const revisionPdfStatusLabel: Record<ArticleRevisionPdfArtifactStatus, string> = {
  PENDING: "대기",
  GENERATING: "생성 중",
  READY: "완료",
  FAILED: "실패",
};

const formatRevisionPdfStatusLabel = (kind: ArticleRevisionPdfArtifactKind | "" | null, status: ArticleRevisionPdfArtifactStatus) =>
  `${kind ? revisionPdfArtifactKindLabel[kind] : "PDF"}·${revisionPdfStatusLabel[status]}`;

const getRevisionPdfStatusTone = (artifact?: ArticleRevisionPdfArtifact, error?: string): "ready" | "failed" | "pending" | "error" => {
  if (error) return "error";
  if (artifact?.status === "READY") return "ready";
  if (artifact?.status === "FAILED") return "failed";
  return "pending";
};

const getRevisionPdfBadgeTone = (artifact?: ArticleRevisionPdfArtifact, error?: string): RevisionPdfBadgeTone => {
  if (artifact?.status === "READY" && getRevisionPdfArtifactKind(artifact) === "baseline") return "baselineReady";
  return getRevisionPdfStatusTone(artifact, error);
};

const getRevisionPdfStatusSummary = ({
  completedArtifacts,
  displayArtifact,
  loading,
  error,
}: {
  completedArtifacts: ArticleRevisionPdfArtifact[];
  displayArtifact?: ArticleRevisionPdfArtifact;
  loading?: boolean;
  error?: string;
}) => {
  if (error) return error;
  if (loading) return "PDF 증빙 상태 확인 중";
  if (displayArtifact?.status === "PENDING" || displayArtifact?.status === "GENERATING") return displayArtifact.message || "PDF 증빙 생성 중";
  if (completedArtifacts.length) return "PDF 증빙 생성 완료";
  if (displayArtifact?.status === "FAILED") return displayArtifact.message || "PDF 증빙 생성 실패";
  return "PDF 증빙 상태를 확인 중입니다.";
};

const RevisionPdfArtifactStatusList = ({
  article,
  artifacts,
  loading,
  error,
  actionKeys,
  onDownload,
  onRetry,
  onRefresh,
  usePressReleaseContext,
  flushTop = false,
}: {
  article: ResultItem;
  artifacts?: ArticleRevisionPdfArtifacts;
  loading?: boolean;
  error?: string;
  actionKeys: string[];
  onDownload?: (item: ResultItem, artifact: ArticleRevisionPdfArtifact) => void;
  onRetry?: (item: ResultItem, artifact: ArticleRevisionPdfArtifact) => void;
  onRefresh?: (item: ResultItem) => void;
  usePressReleaseContext: boolean;
  flushTop?: boolean;
}) => {
  const primaryArtifacts = [artifacts?.baseline, artifacts?.revision].filter((item): item is ArticleRevisionPdfArtifact => Boolean(item));
  const visibleArtifacts = primaryArtifacts.length ? primaryArtifacts : artifacts?.all ?? [];
  if (!loading && !error && !visibleArtifacts.length) return null;
  const refreshable = Boolean(onRefresh) && (Boolean(error) || visibleArtifacts.some((artifact) => artifact.status === "PENDING" || artifact.status === "GENERATING"));
  const completedArtifacts = visibleArtifacts.filter((artifact) => artifact.status === "READY");
  const downloadableArtifacts = visibleArtifacts.filter((artifact) => artifact.downloadAvailable);
  const retryableArtifacts = visibleArtifacts.filter((artifact) => artifact.retryAvailable);
  const hasPendingArtifact = visibleArtifacts.some((artifact) => artifact.status === "PENDING" || artifact.status === "GENERATING");
  const showInlineRefresh = refreshable && !error && !retryableArtifacts.length && hasPendingArtifact;
  const hasAuxiliaryActions = retryableArtifacts.length > 0 || (refreshable && !showInlineRefresh);
  const displayArtifact =
    downloadableArtifacts[0]
    ?? visibleArtifacts.find((artifact) => artifact.status === "PENDING" || artifact.status === "GENERATING")
    ?? visibleArtifacts.find((artifact) => artifact.status === "FAILED")
    ?? visibleArtifacts[0];
  const displayArtifactKind = displayArtifact ? getRevisionPdfArtifactKind(displayArtifact) : null;
  const statusLabel = loading
    ? "PDF 확인 중"
    : displayArtifact
      ? formatRevisionPdfStatusLabel(displayArtifactKind, displayArtifact.status)
      : "PDF 상태";
  const statusText = getRevisionPdfStatusSummary({ completedArtifacts, displayArtifact, loading, error });
  const statusBadges = visibleArtifacts.length
    ? visibleArtifacts.map((artifact) => {
      const kind = getRevisionPdfArtifactKind(artifact);
      return {
        key: getRevisionPdfArtifactActionKey(article, artifact, usePressReleaseContext),
        artifact,
        label: formatRevisionPdfStatusLabel(kind, artifact.status),
        tone: getRevisionPdfBadgeTone(artifact),
      };
    })
    : [{ key: "pdf-status", artifact: undefined, label: statusLabel, tone: getRevisionPdfStatusTone(undefined, error) }];
  return (
    <Box sx={flushTop ? revisionPdfDialogListSx : revisionPdfListSx}>
      <Box sx={revisionPdfItemSx}>
        <Box sx={revisionPdfStatusSummarySx}>
          <Typography sx={revisionPdfMetaSx} title={statusText}>{statusText}</Typography>
          {showInlineRefresh && (
            <Button type="button" size="small" sx={revisionPdfInlineRefreshButtonSx} onClick={() => onRefresh?.(article)}>
              상태 확인
            </Button>
          )}
        </Box>
        <Box sx={revisionPdfStatusBadgeGroupSx}>
          {statusBadges.map((badge) => {
            const kind = badge.artifact ? getRevisionPdfArtifactKind(badge.artifact) : "";
            const actionLabel = kind ? `${revisionPdfArtifactKindLabel[kind]} 다운로드` : "PDF 다운로드";
            const actioning = actionKeys.includes(badge.key);
            const badgeContent = <>
              <Typography component="span" sx={revisionPdfStatusBadgeTextSx}>{badge.label}</Typography>
              {badge.artifact?.downloadAvailable && <FileDownloadOutlinedIcon aria-hidden sx={revisionPdfStatusBadgeIconSx} />}
            </>;
            return badge.artifact?.downloadAvailable ? (
              <Button
                key={badge.key}
                type="button"
                size="small"
                disabled={actioning}
                onClick={() => onDownload?.(article, badge.artifact!)}
                aria-label={downloadableArtifacts.length > 1 ? actionLabel : "다운로드"}
                title={`${actionLabel} · ${revisionPdfStatusLabel[badge.artifact.status]}`}
                sx={revisionPdfStatusBadgeButtonSx(badge.tone)}
              >
                {badgeContent}
              </Button>
            ) : (
              <Box key={badge.key} component="span" sx={revisionPdfStatusBadgeSx(badge.tone)}>{badgeContent}</Box>
            );
          })}
        </Box>
        {hasAuxiliaryActions && (
          <Stack direction="row" spacing={0.5} sx={revisionPdfActionGroupSx}>
            {retryableArtifacts.map((artifact) => {
              const kind = getRevisionPdfArtifactKind(artifact);
              const actionKey = getRevisionPdfArtifactActionKey(article, artifact, usePressReleaseContext);
              const actioning = actionKeys.includes(actionKey);
              return (
                <Button
                  key={`${actionKey}:retry`}
                  type="button"
                  size="small"
                  disabled={actioning}
                  onClick={() => onRetry?.(article, artifact)}
                  sx={revisionPdfAuxButtonSx}
                  title={`${kind ? revisionPdfArtifactKindLabel[kind] : "PDF"} · ${revisionPdfStatusLabel[artifact.status]}`}
                >
                  {actioning ? "요청 중" : "재시도"}
                </Button>
              );
            })}
            {refreshable && (
              <Button type="button" size="small" sx={revisionPdfAuxButtonSx} onClick={() => onRefresh?.(article)}>
                상태 확인
              </Button>
            )}
          </Stack>
        )}
      </Box>
    </Box>
  );
};

const PressReleaseDenseGroup = ({
  group,
  expanded,
  onToggle,
  showTone,
  showRevisionDiff,
  onOpenRevisionDiff,
  onStartRevisionTracking,
  onStopRevisionTracking,
  revisionTrackingStoppingKeys,
  selectedRevisionArticleKeys,
  onToggleRevisionTrackingSelected,
  revisionPdfLoadingKeys,
  revisionPdfActionKeys,
  revisionPdfErrors,
  onDownloadRevisionPdfArtifact,
  onRetryRevisionPdfArtifact,
  onRefreshRevisionPdfArtifacts,
  candidateState,
  onOpenCandidates,
  articlesLoading,
  articleRangeLabel,
}: {
  group: PressReleaseGroup;
  expanded: boolean;
  onToggle: () => void;
  showTone: boolean;
  showRevisionDiff?: boolean;
  onOpenRevisionDiff?: (item: ResultItem) => void;
  onStartRevisionTracking?: (item: ResultItem) => void;
  onStopRevisionTracking?: (item: ResultItem) => void;
  revisionTrackingStoppingKeys?: string[];
  selectedRevisionArticleKeys?: string[];
  onToggleRevisionTrackingSelected?: (item: ResultItem) => void;
  revisionPdfLoadingKeys?: string[];
  revisionPdfActionKeys?: string[];
  revisionPdfErrors?: Record<string, string>;
  onDownloadRevisionPdfArtifact?: (item: ResultItem, artifact: ArticleRevisionPdfArtifact) => void;
  onRetryRevisionPdfArtifact?: (item: ResultItem, artifact: ArticleRevisionPdfArtifact) => void;
  onRefreshRevisionPdfArtifacts?: (item: ResultItem) => void;
  candidateState: CandidateSearchState;
  onOpenCandidates: () => void;
  articlesLoading: boolean;
  articleRangeLabel: string;
}) => {
  const originalSearchTitle = group.originalSearchTitle.trim();
  const showOriginalSearchTitle = Boolean(originalSearchTitle) && originalSearchTitle !== group.title.trim();
  const primarySearchChipLabel = showOriginalSearchTitle ? originalSearchTitle : group.company;
  const candidateStatusLabel = candidateState.loading
    ? "검색 중"
    : candidateState.error
      ? "확인 필요"
      : candidateState.searched
        ? `후보 ${candidateState.items.length}건`
        : "";
  const showCandidateAction = group.relatedArticleCount === 0 || Boolean(group.suggestedSearchQuery.trim()) || Boolean(candidateStatusLabel);
  const articleRangeCount = group.articleRangeRelatedArticleCount ?? group.relatedArticleCount;
  return (
    <Box sx={pressGroupSx}>
      <Box sx={pressGroupHeaderSx} onClick={onToggle}>
        <Box sx={pressGroupHeaderContentSx}>
          {showTone && group.tone && <Chip label={toneMeta[group.tone].label} size="small" sx={toneChipSx(group.tone)} />}
          <Box sx={pressGroupHeaderMainSx}>
            {primarySearchChipLabel && <Chip label={primarySearchChipLabel} size="small" sx={pressKeywordChipSx} />}
            <Typography sx={pressTitleSx}>{group.title}</Typography>
            <Typography sx={pressGroupDateSx}>배포 {formatDateTime(group.distributedAt)}</Typography>
          </Box>
        </Box>
        <Box sx={pressGroupHeaderMetaSx}>
          <Chip label={`연관 ${articleRangeCount}건`} size="small" sx={pressGroupCountChipSx} />
          {group.negativeRelatedArticleCount > 0 && <Chip label={`부정 ${group.negativeRelatedArticleCount}건`} size="small" sx={pressGroupCountChipSx} />}
          {group.revisionDetectedArticleCount > 0 && <Chip label={`수정 ${group.revisionDetectedArticleCount}건`} size="small" sx={pressGroupCountChipSx} />}
          {group.loadError && <Chip label="기사 조회 실패" size="small" sx={dangerChipSx} />}
          {candidateStatusLabel && <Chip label={candidateStatusLabel} size="small" sx={candidateState.error ? dangerChipSx : blueChipSx} />}
          {showCandidateAction && (
            <Button
              type="button"
              size="small"
              disabled={candidateState.loading}
              onClick={(event) => {
                event.stopPropagation();
                onOpenCandidates();
              }}
              sx={pressCandidateButtonSx}
            >
              검색어 보조 후보
            </Button>
          )}
          <Box component="span" aria-hidden sx={toggleIconSx(expanded, showCandidateAction)} />
        </Box>
      </Box>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={pressGroupBodySx}>
          {articlesLoading ? <Typography sx={emptyStateSx}>연관 기사를 불러오는 중입니다.</Typography> : null}
          {group.loadError && !articlesLoading ? <Typography sx={errorStateSx}>{group.loadError}</Typography> : null}
          {!articlesLoading && group.groupKeywordMatched && group.articlesLoaded && group.articles.length === 0 ? (
            <Typography sx={emptyStateSx}>보도자료 정보가 검색어와 일치합니다. 현재 필터에 맞는 연관 기사는 없습니다.</Typography>
          ) : null}
          {!articlesLoading && group.articles.length ? (
            <Box sx={pressRelatedArticleGridSx}>
              {group.articles.map((article) => {
                const articleWithContext = { ...article, pressReleaseId: article.pressReleaseId || group.id };
                const articleKey = getRevisionArticleSelectionKey(articleWithContext, true);
                return (
                  <ArticleDenseRow
                    key={articleKey}
                    item={articleWithContext}
                    showTone={showTone}
                    showRevisionDiff={showRevisionDiff}
                    onOpenRevisionDiff={onOpenRevisionDiff}
                    onStartRevisionTracking={onStartRevisionTracking}
                    onStopRevisionTracking={onStopRevisionTracking}
                    revisionTrackingStopping={revisionTrackingStoppingKeys?.includes(articleKey)}
                    selectableForRevisionTracking={articleWithContext.revisionTrackingStatus !== "ACTIVE"}
                    revisionTrackingSelected={selectedRevisionArticleKeys?.includes(articleKey)}
                    onToggleRevisionTrackingSelected={onToggleRevisionTrackingSelected}
                    revisionPdfLoading={revisionPdfLoadingKeys?.includes(articleKey)}
                    revisionPdfActionKeys={revisionPdfActionKeys}
                    revisionPdfError={revisionPdfErrors?.[articleKey]}
                    onDownloadRevisionPdfArtifact={onDownloadRevisionPdfArtifact}
                    onRetryRevisionPdfArtifact={onRetryRevisionPdfArtifact}
                    onRefreshRevisionPdfArtifacts={onRefreshRevisionPdfArtifacts}
                    usePressReleaseContext
                  />
                );
              })}
            </Box>
          ) : group.loadError || articlesLoading || group.groupKeywordMatched ? null : (
            <Typography sx={emptyStateSx}>
              {group.articlesLoaded ? "이 보도자료와 연관된 기사가 없습니다." : "펼치면 연관 기사를 조회합니다."}
            </Typography>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

const notificationRecipientDeliveryStatusLabel: Record<NotificationRecipientDeliveryStatus, string> = {
  SUCCEEDED: "성공",
  FAILED: "실패",
  SUPPRESSED: "차단됨",
  PENDING: "대기",
  SENDING: "전송 중",
  QUEUED: "접수됨",
  UNKNOWN: "상태 미확인",
};

const RunHistoryTable = ({
  items,
  retryingRunIds,
  retryMessages,
  retryErrors,
  onRetry,
}: {
  items: RunHistoryItem[];
  retryingRunIds: string[];
  retryMessages: Record<string, string>;
  retryErrors: Record<string, string>;
  onRetry: (item: RunHistoryItem) => void;
}) => (
  <Box sx={runHistoryTableSx}>
    <Box sx={runHistoryHeaderSx}>
      <Box sx={runHeaderCellSx}>실행일자</Box>
      <Box sx={runHeaderCellSx}>결과</Box>
      <Box sx={runHeaderCellSx}>요청 여부</Box>
      <Box sx={runHeaderCellSx}>비고</Box>
    </Box>
    {items.map((item) => (
      <RunDenseRow
        key={item.id}
        item={item}
        retrying={retryingRunIds.includes(item.id)}
        retryMessage={retryMessages[item.id]}
        retryError={retryErrors[item.id]}
        onRetry={onRetry}
      />
    ))}
  </Box>
);

const RunDenseRow = ({
  item,
  retrying,
  retryMessage,
  retryError,
  onRetry,
}: {
  item: RunHistoryItem;
  retrying: boolean;
  retryMessage?: string;
  retryError?: string;
  onRetry: (item: RunHistoryItem) => void;
}) => {
  const [detailsExpanded, setDetailsExpanded] = useState(false);
  const status = runStatusMeta[item.status];
  const notificationStatus = notificationDeliveryMeta[item.notificationDeliveryStatus];
  const retryTargetCount = getManualRetryEligibleNotificationRecipients(item).length;
  const deliveryInProgressCount = getNotificationDeliveryInProgressCount(item);
  const retryAvailable = item.notificationSnapshotAvailable && retryTargetCount > 0 && deliveryInProgressCount === 0;
  const showRetryAction = item.notificationSnapshotAvailable && (retryTargetCount > 0 || deliveryInProgressCount > 0);
  const detailId = `notification-recipient-details-${encodeURIComponent(item.id)}`;
  const rowMessage = [item.message, item.notificationDeliveryMessage, retryMessage, retryError].filter(Boolean).join(" · ");
  return (
    <Box>
      <Box sx={runRowSx(item.status)}>
        <Typography sx={runTimeSx}>{formatDateTime(item.scheduledAt)}</Typography>
        <Box sx={runResultCellSx}>
          <Chip label={status.label} size="small" sx={runStatusChipSx(status)} />
          <Typography sx={runStatSx}>발견 <Box component="span" sx={runStatValueSx}>{item.foundCount}</Box></Typography>
          <Typography sx={runStatSx}>확인 <Box component="span" sx={runStatValueSx}>{item.needsReviewCount}</Box></Typography>
          <Typography sx={runStatSx}>알림 <Box component="span" sx={runStatValueSx}>{item.notificationSentCount}</Box></Typography>
        </Box>
        <Typography sx={runNotificationStatusSx(notificationStatus)}>{notificationStatus.label}</Typography>
        <Box sx={runMessageCellSx}>
          <Typography role="status" aria-live="polite" sx={runMessageSx}>{rowMessage}</Typography>
          {item.notificationSnapshotAvailable && (
            <Button
              type="button"
              size="small"
              variant="outlined"
              sx={runDetailButtonSx}
              aria-expanded={detailsExpanded}
              aria-controls={detailId}
              onClick={() => setDetailsExpanded((expanded) => !expanded)}
            >
              {detailsExpanded ? "알림 결과 접기" : "알림 결과 상세"}
            </Button>
          )}
          {showRetryAction && (
            <Button
              type="button"
              size="small"
              variant="outlined"
              sx={runRetryButtonSx}
              disabled={retrying || !retryAvailable}
              aria-label={`실패한 수신자 ${retryTargetCount}명에게 알림 재시도`}
              onClick={() => onRetry(item)}
            >
              {deliveryInProgressCount > 0 ? "전송 진행 중" : retrying ? "재시도 요청 중" : `실패 수신자 ${retryTargetCount}명 재시도`}
            </Button>
          )}
        </Box>
      </Box>
      {item.notificationSnapshotAvailable && detailsExpanded && (
        <Box component="section" id={detailId} aria-label="알림 수신자별 전송 결과" sx={runNotificationDetailSx}>
          <Typography sx={runNotificationSummarySx}>
            수신자 {item.notificationRecipientSummary.totalCount}명 · 성공 {item.notificationRecipientSummary.succeededCount}명 · 실패 {item.notificationRecipientSummary.failedCount}명 · 대기 {item.notificationRecipientSummary.pendingCount}명 · 전송 중 {item.notificationRecipientSummary.sendingCount}명
          </Typography>
          {deliveryInProgressCount > 0 && (
            <Typography sx={runNotificationInProgressSx}>전송이 진행 중이므로 완료 또는 실패 상태가 확인될 때까지 재시도할 수 없습니다.</Typography>
          )}
          {item.notificationRecipientResults.length === 0 ? (
            <Typography sx={runNotificationRecipientEmptySx}>수신자별 전송 결과를 아직 받지 못했습니다.</Typography>
          ) : (
            <Box component="ul" sx={runNotificationRecipientListSx}>
              {item.notificationRecipientResults.map((recipient, index) => (
                <Box component="li" key={`${recipient.recipientId || "recipient"}-${index}`} sx={runNotificationRecipientItemSx}>
                  <Typography sx={runNotificationRecipientTextSx}>
                    수신자 {index + 1} · 전송 상태: {notificationRecipientDeliveryStatusLabel[recipient.deliveryStatus]}{recipient.retryable ? " · 자동 재시도 가능" : ""}
                  </Typography>
                  {recipient.lastMessage && (
                    <Typography sx={runNotificationRecipientErrorSx}>전송 메시지: {recipient.lastMessage}</Typography>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

const PressReleaseCandidateDialog = ({
  group,
  state,
  query,
  onClose,
  onQueryChange,
  onResetQuery,
  onSearch,
  onApprove,
}: {
  group: PressReleaseGroup | null;
  state: CandidateSearchState;
  query: string;
  onClose: () => void;
  onQueryChange: (value: string) => void;
  onResetQuery: () => void;
  onSearch: () => void;
  onApprove: (candidate: PressReleaseCandidate) => void;
}) => {
  const normalizedQuery = query.trim();
  const hasApprovingCandidate = Boolean(state.approvingArticleIds?.length);
  const suggestedSearchAvailable = Boolean(group?.suggestedSearchQuery);
  return (
    <Dialog
      open={Boolean(group)}
      onClose={onClose}
      maxWidth={false}
      BackdropProps={{ sx: candidateDialogBackdropSx }}
      PaperProps={{ sx: candidateDialogPaperSx }}
    >
      <DialogTitle sx={candidateDialogTitleSx}>
        <Box>
          <Typography sx={candidateDialogHeadingSx}>검색어 보조 후보 찾기</Typography>
          <Typography sx={candidateDialogSubtitleSx}>후보는 승인 전까지 연관 기사 수에 반영되지 않습니다.</Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={candidateCloseButtonSx}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent sx={candidateDialogContentSx}>
        {group && (
          <Stack sx={candidateDialogStackSx}>
            <Box sx={candidateSearchSummarySx}>
              <Box sx={candidateBasisGridSx}>
                <CandidateBasis label="원제목 검색어" value={group.originalSearchTitle || group.title} />
                <CandidateBasis
                  label="추천 검색어"
                  value={group.suggestedSearchQuery || "추천 검색어 없음"}
                  highlighted
                  actionLabel="사용하기"
                  actionDisabled={!suggestedSearchAvailable || state.loading || hasApprovingCandidate}
                  onAction={onResetQuery}
                />
              </Box>
              <Box sx={candidateQuerySectionSx}>
                <Typography sx={candidateBasisLabelSx}>후보 검색어 직접 입력</Typography>
                <Box sx={candidateQueryBoxSx}>
                  <TextField
                    value={query}
                    onChange={(event) => onQueryChange(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") onSearch();
                    }}
                    size="small"
                    fullWidth
                    placeholder="후보 검색어를 입력해주세요."
                    sx={candidateQueryInputSx}
                  />
                  <Button size="small" variant="contained" disabled={state.loading || hasApprovingCandidate || !normalizedQuery} onClick={onSearch} sx={candidateSearchButtonSx}>
                    {state.loading ? "검색 중" : "검색"}
                  </Button>
                </Box>
              </Box>
            </Box>
            {state.error && <Typography sx={errorTextSx}>{state.error}</Typography>}
            {state.loading && state.items.length === 0 && <Typography sx={emptyStateSx}>검색어 보조 후보를 검색 중입니다.</Typography>}
            {state.searched && !state.loading && state.items.length === 0 && <Typography sx={emptyStateSx}>검색된 후보 기사가 없습니다.</Typography>}
            {state.items.length ? (
              <Box sx={candidateResultListSx}>
                {state.items.map((candidate) => (
                  <CandidateArticleRow
                    key={candidate.id}
                    item={candidate}
                    approving={Boolean(state.approvingArticleIds?.includes(candidate.id))}
                    onApprove={() => onApprove(candidate)}
                  />
                ))}
              </Box>
            ) : null}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};

const CandidateBasis = ({
  label,
  value,
  highlighted = false,
  actionLabel,
  actionDisabled,
  onAction,
}: {
  label: string;
  value: string;
  highlighted?: boolean;
  actionLabel?: string;
  actionDisabled?: boolean;
  onAction?: () => void;
}) => (
  <Box sx={candidateBasisColumnSx}>
    <Typography sx={candidateBasisLabelSx}>{label}</Typography>
    <Box sx={candidateBasisCardSx(highlighted)}>
      <Typography sx={candidateBasisValueSx}>{value}</Typography>
      {actionLabel && (
        <Box sx={candidateBasisActionWrapSx}>
          <Button size="small" variant="outlined" disabled={actionDisabled} onClick={onAction} sx={candidateBasisActionSx}>
            {actionLabel}
          </Button>
        </Box>
      )}
    </Box>
  </Box>
);

const CandidateArticleRow = ({ item, approving, onApprove }: { item: PressReleaseCandidate; approving: boolean; onApprove: () => void }) => {
  const sourceLabel = item.publisher || (item.alreadyRelated ? "이미 반영됨" : "보조 후보");
  return (
    <Box sx={candidateRowSx}>
      <Box sx={candidateRowContentSx}>
        <Stack direction="row" spacing={1} alignItems="center" sx={candidateRowMetaSx}>
          <Chip label={sourceLabel} size="small" sx={candidateSourceChipSx} />
          <Typography sx={candidateMetaSx}>{item.reporter || "기자 미확인"} · {formatDateTime(item.publishedAt)}</Typography>
        </Stack>
        <Typography sx={candidateTitleSx}>{item.title}</Typography>
      </Box>
      <Stack direction="row" spacing={0.5} justifyContent="flex-end" sx={candidateRowActionSx}>
        {item.url && (
          <Button variant="outlined" size="small" href={item.url} target="_blank" rel="noopener noreferrer" startIcon={<OpenInNewIcon sx={candidateOriginalIconSx} />} sx={candidateOriginalButtonSx}>
            원문
          </Button>
        )}
        <Button variant="outlined" size="small" disabled={item.alreadyRelated || approving} onClick={onApprove} sx={item.alreadyRelated ? candidateApprovedButtonSx : candidateApproveButtonSx}>
          {item.alreadyRelated ? "이미 반영됨" : approving ? "반영 중" : "연관 기사로 반영"}
        </Button>
      </Stack>
    </Box>
  );
};

const ArticleRevisionTrackingDialog = ({
  open,
  article,
  trackingHours,
  notifyOnRevisionDetected,
  saving,
  error,
  onChangeHours,
  onChangeNotify,
  onClose,
  onSubmit,
}: {
  open: boolean;
  article: ResultItem | null;
  trackingHours: number;
  notifyOnRevisionDetected: boolean;
  saving: boolean;
  error: string | null;
  onChangeHours: (value: number) => void;
  onChangeNotify: (value: boolean) => void;
  onClose: () => void;
  onSubmit: () => void;
}) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth={false}
    BackdropProps={{ sx: articleRevisionStartDialogBackdropSx }}
    PaperProps={{ sx: articleRevisionStartDialogPaperSx }}
  >
    <DialogTitle sx={articleRevisionStartDialogTitleSx}>
      <Box sx={articleRevisionStartDialogTitleBlockSx}>
        <Typography sx={articleRevisionStartDialogHeadingSx}>기사 수정 추적 시작</Typography>
        <Typography sx={articleRevisionStartDialogSubtitleSx}>현재 제목과 본문을 기준으로 선택한 시간 동안 재수집 결과와 비교합니다.</Typography>
      </Box>
    </DialogTitle>
    <DialogContent sx={articleRevisionStartDialogContentSx}>
      <Box sx={articleRevisionStartDialogTargetSx}>
        <Typography sx={articleRevisionStartDialogFieldLabelSx}>대상 기사</Typography>
        <Box sx={articleRevisionStartDialogTargetTextSx}>
          <Typography sx={articleRevisionStartDialogArticleTitleSx}>{article?.title ?? "-"}</Typography>
          <Typography sx={articleRevisionStartDialogTargetDescriptionSx}>{article?.publisher ?? "매체 미확인"} · {formatDateTime(article?.publishedAt)}</Typography>
        </Box>
      </Box>
      <Box sx={articleRevisionStartDialogControlGroupSx}>
        <Box sx={articleRevisionStartDialogFieldSx}>
          <Typography sx={articleRevisionStartDialogFieldLabelSx}>추적 시간</Typography>
        <TextField
          select
          value={trackingHours}
          onChange={(event) => onChangeHours(Number(event.target.value))}
          size="small"
          fullWidth
          SelectProps={{ inputProps: { "aria-label": "추적 시간" } }}
          sx={articleRevisionStartSelectSx}
        >
          {[1, 3, 6, 12].map((hour) => (
            <MenuItem key={hour} value={hour}>{hour}시간</MenuItem>
          ))}
        </TextField>
        </Box>
        <Box sx={articleRevisionStartNotifyRowSx}>
          <Typography sx={articleRevisionStartNotifyLabelSx}>수정 감지 시 Teams 알림</Typography>
          <Switch checked={notifyOnRevisionDetected} onChange={(event) => onChangeNotify(event.target.checked)} sx={articleRevisionStartSwitchSx} />
        </Box>
      </Box>
      {error && <Typography sx={articleRevisionStartErrorSx}>{error}</Typography>}
    </DialogContent>
    <DialogActions sx={articleRevisionStartDialogActionsSx}>
      <Button variant="outlined" sx={articleRevisionStartCancelButtonSx} disabled={saving} onClick={onClose}>취소</Button>
      <Button variant="contained" sx={articleRevisionStartSubmitButtonSx} disabled={saving || !article} onClick={onSubmit}>
        {saving ? "시작 중" : "추적 시작"}
      </Button>
    </DialogActions>
  </Dialog>
);

const BatchArticleRevisionTrackingDialog = ({
  open,
  selectedCount,
  trackingHours,
  notifyOnRevisionDetected,
  saving,
  error,
  summary,
  onChangeHours,
  onChangeNotify,
  onClose,
  onSubmit,
}: {
  open: boolean;
  selectedCount: number;
  trackingHours: number;
  notifyOnRevisionDetected: boolean;
  saving: boolean;
  error: string | null;
  summary: string | null;
  onChangeHours: (value: number) => void;
  onChangeNotify: (value: boolean) => void;
  onClose: () => void;
  onSubmit: () => void;
}) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth={false}
    BackdropProps={{ sx: articleRevisionStartDialogBackdropSx }}
    PaperProps={{ sx: articleRevisionStartDialogPaperSx }}
  >
    <DialogTitle sx={articleRevisionStartDialogTitleSx}>
      <Box sx={articleRevisionStartDialogTitleBlockSx}>
        <Typography sx={articleRevisionStartDialogHeadingSx}>선택 기사 수정 추적 시작</Typography>
        <Typography sx={articleRevisionStartDialogSubtitleSx}>선택한 기사들의 현재 제목과 본문을 기준으로 일괄 추적을 시작합니다.</Typography>
      </Box>
    </DialogTitle>
    <DialogContent sx={articleRevisionStartDialogContentSx}>
      <Box sx={articleRevisionStartDialogTargetSx}>
        <Typography sx={articleRevisionStartDialogFieldLabelSx}>선택한 기사</Typography>
        <Box sx={articleRevisionStartDialogTargetTextSx}>
          <Typography sx={articleRevisionStartDialogCountSx}>{selectedCount}개</Typography>
          <Typography sx={articleRevisionStartDialogTargetDescriptionSx}>이미 추적 중인 기사는 자동으로 제외되며 baseline을 재설정하지 않습니다.</Typography>
        </Box>
      </Box>
      <Box sx={articleRevisionStartDialogControlGroupSx}>
        <Box sx={articleRevisionStartDialogFieldSx}>
          <Typography sx={articleRevisionStartDialogFieldLabelSx}>추적 시간</Typography>
        <TextField
          select
          value={trackingHours}
          onChange={(event) => onChangeHours(Number(event.target.value))}
          size="small"
          fullWidth
          SelectProps={{ inputProps: { "aria-label": "추적 시간" } }}
          sx={articleRevisionStartSelectSx}
        >
          {[1, 3, 6, 12].map((hour) => (
            <MenuItem key={hour} value={hour}>{hour}시간</MenuItem>
          ))}
        </TextField>
        </Box>
        <Box sx={articleRevisionStartNotifyRowSx}>
          <Typography sx={articleRevisionStartNotifyLabelSx}>수정 감지 시 Teams 알림</Typography>
          <Switch checked={notifyOnRevisionDetected} onChange={(event) => onChangeNotify(event.target.checked)} sx={articleRevisionStartSwitchSx} />
        </Box>
      </Box>
      {summary && <Typography sx={articleRevisionStartSummarySx}>{summary}</Typography>}
      {error && <Typography sx={articleRevisionStartErrorSx}>{error}</Typography>}
    </DialogContent>
    <DialogActions sx={articleRevisionStartDialogActionsSx}>
      <Button variant="outlined" sx={articleRevisionStartCancelButtonSx} disabled={saving} onClick={onClose}>취소</Button>
      <Button variant="contained" sx={articleRevisionStartSubmitButtonSx} disabled={saving || selectedCount === 0} onClick={onSubmit}>
        {saving ? "시작 중" : "추적 시작"}
      </Button>
    </DialogActions>
  </Dialog>
);

const RevisionDiffDialog = ({
  open,
  article,
  data,
  loading,
  error,
  pdfLoading,
  pdfError,
  pdfActionKeys,
  highlightEnabled,
  onToggleHighlight,
  onDownloadRevisionPdfArtifact,
  onRetryRevisionPdfArtifact,
  onRefreshRevisionPdfArtifacts,
  usePressReleaseContext,
  onClose,
}: {
  open: boolean;
  article: ResultItem | null;
  data: RevisionDiffData | null;
  loading: boolean;
  error: string | null;
  pdfLoading: boolean;
  pdfError?: string;
  pdfActionKeys: string[];
  highlightEnabled: boolean;
  onToggleHighlight: () => void;
  onDownloadRevisionPdfArtifact?: (item: ResultItem, artifact: ArticleRevisionPdfArtifact) => void;
  onRetryRevisionPdfArtifact?: (item: ResultItem, artifact: ArticleRevisionPdfArtifact) => void;
  onRefreshRevisionPdfArtifacts?: (item: ResultItem) => void;
  usePressReleaseContext: boolean;
  onClose: () => void;
}) => {
  const diffEntries = data?.diff ?? [];
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      BackdropProps={{ sx: revisionDiffDialogBackdropSx }}
      PaperProps={{ sx: revisionDiffDialogPaperSx }}
    >
      <DialogTitle sx={revisionDiffDialogTitleSx}>
        <Typography sx={revisionDiffDialogHeadingSx}>수정 비교</Typography>
        <IconButton onClick={onClose} size="small" sx={candidateCloseButtonSx}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent sx={revisionDiffDialogContentSx}>
        {loading ? (
          <Typography sx={revisionDiffStateSx}>수정 비교 정보를 불러오는 중입니다.</Typography>
        ) : error ? (
          <Typography sx={revisionDiffErrorSx}>{error}</Typography>
        ) : !data?.exists ? (
          <Typography sx={revisionDiffStateSx}>수정 추적 스냅샷이 없습니다.</Typography>
        ) : (
          <Box sx={revisionDiffContentStackSx}>
            {article && (pdfLoading || pdfError || hasRevisionPdfArtifacts(article.revisionPdfArtifacts)) && (
              <RevisionPdfArtifactStatusList
                article={article}
                artifacts={article.revisionPdfArtifacts}
                loading={pdfLoading}
                error={pdfError}
                actionKeys={pdfActionKeys}
                onDownload={onDownloadRevisionPdfArtifact}
                onRetry={onRetryRevisionPdfArtifact}
                onRefresh={onRefreshRevisionPdfArtifacts}
                usePressReleaseContext={usePressReleaseContext}
                flushTop
              />
            )}
            {diffEntries.length ? diffEntries.map((entry, index) => (
              <Box key={entry.field} sx={revisionBlockSx}>
                {index === 0 && (
                  <Box sx={revisionDiffHighlightRowSx}>
                    <Box component="label" htmlFor="revision-diff-highlight-switch" sx={revisionDiffHighlightLabelSx}>
                      수정된 부분 표시
                    </Box>
                    <Switch
                      id="revision-diff-highlight-switch"
                      checked={highlightEnabled}
                      onChange={() => onToggleHighlight()}
                      sx={revisionDiffSwitchSx}
                      inputProps={{ "aria-label": "수정된 부분 표시" }}
                    />
                  </Box>
                )}
                <Box sx={revisionDiffSectionHeadingSx}>
                  <Typography sx={revisionDiffSectionTitleSx}>{entry.label}</Typography>
                  <Box component="span" sx={revisionDiffSectionBadgeSx(entry.label, entry.changed)}>
                    {entry.changed ? "변경됨" : "변경 없음"}
                  </Box>
                </Box>
                <Box sx={revisionCompareGridSx}>
                  <RevisionTextPanel variant="before" title="변경 전" text={entry.before} compareText={entry.after} changed={entry.changed} highlightEnabled={highlightEnabled} longForm={isRevisionDiffLongFormField(entry)} />
                  <RevisionTextPanel variant="after" title="변경 후" text={entry.after} compareText={entry.before} changed={entry.changed} highlightEnabled={highlightEnabled} longForm={isRevisionDiffLongFormField(entry)} />
                </Box>
              </Box>
            )) : (
              <Typography sx={revisionDiffStateSx}>비교 가능한 수정 항목이 없습니다.</Typography>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

const RevisionTextPanel = ({
  variant,
  title,
  text,
  compareText,
  changed,
  highlightEnabled,
  longForm,
}: {
  variant: "before" | "after";
  title: string;
  text: string;
  compareText: string;
  changed: boolean;
  highlightEnabled: boolean;
  longForm: boolean;
}) => {
  const value = text?.trim() || "-";
  const parts = changed && highlightEnabled ? buildRevisionHighlightParts(value, compareText?.trim() || "") : [{ text: value, highlighted: false }];
  return (
    <Box sx={revisionTextPanelSx(longForm)}>
      <Typography sx={revisionTextPanelTitleSx}>{title}</Typography>
      <Typography component="div" sx={revisionTextSx}>
        {parts.map((part, index) => (
          <Box key={`${part.text}-${index}`} component="span" sx={part.highlighted ? revisionTextHighlightSx(variant) : undefined}>
            {part.text}
          </Box>
        ))}
      </Typography>
    </Box>
  );
};

const isRevisionDiffLongFormField = (entry: RevisionDiffEntry) => entry.field === "body" || entry.label.includes("본문");

const buildRevisionHighlightParts = (source: string, target: string): RevisionHighlightPart[] => {
  if (!source || source === target) return [{ text: source || "-", highlighted: false }];
  const sourceChars = Array.from(source);
  const targetChars = Array.from(target);
  let prefix = 0;
  while (prefix < sourceChars.length && prefix < targetChars.length && sourceChars[prefix] === targetChars[prefix]) {
    prefix += 1;
  }
  let suffix = 0;
  while (
    suffix < sourceChars.length - prefix
    && suffix < targetChars.length - prefix
    && sourceChars[sourceChars.length - 1 - suffix] === targetChars[targetChars.length - 1 - suffix]
  ) {
    suffix += 1;
  }
  const before = sourceChars.slice(0, prefix).join("");
  const middle = sourceChars.slice(prefix, sourceChars.length - suffix).join("");
  const after = suffix ? sourceChars.slice(sourceChars.length - suffix).join("") : "";
  return [
    { text: before, highlighted: false },
    { text: middle || source, highlighted: Boolean(middle) },
    { text: after, highlighted: false },
  ].filter((part) => part.text.length > 0);
};

const PolicyBlock = ({ label, values, empty }: { label: string; values: string[]; empty: string }) => (
  <Box sx={settingsTermCardSx}>
    <Typography sx={settingsTermLabelSx}>{label}</Typography>
    <Stack direction="row" spacing={0.5} sx={settingsChipListSx}>
      {values.length ? values.map((value) => <Chip key={value} label={value} size="small" sx={settingsChipSx} />) : <Typography sx={settingsEmptyTextSx}>{empty}</Typography>}
    </Stack>
  </Box>
);

const SearchTermExcludePolicyBlock = ({
  rows,
  commonExcludeTerms,
}: {
  rows: SearchTermExcludeDisplayRow[];
  commonExcludeTerms: string[];
}) => {
  const hasRows = rows.length > 0;
  const hasCommon = commonExcludeTerms.length > 0;
  return (
    <Box sx={searchTermExcludePolicyBoxSx}>
      <Typography sx={settingsTermLabelSx}>검색어별 적용 제외어</Typography>
      <Stack spacing={0.5}>
        {hasRows ? (
          rows.map((row) => <SearchTermExcludePolicyRow key={`${row.searchTermId || row.term}`} label={row.term} terms={row.excludeTerms} />)
        ) : hasCommon ? (
          <SearchTermExcludePolicyRow label="공통" terms={commonExcludeTerms} />
        ) : (
          <Typography sx={settingsEmptyTextSx}>등록된 제외어 없음</Typography>
        )}
        {hasRows && hasCommon && <SearchTermExcludePolicyRow label="공통" terms={commonExcludeTerms} />}
      </Stack>
    </Box>
  );
};

const SearchTermExcludePolicyRow = ({ label, terms }: { label: string; terms: string[] }) => (
  <Box sx={searchTermExcludePolicyRowSx}>
    <Typography sx={searchTermExcludePolicyTermSx} noWrap>{label}</Typography>
    <Stack direction="row" spacing={0.45} sx={searchTermExcludePolicyChipListSx}>
      {terms.length ? (
        terms.map((term) => <Chip key={`${label}-${term}`} label={term} size="small" sx={settingsChipSx} />)
      ) : (
        <Typography sx={settingsEmptyTextSx}>없음</Typography>
      )}
    </Stack>
  </Box>
);

const PolicyRow = ({ label, value }: { label: string; value: string }) => (
  <Stack direction="row" justifyContent="space-between" spacing={1} sx={settingsPolicyRowSx}>
    <Typography sx={settingsPolicyLabelSx}>{label}</Typography>
    <Typography sx={policyValueSx}>{value}</Typography>
  </Stack>
);

const getSentimentPolicyDescription = (state: SentimentKeywordDisplayState) => {
  if (state.status === "DISABLED") return "부정 판정이 비활성화되어 현재 기사 판별에는 키워드를 적용하지 않습니다.";
  if (state.status === "CUSTOM") return "이 모니터링에 저장된 맞춤 부정 키워드를 적용합니다.";
  return "전역 부정 키워드 설정을 적용합니다. 이 화면에서는 전역 목록을 표시하지 않습니다.";
};

const SentimentKeywordPolicyBlock = ({ state }: { state: SentimentKeywordDisplayState }) => (
  <Box sx={sentimentPolicyBoxSx}>
    <Stack direction="row" spacing={0.7} alignItems="center" justifyContent="space-between" sx={settingsSentimentHeaderSx}>
      <Typography sx={settingsPolicyLabelSx}>부정 판정 키워드</Typography>
      <Chip label={state.statusLabel} size="small" sx={sentimentStatusChipSx(state.status)} />
    </Stack>
    <Typography sx={sentimentPolicyDescriptionSx}>{getSentimentPolicyDescription(state)}</Typography>
    {state.status === "CUSTOM" && (
      <Box sx={sentimentKeywordGridSx}>
        <SentimentKeywordGroup label="부정 키워드" keywords={state.negativeSentimentKeywords} empty="설정된 부정 키워드 없음" />
      </Box>
    )}
  </Box>
);

const SentimentKeywordGroup = ({
  label,
  keywords,
  empty,
}: {
  label: string;
  keywords: string[];
  empty: string;
}) => (
  <Box sx={sentimentKeywordGroupSx}>
    <Typography sx={settingsTermLabelSx}>{label}</Typography>
    <Stack direction="row" spacing={0.5} sx={settingsSentimentKeywordListSx}>
      {keywords.length ? (
        keywords.map((keyword) => (
          <Chip key={keyword} label={keyword} size="small" sx={sentimentNegativeChipSx} />
        ))
      ) : (
        <Typography sx={settingsEmptyTextSx}>{empty}</Typography>
      )}
    </Stack>
  </Box>
);

const StatusChip = ({ status }: { status: MonitoringStatus }) => (
  <Chip label={status === "ACTIVE" ? "활성" : "비활성"} size="small" sx={monitorStatusBadgeSx(status)} />
);

const toneMeta: Record<Tone, { label: string; bg: string; color: string }> = {
  NEGATIVE: { label: "부정", bg: "#FFF2F2", color: "#E32329" },
  NEUTRAL: { label: "중립", bg: "#DCDFE5", color: "#2E3341" },
  UNKNOWN: { label: "미분류", bg: "#DCDFE5", color: "#2E3341" },
};

const runStatusMeta: Record<RunStatus, { label: string; bg: string; color: string }> = {
  SUCCESS: { label: "성공", bg: "#ECF8EF", color: "#078923" },
  FAILED: { label: "실패", bg: "#FFF2F2", color: "#E32329" },
  SKIPPED: { label: "건너뜀", bg: "#DCDFE5", color: "#2E3341" },
  RUNNING: { label: "실행 중", bg: "#F0F4FF", color: "#5D88F1" },
};

const notificationDeliveryMeta: Record<NotificationDeliveryStatus, { label: string; bg: string; color: string }> = {
  NOT_REQUESTED: { label: "미요청", bg: "transparent", color: "#2E3341" },
  QUEUED: { label: "대기", bg: "transparent", color: "#2E3341" },
  SUCCEEDED: { label: "성공", bg: "transparent", color: "#078923" },
  FAILED: { label: "실패", bg: "transparent", color: "#E32329" },
  SUPPRESSED: { label: "전송 차단", bg: "transparent", color: "#92400e" },
};

const detailFontFamily = "\"Pretendard\", sans-serif";

const pageSx = {
  minHeight: "100%",
  bgcolor: "#F2F4F8",
  px: { xs: 2, md: 3 },
  py: { xs: 2.5, md: 5 },
  boxSizing: "border-box",
  fontFamily: detailFontFamily,
  letterSpacing: 0,
  "& *": { fontFamily: detailFontFamily, letterSpacing: 0 },
};
const pageContentSx = { width: "100%", maxWidth: 1518, mx: "auto" };
const topActionRowSx = {
  display: "flex",
  flexDirection: { xs: "column", md: "row" },
  justifyContent: "space-between",
  alignItems: { xs: "stretch", md: "center" },
  gap: 1,
};
const topActionControlsSx = {
  justifyContent: { xs: "flex-start", md: "flex-end" },
  flexWrap: "wrap",
  minWidth: 0,
};
const monitorSwitchWrapSx = { minWidth: 0, width: { xs: "100%", md: "auto" } };
const topActionLabelSx = { color: "#2E3341", fontSize: 13, fontWeight: 600, lineHeight: "20px", whiteSpace: "nowrap" };
const headerPanelSx = { borderRadius: "12px", border: "0", p: 0, bgcolor: "#fff", boxShadow: "0px 6px 14px rgba(68, 100, 178, 0.08)", overflow: "hidden" };
const panelSx = { overflow: "hidden", borderRadius: "12px", border: "0", bgcolor: "#fff", boxShadow: "0px 6px 14px rgba(68, 100, 178, 0.08)" };
const headerTopSx = { display: "flex", justifyContent: "space-between", alignItems: "flex-start", height: 92, boxSizing: "border-box", px: 3, pt: 3, pb: 1, gap: 1 };
const headerInfoSx = { display: "grid", gap: 0.5, minWidth: 0 };
const headerTitleRowSx = { minWidth: 0, flexWrap: "wrap", rowGap: 0.75 };
const pageTitleSx = { color: "#2E3341", fontSize: 24, fontWeight: 700, lineHeight: "32px", display: "flex", alignItems: "center" };
const mutedTextSx = { color: "#6F7279", fontSize: 16, fontWeight: 400, lineHeight: "24px" };
const noticeTextSx = { mt: 1, fontSize: 13, fontWeight: 500, lineHeight: "20px" };
const batchRevisionTrackingSnackbarSx: SxProps<Theme> = {
  bottom: "48px",
  maxWidth: "calc(100vw - 32px)",
};
const batchRevisionTrackingToastSx: SxProps<Theme> = {
  width: { xs: "100%", sm: "fit-content" },
  maxWidth: "calc(100vw - 32px)",
  minHeight: 48,
  px: 2,
  py: 1,
  borderRadius: "8px",
  bgcolor: "#12B43D",
  color: "#FFFFFF",
  display: "flex",
  alignItems: "center",
  gap: 1,
  boxSizing: "border-box",
  boxShadow: "0px 12px 24px rgba(0, 0, 0, 0.32)",
};
const batchRevisionTrackingToastIconSx = { width: 24, height: 24, flex: "0 0 auto", color: "inherit" };
const batchRevisionTrackingToastTextSx = {
  minWidth: 0,
  color: "inherit",
  fontFamily: "Pretendard",
  fontSize: 16,
  fontWeight: 500,
  lineHeight: "24px",
  letterSpacing: 0,
  whiteSpace: { xs: "normal", sm: "nowrap" },
  wordBreak: "keep-all",
};
const primaryButtonSx = { minWidth: 0, px: 1.25, py: 0.5, borderRadius: "8px", bgcolor: "#2E3341", color: "#fff", fontSize: 14, fontWeight: 600, lineHeight: "14px", boxShadow: "none", "&:hover": { bgcolor: "#252A36", boxShadow: "none" } };
const outlineButtonSx = { minWidth: 0, px: 1.05, py: 0.45, borderRadius: "8px", color: "#2E3341", borderColor: "#DDE0EA", bgcolor: "#fff", fontSize: 14, fontWeight: 600, lineHeight: "14px", boxShadow: "none", "&:hover": { borderColor: "#C9CDD8", bgcolor: "#fff" } };
const topActionButtonSx = { minWidth: 99, width: { xs: "100%", md: 99 }, height: 40, px: 2, py: 0, gap: 0.5, borderRadius: "8px", color: "#2E3341", borderColor: "#DDE0EA", bgcolor: "#fff", fontSize: 14, fontWeight: 600, lineHeight: "14px", boxShadow: "none", whiteSpace: "nowrap", "&:hover": { borderColor: "#C9CDD8", bgcolor: "#fff", boxShadow: "none" }, "& .MuiButton-startIcon": { m: 0 } };
const topBackButtonSx = { ...topActionButtonSx, justifyContent: "center", width: { xs: "100%", sm: 177 }, minWidth: 177 };
const rerunButtonSx = { minWidth: 121, width: { xs: "100%", md: 121 }, height: 40, px: 2, py: 0, gap: 0.5, borderRadius: "8px", bgcolor: "#2E3341", color: "#fff", fontSize: 14, fontWeight: 600, lineHeight: "14px", boxShadow: "none", whiteSpace: "nowrap", "&:hover": { bgcolor: "#252A36", boxShadow: "none" }, "&.Mui-disabled": { bgcolor: "#98a2b3", color: "#fff" }, "& .MuiButton-startIcon": { m: 0 } };
const monitorSwitchFieldSx = {
  width: { xs: "100%", sm: 240 },
  "& .MuiOutlinedInput-root": {
    height: 40,
    borderRadius: "8px",
    bgcolor: "#fff",
    color: "#2E3341",
    fontSize: 16,
    fontWeight: 400,
    lineHeight: "24px",
    position: "relative",
    "&::after": {
      content: "\"\"",
      position: "absolute",
      right: 16,
      top: "50%",
      width: 12,
      height: 7,
      transform: "translateY(-50%)",
      backgroundImage: `url(${monitoringDetailIcons.selectArrow})`,
      backgroundRepeat: "no-repeat",
      backgroundSize: "contain",
      backgroundPosition: "center",
      pointerEvents: "none",
    },
  },
  "& .MuiSelect-select": { py: 1, px: 2, pr: "42px !important", color: "#2E3341", fontSize: 16, fontWeight: 400, lineHeight: "24px" },
  "& .MuiSelect-icon": { display: "none" },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#DDE0EA" },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#C8CDD9" },
};
const rerunPlayIconSx = { width: 16, height: 16, objectFit: "contain", display: "block", flexShrink: 0 };
const monitorTypeBadgeSx = {
  minWidth: 61,
  height: 32,
  borderRadius: "16px",
  bgcolor: "#DCDFE5",
  color: "#2E3341",
  fontSize: 14,
  fontWeight: 500,
  lineHeight: "14px",
  "& .MuiChip-label": { px: 1.5 },
};
const monitorStatusBadgeSx = (status: MonitoringStatus) => ({
  minWidth: status === "ACTIVE" ? 49 : 61,
  height: 32,
  borderRadius: "16px",
  bgcolor: status === "ACTIVE" ? "#D5F5D7" : "#DCDFE5",
  color: status === "ACTIVE" ? "#117C18" : "#2E3341",
  fontSize: 14,
  fontWeight: 500,
  lineHeight: "14px",
  "& .MuiChip-label": { px: 1.5 },
});
const filterCountBadgeSx = { ...monitorTypeBadgeSx, bgcolor: "#EEF2FF", color: "#5D88F1" };
const metricRailSx = { display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(3, minmax(0, 1fr))" }, gap: 2, px: 3, py: 3, minHeight: 164, height: { lg: 164 }, boxSizing: "border-box", alignItems: "center" };
const metricCardSx = (tone: "normal" | "danger" | "warning", compact: boolean) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: 1,
  px: 2,
  py: 2,
  minHeight: compact ? 114 : 116,
  height: compact ? 114 : 116,
  boxSizing: "border-box",
  borderRadius: "12px",
  border: "0",
  bgcolor: tone === "danger" ? "#FCEEEE" : "#FAFAFA",
  boxShadow: "none",
});
const metricContentSx = (detailed: boolean) => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: detailed ? "flex-start" : "space-between",
  alignItems: "flex-start",
  gap: 1,
  width: "100%",
  height: "100%",
});
const metricLabelSx = { color: "#2E3341", fontSize: 14, fontWeight: 600, lineHeight: "20px" };
const metricValueRowSx = { display: "flex", alignItems: "flex-end", gap: 1, minHeight: 32 };
const metricValueSx = { color: "#2E3341", fontSize: 24, fontWeight: 700, lineHeight: "32px" };
const metricSecondaryValueSx = { color: "#2E3341", fontSize: 14, fontWeight: 500, lineHeight: "20px", pb: 0.25 };
const metricSubSx = { color: "#6F7279", fontSize: 13, fontWeight: 500, lineHeight: "20px" };
const workbenchSx = { display: "grid", gridTemplateColumns: { xs: "1fr", lg: "384px minmax(0, 1fr)" }, gap: 1.5, alignItems: "start" };
const filterPanelSx = { ...panelSx, p: { xs: 1.5, md: 1.7 }, position: { lg: "sticky" }, top: 16 };
const resultPanelSx = { ...panelSx, overflow: "hidden", minWidth: 0 };
const pressReleaseWorkbenchSx = {
  ...workbenchSx,
  gridTemplateColumns: { xs: "1fr", lg: "383px minmax(0, 1fr)" },
  gap: { xs: 1.5, lg: 2 },
};
const pressReleaseFilterPanelSx = {
  ...filterPanelSx,
  p: 3,
  minHeight: 446,
  boxSizing: "border-box",
  border: "0",
  borderRadius: "12px",
  bgcolor: "#fff",
  boxShadow: "0px 6px 14px rgba(68, 100, 178, 0.08)",
};
const pressReleaseFilterStackSx = { gap: 3 };
const pressReleaseFilterIconSx = { width: 16, height: 16, objectFit: "contain", display: "block", flexShrink: 0 };
const pressReleaseFilterFieldSx = {
  "& .MuiInputBase-root": {
    height: 48,
    borderRadius: "8px",
    bgcolor: "#fff",
    color: "#2E3341",
    fontSize: 16,
    fontWeight: 400,
  },
  "& .MuiInputBase-input": { py: 1, letterSpacing: 0 },
  "& .MuiInputBase-input::placeholder": { color: "#6F7279", opacity: 1 },
  "& .MuiInputLabel-root": { fontSize: 13, color: "#6F7279" },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#DDE0EA" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#C8CEDA" },
};
const pressReleaseFilterSectionSx = { display: "grid", gap: 0.5 };
const pressReleaseDateControlSx = { display: "grid", gap: 0 };
const pressReleaseRangeOptionsSx = {
  display: "flex",
  alignItems: "center",
  columnGap: 3,
  rowGap: 0,
  minHeight: 40,
  flexWrap: "wrap",
  alignContent: "flex-start",
};
const pressReleaseResultPanelSx = {
  ...resultPanelSx,
  border: "0",
  borderRadius: "12px",
  bgcolor: "#fff",
  boxShadow: "0px 6px 14px rgba(68, 100, 178, 0.08)",
};
const panelTopSx = { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1, px: { xs: 1.5, md: 2 }, py: 1.35, bgcolor: "#fff" };
const pressReleasePanelTopSx = {
  ...panelTopSx,
  px: 3,
  pt: 3,
  pb: 0,
  minHeight: 46,
  height: { lg: 46 },
  boxSizing: "border-box",
  alignItems: "flex-start",
  borderBottom: "0",
};
const panelTitleSx = { color: "#172033", fontSize: 14, fontWeight: 800 };
const pressReleasePanelTitleSx = { color: "#000", fontSize: 16, fontWeight: 600, lineHeight: "22px" };
const sectionTitleSx = { color: "#000", fontSize: 16, fontWeight: 600, lineHeight: "22px" };
const filterLabelSx = { color: "#252d3d", fontSize: 11, fontWeight: 800, mb: 0.65 };
const pressReleaseFilterLabelSx = { color: "#2E3341", fontSize: 13, fontWeight: 600, lineHeight: "20px", mb: 0 };
const filterFieldSx = { "& .MuiInputBase-root": { borderRadius: "8px", fontSize: 13, bgcolor: "#fff" }, "& .MuiInputLabel-root": { fontSize: 12 } };
const candidateQueryInputSx = {
  "& .MuiOutlinedInput-root": {
    height: 40,
    borderRadius: "8px",
    bgcolor: "#fff",
    color: "#2E3341",
    fontSize: 16,
    fontWeight: 400,
    lineHeight: "24px",
    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#DDE0EA" },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#C8CEDA" },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#2E3341", borderWidth: 1 },
  },
  "& .MuiInputBase-input": {
    height: 24,
    boxSizing: "border-box",
    px: 2,
    py: 1,
    color: "#2E3341",
    fontSize: 16,
    fontWeight: 400,
    lineHeight: "24px",
    letterSpacing: 0,
  },
  "& .MuiInputBase-input::placeholder": { color: "#A3A3A3", opacity: 1 },
};
const filterButtonSx = { minWidth: 0, px: 0.95, py: 0.35, borderRadius: "7px", color: "#4a5568", bgcolor: "#fff", border: "1px solid #d8dee8", fontSize: 12, fontWeight: 700, "&:hover": { borderColor: "#cbd5e1", bgcolor: "#f8fafc" } };
const filterActiveButtonSx = { ...filterButtonSx, color: "#3f66e9", bgcolor: "#eef3ff", borderColor: "#9bb4ff" };
const pressReleaseFilterPillButtonSx = {
  minWidth: 0,
  height: 32,
  px: 1.5,
  py: 0,
  borderRadius: "8px",
  color: "#2E3341",
  bgcolor: "#fff",
  border: "1px solid #DDE0EA",
  fontSize: 14,
  fontWeight: 500,
  lineHeight: "14px",
  letterSpacing: 0,
  boxShadow: "none",
  "&:hover": { borderColor: "#C8CEDA", bgcolor: "#fff", boxShadow: "none" },
};
const pressReleaseFilterPillActiveButtonSx = {
  ...pressReleaseFilterPillButtonSx,
  color: "#2E3341",
  bgcolor: "#fff",
  borderColor: "#2E3341",
  fontWeight: 600,
  "&:hover": { borderColor: "#2E3341", bgcolor: "#fff", boxShadow: "none" },
};
const keywordFilterChipListSx = {
  display: "flex",
  flexDirection: "row",
  flexWrap: "wrap",
  justifyContent: "flex-start",
  alignItems: "flex-start",
  alignContent: "flex-start",
  columnGap: 0.5,
  rowGap: 0.5,
  width: "100%",
};
const legacyKeywordFilterChipListSx = {
  display: "flex",
  flexDirection: "row",
  flexWrap: "wrap",
  justifyContent: "flex-start",
  alignItems: "flex-start",
  alignContent: "flex-start",
  columnGap: 0.45,
  rowGap: 0.45,
};
const radioFilterButtonSx = (active: boolean) => ({
  ...filterButtonSx,
  gap: 0.55,
  px: 0.15,
  py: 0.25,
  border: "0",
  bgcolor: "transparent",
  color: active ? "#172033" : "#334155",
  boxShadow: "none",
  "&:hover": { bgcolor: "transparent", color: "#172033" },
  "&::before": {
    content: '""',
    width: 14,
    height: 14,
    borderRadius: "50%",
    boxSizing: "border-box",
    border: active ? "4px solid #5b7cfa" : "1px solid #d9dee7",
    bgcolor: active ? "#fff" : "#e5e7eb",
    flexShrink: 0,
  },
});
const pressReleaseRadioFilterButtonSx = (active: boolean) => ({
  minWidth: 0,
  height: 40,
  px: 0,
  py: 0,
  gap: 1,
  border: "0",
  borderRadius: 0,
  bgcolor: "transparent",
  color: "#2E3341",
  boxShadow: "none",
  fontSize: 16,
  fontWeight: 500,
  lineHeight: "24px",
  letterSpacing: 0,
  whiteSpace: "nowrap",
  "&:hover": { bgcolor: "transparent", color: "#2E3341" },
  "&::before": {
    content: '""',
    width: 16,
    height: 16,
    borderRadius: "50%",
    boxSizing: "border-box",
    border: active ? "4px solid #5D88F1" : "0",
    bgcolor: active ? "#fff" : "#E5E5E5",
    boxShadow: active ? "0 0 0 1px #5D88F1 inset" : "none",
    flexShrink: 0,
  },
});
const viewButtonSx = { ...filterButtonSx, justifyContent: "flex-start" };
const viewActiveButtonSx = { ...filterActiveButtonSx, justifyContent: "flex-start" };
const pressReleaseAdvancedButtonSx = {
  minWidth: 0,
  width: "100%",
  height: 40,
  px: 1.5,
  borderRadius: "8px",
  border: "0",
  bgcolor: "#F0F4FF",
  color: "#2E3341",
  fontSize: 14,
  fontWeight: 600,
  lineHeight: "14px",
  letterSpacing: 0,
  boxShadow: "none",
  "&:hover": { bgcolor: "#E4EBFF", boxShadow: "none" },
};
const viewTabBarSx = { display: "flex", gap: 2.6, px: { xs: 1.5, md: 2 }, pt: 1.15, borderBottom: "1px solid #d8dee8", bgcolor: "#fff", overflowX: "auto" };
const pressReleaseViewTabBarSx = {
  display: "flex",
  alignItems: "center",
  gap: 0,
  minHeight: 72,
  height: { lg: 72 },
  boxSizing: "border-box",
  px: 3,
  pt: 3,
  pb: 1,
  borderBottom: "0",
  bgcolor: "#fff",
  overflowX: "auto",
};
const viewTabButtonSx = {
  display: "inline-flex",
  alignItems: "center",
  gap: 0.4,
  minWidth: "fit-content",
  px: 0.15,
  pb: 1.05,
  color: "#98a2b3",
  bgcolor: "transparent",
  border: 0,
  borderBottom: "2px solid transparent",
  fontSize: 14,
  fontWeight: 800,
  whiteSpace: "nowrap",
  cursor: "pointer",
  appearance: "none",
  fontFamily: "inherit",
  "&:hover": { color: "#172033" },
};
const viewTabActiveSx = { ...viewTabButtonSx, color: "#252d3d", borderBottomColor: "#252d3d" };
const viewTabCountSx = { color: "#5b7cfa", fontSize: 13, fontWeight: 900 };
const pressReleaseViewTabButtonBaseSx = {
  ...viewTabButtonSx,
  height: 40,
  px: 3,
  pb: 0,
  alignItems: "flex-start",
  borderBottom: "1px solid #DDE0EA",
  color: "#2E3341",
  opacity: 0.5,
  fontSize: 16,
  fontWeight: 600,
  lineHeight: "16px",
  letterSpacing: 0,
};
const getPressReleaseViewTabSx = (mode: ViewMode, active: boolean) => ({
  ...pressReleaseViewTabButtonBaseSx,
  width: mode === "settings" ? 76 : 132,
  minWidth: mode === "settings" ? 76 : 132,
  ...(active ? {
    borderBottomColor: "#2E3341",
    opacity: 1,
  } : {}),
});
const pressReleaseViewTabCountSx = { color: "#5D88F1", fontSize: 16, fontWeight: 600, lineHeight: "16px", letterSpacing: 0 };
const articleRevisionPanelHeaderSx = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) auto" },
  gap: 2,
  alignItems: "end",
  px: 3,
  pt: 3,
  pb: 0,
  bgcolor: "#fff",
  boxSizing: "border-box",
};
const articleRevisionHeaderTextSx = { display: "grid", gap: 0, minWidth: 0 };
const articleRevisionHeaderDescriptionSx = { color: "#6F7279", fontSize: 13, fontWeight: 500, lineHeight: "20px" };
const articleRevisionHeaderActionsSx = {
  gap: 0.5,
  justifyContent: { xs: "flex-start", md: "flex-end" },
  alignItems: "center",
  flexWrap: "wrap",
  minHeight: 40,
};
const articleRevisionHeaderButtonSx = {
  minWidth: 83,
  height: 40,
  px: 2,
  py: 0,
  borderRadius: "8px",
  bgcolor: "#fff",
  color: "#2E3341",
  border: "1px solid #DDE0EA",
  fontSize: 14,
  fontWeight: 600,
  lineHeight: "14px",
  letterSpacing: 0,
  boxShadow: "none",
  "&:hover": { borderColor: "#C8CEDA", bgcolor: "#fff", boxShadow: "none" },
  "&.Mui-disabled": { borderColor: "#DDE0EA", bgcolor: "#fff", color: "#A3A8B3" },
};
const articleRevisionHeaderStartButtonSx = {
  ...articleRevisionHeaderButtonSx,
  minWidth: 110,
  borderColor: "#2E3341",
  "&:hover": { borderColor: "#2E3341", bgcolor: "#fff", boxShadow: "none" },
  "&.Mui-disabled": { borderColor: "#C5C8D0", bgcolor: "#fff", color: "#A3A8B3" },
};
const batchActionBarSx = (pressRelease = false) => ({
  mx: pressRelease ? 3 : 1,
  mt: pressRelease ? 2 : 1,
  px: 1.15,
  py: pressRelease ? 1.2 : 0.8,
  minHeight: pressRelease ? 56 : "auto",
  boxSizing: "border-box",
  display: "grid",
  gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) auto" },
  gap: 0.8,
  alignItems: "center",
  border: "1px solid #d8dee8",
  borderRadius: "8px",
  bgcolor: "#fff",
});
const batchActionTextSx = { color: "#475569", fontSize: 12, fontWeight: 700 };
const denseListSx = { p: 1, display: "grid", gap: 0.6, bgcolor: "#fff" };
const pressReleaseDenseListSx = { px: 3, pb: 3, pt: 2, display: "grid", gap: 1, bgcolor: "#fff" };
type RevisionTrackingStateTone = "active" | "expired" | "stopped" | "idle";
const articleRevisionTrackingStateIconSrc: Record<Exclude<RevisionTrackingStateTone, "idle">, string> = {
  active: monitoringDetailIcons.revisionTrackingActive,
  expired: monitoringDetailIcons.revisionTrackingExpired,
  stopped: monitoringDetailIcons.revisionTrackingStopped,
};

const articleRowSx = (
  variant: ArticleDenseRowVariant = "default",
  showRevisionDiff = false,
  showTone = false,
  showKeywordMatch = false,
): SxProps<Theme> => {
  const defaultArticleGridColumns = [
    showRevisionDiff && "60px",
    showTone && "80px",
    showKeywordMatch && "80px",
    "minmax(0, 1fr)",
    showRevisionDiff && "120px",
    "160px",
  ].filter(Boolean).join(" ");

  return variant === "pressRelated"
    ? {
    display: "grid",
    gridTemplateColumns: { xs: "1fr", lg: showRevisionDiff ? "auto minmax(0, 1fr) auto auto" : "auto minmax(0, 1fr) auto" },
    gap: { xs: 1, lg: 2 },
    alignItems: "center",
    minWidth: 0,
    boxSizing: "border-box",
    px: 2,
    py: 2,
    border: "1px solid #DDE0EA",
    borderRadius: "8px",
    bgcolor: "#fff",
    boxShadow: "none",
    transition: "border-color .15s ease, background-color .15s ease",
    "&:hover": { borderColor: "#C8CEDA", bgcolor: "#fff" },
    }
    : {
    display: "grid",
    gridTemplateColumns: {
      xs: "1fr",
      lg: defaultArticleGridColumns,
    },
    gap: { xs: 1, lg: 0 },
    alignItems: "center",
    minWidth: 0,
    boxSizing: "border-box",
    px: { xs: 2, lg: 0 },
    py: { xs: 2, lg: 0 },
    border: "1px solid #DDE0EA",
    borderRadius: "8px",
    bgcolor: "#FAFAFA",
    boxShadow: "none",
    transition: "border-color .15s ease, box-shadow .15s ease, background-color .15s ease",
      "&:hover": { borderColor: "#C8CEDA", boxShadow: "0px 6px 14px rgba(68, 100, 178, 0.08)", bgcolor: "#fff" },
    };
};
const articleSignalSx = (variant: ArticleDenseRowVariant = "default", showRevisionDiff = false): SxProps<Theme> => variant === "pressRelated"
  ? { display: "flex", gap: 1, flexWrap: { xs: "wrap", lg: showRevisionDiff ? "nowrap" : "wrap" }, alignItems: "center", minWidth: 0, maxWidth: "none" }
  : showRevisionDiff ? {
    display: "flex",
    gap: 3,
    flexWrap: { xs: "wrap", lg: "nowrap" },
    alignItems: "center",
    justifyContent: "flex-start",
    minWidth: 0,
    maxWidth: "none",
    "& > *": {
      flexShrink: 0,
    },
  } : { display: "flex", gap: 3, flexWrap: { xs: "wrap", lg: "nowrap" }, alignItems: "center", minWidth: 0, maxWidth: "none" };
const revisionSelectCheckboxSx = {
  width: 24,
  height: 24,
  p: 0,
  flex: "0 0 auto",
  color: "transparent",
  "&:hover": { bgcolor: "transparent" },
  "&.Mui-checked": { color: "transparent" },
  "&.Mui-disabled": { opacity: 0.45 },
};
const articleCheckboxColumnSx = { display: "flex", justifyContent: { xs: "flex-start", lg: "center" }, alignItems: "center", alignSelf: { lg: "stretch" }, minWidth: 0 };
const articleToneColumnSx = { display: "flex", justifyContent: { xs: "flex-start", lg: "center" }, alignItems: "center", alignSelf: { lg: "stretch" }, minWidth: 0 };
const articleKeywordMatchColumnSx = { display: "flex", justifyContent: { xs: "flex-start", lg: "center" }, alignItems: "center", alignSelf: { lg: "stretch" }, minWidth: 0 };
const articleTitleSx = { flex: "1 1 auto", minWidth: 0, color: "#000", fontSize: 16, fontWeight: 500, lineHeight: "24px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };
const articleTitleRowSx = {
  display: "flex",
  alignItems: "center",
  gap: 0.5,
  width: "100%",
  minWidth: 0,
  height: 24,
};
const articleRevisionDetectedBadgeSx = {
  height: 24,
  px: 0.75,
  border: "1px solid #5D88F1",
  borderRadius: "16px",
  color: "#5D88F1",
  fontFamily: "Pretendard",
  fontSize: 14,
  fontWeight: 500,
  lineHeight: "22px",
  whiteSpace: "nowrap",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  flex: "0 0 auto",
  boxSizing: "border-box",
};
const articleTitleLinkButtonSx = {
  width: 24,
  minWidth: 24,
  height: 24,
  p: 0,
  color: "#9AA4B4",
  flex: "0 0 24px",
  borderRadius: "4px",
  "&:hover": { bgcolor: "#F5F6FA" },
};
const articleTitleLinkIconSx = { width: 16, height: 16, color: "#9AA4B4" };
const articleMetaSx = { mt: 0.2, color: "#6F7279", fontSize: 13, fontWeight: 500, lineHeight: "20px" };
const articleBodySx = { mt: 1, color: "#000", fontSize: 14, fontWeight: 400, lineHeight: "22px", display: "-webkit-box", WebkitLineClamp: 1, WebkitBoxOrient: "vertical", overflow: "hidden" };
const pressRelatedArticleTitleSx = { minWidth: 0, color: "#000", fontSize: 14, fontWeight: 500, lineHeight: "22px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };
const pressRelatedArticleMetaSx = { mt: 0, color: "#6F7279", fontSize: 13, fontWeight: 500, lineHeight: "20px", display: "-webkit-box", WebkitLineClamp: 1, WebkitBoxOrient: "vertical", overflow: "hidden" };
const articleContentSx = (variant: ArticleDenseRowVariant): SxProps<Theme> => ({
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "stretch",
  height: "auto",
  px: variant === "default" ? { xs: 0, lg: 2 } : 0,
  py: variant === "default" ? { xs: 0, lg: 2 } : 0,
  boxSizing: "border-box",
});
const articleActionRowSx = (showRevisionDiff = false, variant: ArticleDenseRowVariant = "default"): SxProps<Theme> => ({
  flexWrap: "wrap",
  rowGap: 0.5,
  justifyContent: variant === "default" ? { xs: "flex-start", lg: "center" } : showRevisionDiff ? { xs: "flex-start", lg: "flex-end" } : "flex-end",
  alignSelf: { xs: "center", lg: variant === "default" ? "stretch" : "center" },
  alignItems: "center",
  width: variant === "default" ? "100%" : showRevisionDiff ? { xs: "100%", lg: "auto" } : "auto",
  minWidth: 0,
  height: "auto",
  px: 0,
  py: 0,
  boxSizing: "border-box",
});
const articleRevisionTrackingStateSx = {
  width: { xs: "100%", lg: 120 },
  minWidth: { lg: 120 },
  height: { xs: "auto", lg: "100%" },
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  px: { xs: 0, lg: 2 },
  py: { xs: 0, lg: 2 },
  boxSizing: "border-box",
};
const articleRevisionTrackingStateLabelRowSx = (tone: RevisionTrackingStateTone): SxProps<Theme> => ({
  width: tone === "active" || tone === "idle" ? 40 : 64,
  height: tone === "idle" ? 22 : 42,
  px: 0,
  borderRadius: "16px",
  bgcolor: "transparent",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  gap: 0.5,
  boxSizing: "border-box",
});
const articleRevisionTrackingStateIconSx: SxProps<Theme> = {
  width: 16,
  height: 16,
  flex: "0 0 auto",
  display: "block",
  objectFit: "contain",
};
const articleRevisionTrackingStateLabelSx = (tone: RevisionTrackingStateTone): SxProps<Theme> => ({
  color: tone === "active" ? "#078923" : tone === "expired" ? "#F07000" : tone === "stopped" ? "#E32329" : "#6F7279",
  fontSize: 14,
  fontWeight: 500,
  lineHeight: "22px",
  letterSpacing: 0,
  whiteSpace: "nowrap",
});
const revisionPdfListSx = { display: "grid", gap: 0, mt: 0.55 };
const revisionPdfDialogListSx = { ...revisionPdfListSx, mt: 3 };
const revisionPdfItemSx = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 1,
  width: "100%",
  maxWidth: "100%",
  minHeight: 40,
  minWidth: 0,
  boxSizing: "border-box",
  px: 1,
  py: 1,
  border: "1px solid #DDE0EA",
  borderRadius: "8px",
  bgcolor: "#fff",
  flexWrap: "wrap",
};
const revisionPdfBadgePalette: Record<RevisionPdfBadgeTone, { bgcolor: string; color: string }> = {
  baselineReady: { bgcolor: "#DCDFE5", color: "#2E3341" },
  ready: { bgcolor: "#ECF8EF", color: "#078923" },
  failed: { bgcolor: "#FCEEEE", color: "#B91C1C" },
  pending: { bgcolor: "#FFF3CD", color: "#C2410C" },
  error: { bgcolor: "#FCEEEE", color: "#B91C1C" },
};
const revisionPdfStatusBadgeGroupSx: SxProps<Theme> = {
  width: "fit-content",
  maxWidth: "100%",
  minWidth: 0,
  minHeight: 24,
  display: "flex",
  alignItems: "center",
  gap: 1,
  flexWrap: "wrap",
  flex: "0 0 auto",
  ml: "auto",
};
const revisionPdfStatusBadgeSx = (tone: RevisionPdfBadgeTone) => ({
  ...revisionPdfBadgePalette[tone],
  width: tone === "pending" ? "fit-content" : 114,
  minWidth: tone === "pending" ? 0 : 114,
  height: 24,
  px: 1,
  borderRadius: "16px",
  display: "inline-flex",
  justifyContent: "center",
  alignItems: "center",
  gap: "10px",
  boxSizing: "border-box",
  whiteSpace: "nowrap",
});
const revisionPdfStatusBadgeButtonSx = (tone: RevisionPdfBadgeTone): SxProps<Theme> => ({
  ...revisionPdfStatusBadgeSx(tone),
  border: 0,
  boxShadow: "none",
  textTransform: "none",
  "&:hover": { ...revisionPdfBadgePalette[tone], boxShadow: "none" },
  "&.Mui-disabled": { ...revisionPdfBadgePalette[tone], opacity: 1 },
});
const revisionPdfStatusBadgeTextSx = {
  color: "inherit",
  fontFamily: "Pretendard",
  fontSize: 13,
  fontWeight: 500,
  lineHeight: "13px",
  letterSpacing: 0,
  whiteSpace: "nowrap",
};
const revisionPdfStatusBadgeIconSx = { width: 16, height: 16, color: "inherit", flex: "0 0 auto" };
const revisionPdfStatusSummarySx: SxProps<Theme> = {
  minWidth: 0,
  display: "flex",
  alignItems: "center",
  gap: 1,
  flex: "1 1 auto",
  flexWrap: "wrap",
};
const revisionPdfMetaSx = {
  minWidth: 0,
  flex: "0 1 auto",
  color: "#6F7279",
  fontFamily: "Pretendard",
  fontSize: 13,
  fontWeight: 400,
  lineHeight: "20px",
  letterSpacing: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};
const revisionPdfInlineRefreshButtonSx = {
  minWidth: 0,
  height: 20,
  p: 0,
  borderRadius: 0,
  color: "#1670E8",
  fontFamily: "Pretendard",
  fontSize: 13,
  fontWeight: 500,
  lineHeight: "20px",
  letterSpacing: 0,
  textTransform: "none",
  whiteSpace: "nowrap",
  "&:hover": { bgcolor: "transparent", color: "#0F5FCC" },
  "&.Mui-disabled": { color: "#A3A8B3" },
};
const revisionPdfActionGroupSx = {
  flexWrap: "wrap",
  rowGap: 0.5,
  justifyContent: "flex-end",
  flex: "0 0 auto",
};
const revisionPdfAuxButtonSx = {
  minWidth: 69,
  height: 24,
  px: 1,
  py: 0,
  border: "1px solid #DDE0EA",
  borderRadius: "8px",
  bgcolor: "#fff",
  color: "#2E3341",
  fontSize: 13,
  fontWeight: 600,
  lineHeight: "13px",
  letterSpacing: 0,
  boxShadow: "none",
  "&:hover": { borderColor: "#C8CEDA", bgcolor: "#fff", boxShadow: "none" },
  "&.Mui-disabled": { borderColor: "#DDE0EA", bgcolor: "#fff", color: "#A3A8B3" },
};
const softChipSx = { height: 22, borderRadius: "20px", bgcolor: "#f1f5f9", color: "#475569", fontSize: 10.5, fontWeight: 600, "& .MuiChip-label": { px: 0.85 } };
const blueChipSx = { ...softChipSx, bgcolor: "#e0f2fe", color: "#0369a1" };
const dangerChipSx = { ...softChipSx, bgcolor: "#fee2e2", color: "#b91c1c" };
const toneChipSx = (tone: Tone) => ({
  minWidth: 49,
  height: 32,
  borderRadius: "16px",
  bgcolor: toneMeta[tone].bg,
  color: toneMeta[tone].color,
  fontSize: 14,
  fontWeight: 500,
  lineHeight: "14px",
  "& .MuiChip-label": { px: 1.5 },
});
const pressRelatedToneChipSx = (tone: Tone) => ({
  minWidth: 39,
  height: 24,
  borderRadius: "16px",
  bgcolor: toneMeta[tone].bg,
  color: toneMeta[tone].color,
  fontSize: 13,
  fontWeight: 500,
  lineHeight: "13px",
  "& .MuiChip-label": { px: 1 },
});
const articleCheckboxEmptyIconSx = {
  width: 24,
  height: 24,
  borderRadius: "4px",
  bgcolor: "#E5E5E5",
  display: "block",
};
const articleCheckboxCheckedIconSx = {
  ...articleCheckboxEmptyIconSx,
  bgcolor: "#5D88F1",
  position: "relative",
};
const articleCheckboxCheckMarkSx = {
  position: "absolute",
  left: 6,
  top: 6,
  width: 12,
  height: 7,
  borderLeft: "2px solid #fff",
  borderBottom: "2px solid #fff",
  transform: "rotate(-45deg)",
};
const keywordMatchGaugeSx = (_level?: KeywordMatchLevel | null) => ({
  width: 50,
  height: 37,
  display: "inline-flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  gap: 1,
  px: 1,
  py: 0,
  border: 0,
  bgcolor: "transparent",
  whiteSpace: "nowrap",
});
const keywordMatchGaugeLabelSx = { color: "#2E3341", fontSize: 13, fontWeight: 500, lineHeight: "13px" };
const keywordMatchGaugeValueSx = (level?: KeywordMatchLevel | null) => ({
  color: level === "HIGH" ? "#5D88F1" : level === "LOW" ? "#6F7279" : "#2E3341",
  fontSize: 16,
  fontWeight: 600,
  lineHeight: "16px",
});
const keywordArticleButtonIconSx = { width: 16, height: 16, fontSize: 16, color: "#9AA4B4" };
const pressRelatedArticleButtonIconSx = { width: 16, height: 16, fontSize: 16, color: "#9AA4B4" };
const keywordArticleButtonSx = {
  minWidth: 0,
  height: 32,
  px: 1.5,
  py: 0,
  borderRadius: "8px",
  color: "#2E3341",
  border: "1px solid #DDE0EA",
  bgcolor: "#fff",
  fontSize: 14,
  fontWeight: 600,
  lineHeight: "14px",
  letterSpacing: 0,
  whiteSpace: "nowrap",
  boxShadow: "none",
  "& .MuiButton-startIcon": { mr: 0.5, ml: 0 },
  "& .MuiButton-endIcon": { ml: 0.5, mr: 0 },
  "&:hover": { borderColor: "#C8CEDA", bgcolor: "#fff", boxShadow: "none" },
};
const pressRelatedArticleButtonSx = {
  ...keywordArticleButtonSx,
  minWidth: 68,
  height: 32,
  "& .MuiButton-startIcon": { mr: 0.5, ml: 0 },
};
const pressGroupSx = { border: "1px solid #DDE0EA", borderRadius: "8px", bgcolor: "#FAFAFA", overflow: "hidden", boxShadow: "none" };
const pressGroupHeaderSx = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) auto" },
  gap: { xs: 1, md: 3 },
  alignItems: "center",
  boxSizing: "border-box",
  p: 2,
  cursor: "pointer",
  bgcolor: "#FAFAFA",
  transition: "background-color .15s ease",
  "&:hover": { bgcolor: "#F6F8FC" },
};
const pressGroupCountChipSx = {
  minWidth: 79,
  height: 32,
  borderRadius: "16px",
  bgcolor: "#DCDFE5",
  color: "#2E3341",
  fontSize: 14,
  fontWeight: 500,
  lineHeight: "14px",
  "& .MuiChip-label": { px: 1.5 },
};
const pressGroupHeaderContentSx = { minWidth: 0, display: "flex", alignItems: "center", gap: 3 };
const pressGroupHeaderMainSx = { minWidth: 0, display: "grid", gap: 0.5, alignItems: "start" };
const pressGroupHeaderMetaSx = {
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  gap: 0.5,
  flexWrap: "wrap",
  minWidth: 0,
  justifySelf: { xs: "stretch", md: "end" },
  width: { xs: "100%", md: "auto" },
};
const pressTitleSx = { color: "#000", fontSize: 16, fontWeight: 500, lineHeight: "24px", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };
const pressGroupDateSx = { color: "#6F7279", fontSize: 13, fontWeight: 500, lineHeight: "20px" };
const pressKeywordChipSx = {
  ...softChipSx,
  width: "fit-content",
  maxWidth: "100%",
  height: 24,
  justifySelf: "start",
  bgcolor: "#FAFAFA",
  color: "#2E3341",
  border: "1px solid #2E3341",
  fontSize: 13,
  fontWeight: 500,
  lineHeight: "13px",
  "& .MuiChip-label": { px: 1, overflow: "hidden", textOverflow: "ellipsis" },
};
const pressCandidateButtonSx = {
  minWidth: 0,
  height: 32,
  px: 1.5,
  borderRadius: "8px",
  color: "#2E3341",
  border: "1px solid #DDE0EA",
  bgcolor: "#fff",
  fontSize: 14,
  fontWeight: 600,
  lineHeight: "14px",
  letterSpacing: 0,
  whiteSpace: "nowrap",
  boxShadow: "none",
  "&:hover": { borderColor: "#C8CEDA", bgcolor: "#F8FAFF", boxShadow: "none" },
};
const pressGroupBodySx = { px: 2, pt: 1, pb: 2, display: "grid", gap: 1, bgcolor: "#FAFAFA" };
const pressRelatedArticleGridSx = { display: "grid", gridTemplateColumns: "1fr", gap: 1, alignItems: "start" };
const toggleIconSx = (expanded: boolean, afterCandidateButton = false) => ({
  width: 12,
  height: 7,
  ml: afterCandidateButton ? 2.5 : 0,
  display: "block",
  flexShrink: 0,
  backgroundImage: `url(${monitoringDetailIcons.selectArrow})`,
  backgroundRepeat: "no-repeat",
  backgroundSize: "contain",
  backgroundPosition: "center",
  transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
  transition: "transform .2s ease",
});
const runHistoryPanelHeaderSx = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 1,
  px: 3,
  pt: 3,
  pb: 0,
  minHeight: 22,
  boxSizing: "border-box",
  bgcolor: "#fff",
};
const runHistoryPanelDescriptionSx = { color: "#6F7279", fontSize: 14, fontWeight: 500, lineHeight: "20px" };
const runHistoryPanelSx = { px: 3, pb: 3, pt: 2, bgcolor: "#fff" };
const runHistoryTableSx = {
  display: "grid",
  gridTemplateColumns: "1fr",
  width: "100%",
  minWidth: { xs: 1071, lg: 0 },
  overflowX: "auto",
};
const runHistoryHeaderSx = {
  display: "grid",
  gridTemplateColumns: "160px 320px 120px minmax(471px, 1fr)",
  gap: 0,
  alignItems: "center",
  minWidth: 1071,
  height: 40,
  bgcolor: "#F2F5FD",
  borderTop: "1px solid #2E3341",
  boxSizing: "border-box",
};
const runHeaderCellSx = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  height: 40,
  px: 2,
  color: "#2E3341",
  fontSize: 14,
  fontWeight: 500,
  lineHeight: "22px",
  letterSpacing: 0,
  boxSizing: "border-box",
};
const runRowSx = (status: RunStatus) => ({
  display: "grid",
  gridTemplateColumns: "160px 320px 120px minmax(471px, 1fr)",
  gap: 0,
  alignItems: "center",
  minWidth: 1071,
  minHeight: 56,
  bgcolor: status === "FAILED" ? "#fff" : "#fff",
});
const runCellBaseSx = {
  height: 56,
  minWidth: 0,
  boxSizing: "border-box",
  borderTop: "1px solid #F2F2F2",
  borderBottom: "1px solid #F2F2F2",
};
const runTimeSx = {
  ...runCellBaseSx,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  px: 2,
  color: "#2E3341",
  fontSize: 16,
  fontWeight: 400,
  lineHeight: "16px",
  textAlign: "center",
};
const runResultCellSx = {
  ...runCellBaseSx,
  display: "flex",
  alignItems: "center",
  gap: 1.5,
  px: 2,
  flexWrap: "nowrap",
};
const runStatusChipSx = (status: { bg: string; color: string }) => ({
  minWidth: 39,
  height: 24,
  borderRadius: "16px",
  bgcolor: status.bg,
  color: status.color,
  fontSize: 13,
  fontWeight: 500,
  lineHeight: "13px",
  "& .MuiChip-label": { px: 1 },
});
const runStatSx = {
  color: "#2E3341",
  fontSize: 16,
  fontWeight: 400,
  lineHeight: "16px",
  whiteSpace: "nowrap",
};
const runStatValueSx = { color: "#5D88F1", fontSize: 14, fontWeight: 600, lineHeight: "14px", ml: 0.5 };
const runNotificationStatusSx = (status: { color: string }) => ({
  ...runCellBaseSx,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  px: 2,
  color: status.color,
  fontSize: 16,
  fontWeight: 400,
  lineHeight: "16px",
});
const runMessageCellSx = {
  ...runCellBaseSx,
  display: "flex",
  alignItems: "center",
  gap: 0.75,
  px: 2,
  minHeight: 56,
  height: "auto",
};
const runMessageSx = {
  minWidth: 0,
  flex: "1 1 auto",
  color: "#2E3341",
  fontSize: 14,
  fontWeight: 400,
  lineHeight: "20px",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};
const runDetailButtonSx = { ...outlineButtonSx, flexShrink: 0, fontSize: 12, lineHeight: "16px", px: 0.9, py: 0.35 };
const runRetryButtonSx = { ...primaryButtonSx, flexShrink: 0, fontSize: 12, lineHeight: "16px", px: 0.9, py: 0.35, "&.Mui-disabled": { bgcolor: "#98a2b3", color: "#fff" } };
const runNotificationDetailSx = { minWidth: 1071, display: "grid", gap: 0.75, px: 2, py: 1.25, borderBottom: "1px solid #F2F2F2", bgcolor: "#FAFAFA" };
const runNotificationSummarySx = { color: "#2E3341", fontSize: 13, fontWeight: 600, lineHeight: "20px" };
const runNotificationInProgressSx = { color: "#5D88F1", fontSize: 13, fontWeight: 500, lineHeight: "20px" };
const runNotificationRecipientEmptySx = { color: "#6F7279", fontSize: 13, fontWeight: 400, lineHeight: "20px" };
const runNotificationRecipientListSx = { display: "grid", gap: 0.5, m: 0, p: 0, listStyle: "none" };
const runNotificationRecipientItemSx = { display: "grid", gap: 0.25, p: 0.75, border: "1px solid #E5E7EB", borderRadius: "6px", bgcolor: "#fff" };
const runNotificationRecipientTextSx = { color: "#2E3341", fontSize: 13, fontWeight: 500, lineHeight: "20px" };
const runNotificationRecipientErrorSx = { color: "#9F1239", fontSize: 13, fontWeight: 400, lineHeight: "20px" };
const settingsContentSx = {
  display: "grid",
  gap: 5,
  alignContent: "space-between",
  minHeight: 524,
  px: 3,
  py: 3,
  boxSizing: "border-box",
  bgcolor: "#fff",
};
const settingsPolicySectionSx = { display: "grid", gap: 2, minWidth: 0 };
const settingsPanelHeaderSx = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 1,
  minHeight: 22,
  width: "100%",
  bgcolor: "#fff",
};
const settingsPanelDescriptionSx = { color: "#6F7279", fontSize: 14, fontWeight: 500, lineHeight: "20px" };
const settingsPolicyStackSx = { display: "grid", gap: 1, minWidth: 0 };
const settingsTermRowSx = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
  gap: 1,
  minWidth: 0,
};
const settingsDetailRowSx = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
  gap: 1,
  minWidth: 0,
  alignItems: "stretch",
};
const settingsTermCardSx = {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: 0.5,
  minHeight: 88,
  p: 2,
  boxSizing: "border-box",
  border: "1px solid #DDE0EA",
  borderRadius: "8px",
  bgcolor: "#FAFAFA",
};
const settingsTermLabelSx = { color: "#2E3341", fontSize: 13, fontWeight: 600, lineHeight: "20px" };
const settingsChipListSx = {
  flexWrap: "wrap",
  rowGap: 0.5,
  alignItems: "flex-start",
  alignContent: "flex-start",
  width: "100%",
  minWidth: 0,
};
const settingsChipSx = {
  maxWidth: "100%",
  height: 32,
  borderRadius: "16px",
  bgcolor: "#DCDFE5",
  color: "#2E3341",
  fontSize: 14,
  fontWeight: 500,
  lineHeight: "14px",
  "& .MuiChip-label": { px: 1.5, overflow: "hidden", textOverflow: "ellipsis" },
};
const settingsEmptyTextSx = { color: "#6F7279", fontSize: 14, fontWeight: 500, lineHeight: "32px" };
const settingsRowsCardSx = {
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  minHeight: 262,
  p: 2,
  boxSizing: "border-box",
  border: "1px solid #DDE0EA",
  borderRadius: "8px",
  bgcolor: "#FAFAFA",
};
const settingsPolicyRowSx = {
  minHeight: 46,
  px: 1.5,
  py: 0,
  alignItems: "center",
  borderBottom: "1px solid #DDE0EA",
  boxSizing: "border-box",
  "&:last-of-type": { borderBottom: 0 },
};
const settingsPolicyLabelSx = { color: "#2E3341", fontSize: 14, fontWeight: 600, lineHeight: "22px", whiteSpace: "nowrap" };
const settingsGridSx = { display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1, px: 3, pb: 3, pt: 2, alignItems: "stretch" };
const policyBoxSx = { p: 1.1, border: "1px solid #d8dee8", borderRadius: "7px", bgcolor: "#fff", minHeight: 88 };
const searchTermExcludePolicyBoxSx = { ...settingsTermCardSx };
const searchTermExcludePolicyRowSx = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "120px minmax(0, 1fr)" },
  gap: 1,
  alignItems: "center",
  minHeight: 32,
  width: "100%",
  minWidth: 0,
};
const searchTermExcludePolicyTermSx = { color: "#2E3341", fontSize: 14, fontWeight: 600, lineHeight: "22px", minWidth: 0 };
const searchTermExcludePolicyChipListSx = { ...settingsChipListSx, rowGap: 0.5, minWidth: 0 };
const sentimentPolicyBoxSx = {
  display: "grid",
  gap: 0.75,
  mt: 1.5,
  pt: 1.5,
  borderTop: "1px solid #DDE0EA",
};
const settingsSentimentHeaderSx = { flexWrap: "wrap", rowGap: 0.5, minHeight: 32 };
const sentimentPolicyDescriptionSx = { color: "#6F7279", fontSize: 14, fontWeight: 400, lineHeight: "22px" };
const sentimentKeywordGridSx = { display: "grid", gridTemplateColumns: "1fr", gap: 1, mt: 0.25 };
const sentimentKeywordGroupSx = { minWidth: 0, display: "grid", gap: 0.5 };
const settingsSentimentKeywordListSx = { ...settingsChipListSx, mt: 0 };
const sentimentNegativeChipSx = {
  ...settingsChipSx,
  bgcolor: "#FFF2F2",
  color: "#E32329",
};
const sentimentStatusChipSx = (status: SentimentKeywordDisplayState["status"]) => {
  if (status === "CUSTOM") return { ...settingsChipSx, bgcolor: "#F0F4FF", color: "#5D88F1" };
  if (status === "GLOBAL") return { ...settingsChipSx, bgcolor: "#ECF8EF", color: "#078923" };
  return settingsChipSx;
};
const policyValueSx = { color: "#2E3341", fontSize: 14, fontWeight: 400, lineHeight: "22px", textAlign: "right", overflowWrap: "anywhere" };
const settingsDangerRowSx = {
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  minHeight: 40,
  width: "100%",
};
const settingsDeleteButtonSx = {
  minWidth: 142,
  width: 142,
  height: 40,
  px: 2,
  py: 0,
  borderRadius: "8px",
  bgcolor: "#E32329",
  color: "#fff",
  fontSize: 14,
  fontWeight: 600,
  lineHeight: "14px",
  gap: 0.5,
  letterSpacing: 0,
  whiteSpace: "nowrap",
  boxShadow: "none",
  "&:hover": { bgcolor: "#C91F24", boxShadow: "none" },
  "&.Mui-disabled": { bgcolor: "#F0A1A3", color: "#fff" },
};
const dangerZoneSx = { gridColumn: { md: "span 2" }, p: 0, display: "grid", gap: 0.5, justifyItems: "end", bgcolor: "transparent" };
const dangerButtonSx = { ...outlineButtonSx, width: "fit-content", borderColor: "#dc2626", bgcolor: "#dc2626", color: "#fff", px: 1.35, py: 0.55, fontWeight: 800, "&:hover": { borderColor: "#b91c1c", bgcolor: "#b91c1c" }, "&.Mui-disabled": { borderColor: "#fca5a5", bgcolor: "#fca5a5", color: "#fff" } };
const emptyStateSx = { p: 2, color: "#94a3b8", fontSize: 13, textAlign: "center", border: "1px dashed #cbd5e1", borderRadius: "8px", bgcolor: "#fff" };
const errorStateSx = { ...emptyStateSx, color: "#b91c1c", borderColor: "#fecaca", bgcolor: "#fef2f2" };
const errorTextSx = { color: "#b91c1c", fontSize: 12, lineHeight: 1.5 };
const deleteDialogBackdropSx = { bgcolor: "rgba(0, 0, 0, 0.4)" };
const deleteDialogPaperSx = {
  width: 400,
  height: "auto",
  maxWidth: "calc(100% - 32px)",
  m: 0,
  borderRadius: "24px",
  overflow: "hidden",
  bgcolor: "#fff",
  boxShadow: "0px 6px 14px rgba(68, 100, 178, 0.08)",
};
const deleteDialogTitleSx = {
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  gap: 0.5,
  width: 400,
  maxWidth: "100%",
  minHeight: 134,
  p: 5,
  boxSizing: "border-box",
  textAlign: "center",
};
const deleteDialogHeadingSx = { color: "#000", fontSize: 20, fontWeight: 600, lineHeight: "26px" };
const deleteDialogMessageSx = { color: "#2E3341", fontSize: 16, fontWeight: 400, lineHeight: "24px" };
const deleteDialogContentSx = { px: 5, pt: 0, pb: 1.5, textAlign: "center" };
const deleteDialogActionsSx = {
  justifyContent: "center",
  alignItems: "center",
  gap: 1,
  width: 400,
  maxWidth: "100%",
  minHeight: 72,
  px: 5,
  pt: 0,
  pb: 3,
  boxSizing: "border-box",
};
const deleteCancelButtonSx = {
  minWidth: 80,
  width: 80,
  height: 40,
  px: 2,
  py: 0,
  gap: 0.5,
  borderRadius: "8px",
  bgcolor: "#fff",
  border: "1px solid #DDE0EA",
  color: "#2E3341",
  fontSize: 14,
  fontWeight: 600,
  lineHeight: "14px",
  letterSpacing: 0,
  boxShadow: "none",
  whiteSpace: "nowrap",
  "&:hover": { borderColor: "#C8CEDA", bgcolor: "#fff", boxShadow: "none" },
};
const deleteConfirmButtonSx = {
  minWidth: 80,
  width: 80,
  height: 40,
  px: 2,
  py: 0,
  gap: 0.5,
  borderRadius: "8px",
  bgcolor: "#E32329",
  color: "#fff",
  fontSize: 14,
  fontWeight: 600,
  lineHeight: "14px",
  letterSpacing: 0,
  boxShadow: "none",
  whiteSpace: "nowrap",
  "&:hover": { bgcolor: "#C91F24", boxShadow: "none" },
  "&.Mui-disabled": { bgcolor: "#F0A1A3", color: "#fff" },
};
const articleRevisionStartDialogBackdropSx = { bgcolor: "rgba(0, 0, 0, 0.4)" };
const articleRevisionStartDialogPaperSx = {
  width: 560,
  minHeight: 412,
  maxWidth: "calc(100% - 32px)",
  m: 0,
  borderRadius: "24px",
  overflow: "hidden",
  bgcolor: "#fff",
  boxShadow: "0px 6px 14px rgba(68, 100, 178, 0.08)",
};
const articleRevisionStartDialogTitleSx = {
  width: 560,
  maxWidth: "100%",
  minHeight: 90,
  px: 5,
  pt: 5,
  pb: 0,
  boxSizing: "border-box",
  bgcolor: "#fff",
};
const articleRevisionStartDialogTitleBlockSx = {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: 0.5,
  width: 356,
  maxWidth: "100%",
};
const articleRevisionStartDialogHeadingSx = { color: "#000", fontSize: 20, fontWeight: 600, lineHeight: "26px" };
const articleRevisionStartDialogSubtitleSx = { color: "#6F7279", fontSize: 13, fontWeight: 400, lineHeight: "20px" };
const articleRevisionStartDialogContentSx = {
  width: 560,
  maxWidth: "100%",
  minHeight: 242,
  px: 5,
  py: 3,
  boxSizing: "border-box",
  bgcolor: "#fff",
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: 3,
  overflow: "visible",
};
const articleRevisionStartDialogTargetSx = {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: 0.5,
  width: "100%",
  minHeight: 70,
};
const articleRevisionStartDialogFieldLabelSx = { color: "#2E3341", fontSize: 13, fontWeight: 600, lineHeight: "20px" };
const articleRevisionStartDialogTargetTextSx = {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  width: "100%",
  minWidth: 0,
};
const articleRevisionStartDialogCountSx = { color: "#5D88F1", fontSize: 20, fontWeight: 600, lineHeight: "26px" };
const articleRevisionStartDialogArticleTitleSx = {
  color: "#000",
  fontSize: 16,
  fontWeight: 500,
  lineHeight: "24px",
  maxWidth: "100%",
  display: "-webkit-box",
  WebkitLineClamp: 1,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
};
const articleRevisionStartDialogTargetDescriptionSx = {
  color: "#6F7279",
  fontSize: 13,
  fontWeight: 400,
  lineHeight: "20px",
  maxWidth: "100%",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};
const articleRevisionStartDialogControlGroupSx = {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-end",
  gap: 1.5,
  width: "100%",
  minHeight: 100,
};
const articleRevisionStartDialogFieldSx = {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: 0.5,
  width: "100%",
};
const articleRevisionStartSelectSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    height: 48,
    borderRadius: "8px",
    bgcolor: "#fff",
    color: "#2E3341",
    fontSize: 16,
    fontWeight: 400,
    lineHeight: "24px",
    letterSpacing: "-0.015em",
    position: "relative",
    "&::after": {
      content: "\"\"",
      position: "absolute",
      right: 16,
      top: "50%",
      width: 12,
      height: 7,
      transform: "translateY(-50%)",
      backgroundImage: `url(${monitoringDetailIcons.selectArrow})`,
      backgroundRepeat: "no-repeat",
      backgroundSize: "contain",
      backgroundPosition: "center",
      pointerEvents: "none",
    },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#DDE0EA" },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#C8CEDA" },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#2E3341", borderWidth: 1 },
  },
  "& .MuiSelect-select": {
    height: 24,
    minHeight: "24px !important",
    py: "12px !important",
    pl: "16px !important",
    pr: "42px !important",
    color: "#2E3341",
    fontSize: 16,
    fontWeight: 400,
    lineHeight: "24px",
    letterSpacing: "-0.015em",
    boxSizing: "content-box",
  },
  "& .MuiSelect-icon": { display: "none" },
};
const articleRevisionStartNotifyRowSx = {
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  gap: 1,
  width: "100%",
  height: 24,
};
const articleRevisionStartNotifyLabelSx = { color: "#2E3341", fontSize: 14, fontWeight: 500, lineHeight: "22px", letterSpacing: "-0.015em" };
const articleRevisionStartSwitchSx = {
  width: 40,
  height: 24,
  p: 0,
  flex: "0 0 auto",
  "& .MuiSwitch-switchBase": {
    p: 0.5,
    color: "#fff",
    "&.Mui-checked": {
      color: "#fff",
      transform: "translateX(16px)",
      "& + .MuiSwitch-track": { bgcolor: "#5D88F1", opacity: 1 },
    },
  },
  "& .MuiSwitch-thumb": { width: 16, height: 16, bgcolor: "#fff", boxShadow: "none" },
  "& .MuiSwitch-track": { borderRadius: "20px", bgcolor: "#DCDFE5", opacity: 1 },
};
const articleRevisionStartDialogActionsSx = {
  justifyContent: "flex-end",
  alignItems: "flex-start",
  gap: 1,
  width: 560,
  maxWidth: "100%",
  minHeight: 80,
  px: 5,
  pt: 0,
  pb: 5,
  boxSizing: "border-box",
  bgcolor: "#fff",
};
const articleRevisionStartCancelButtonSx = {
  minWidth: 80,
  width: 80,
  height: 40,
  px: 2,
  py: 0,
  borderRadius: "8px",
  bgcolor: "#fff",
  border: "1px solid #DDE0EA",
  color: "#2E3341",
  fontSize: 14,
  fontWeight: 600,
  lineHeight: "14px",
  letterSpacing: "-0.015em",
  boxShadow: "none",
  "&:hover": { borderColor: "#C8CEDA", bgcolor: "#fff", boxShadow: "none" },
  "&.Mui-disabled": { borderColor: "#DDE0EA", bgcolor: "#fff", color: "#A3A8B3" },
};
const articleRevisionStartSubmitButtonSx = {
  minWidth: 80,
  width: 83,
  height: 40,
  px: 2,
  py: 0,
  borderRadius: "8px",
  bgcolor: "#2E3341",
  color: "#fff",
  fontSize: 14,
  fontWeight: 600,
  lineHeight: "14px",
  letterSpacing: "-0.015em",
  boxShadow: "none",
  "&:hover": { bgcolor: "#252A36", boxShadow: "none" },
  "&.Mui-disabled": { bgcolor: "#A3A8B3", color: "#fff" },
};
const articleRevisionStartSummarySx = { color: "#5D88F1", fontSize: 13, fontWeight: 500, lineHeight: "20px" };
const articleRevisionStartErrorSx = { color: "#E32329", fontSize: 13, fontWeight: 500, lineHeight: "20px" };
const revisionDiffDialogBackdropSx = { bgcolor: "rgba(0, 0, 0, 0.4)" };
const revisionDiffDialogPaperSx = {
  width: 1000,
  height: 800,
  maxWidth: "calc(100% - 32px)",
  maxHeight: "calc(100% - 32px)",
  m: 0,
  borderRadius: "24px",
  overflow: "hidden",
  bgcolor: "#fff",
  boxShadow: "0px 6px 14px rgba(68, 100, 178, 0.08)",
};
const revisionDiffDialogTitleSx = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: 1,
  width: 1000,
  maxWidth: "100%",
  minHeight: 66,
  px: 5,
  pt: 5,
  pb: 0,
  boxSizing: "border-box",
  bgcolor: "#fff",
};
const revisionDiffDialogHeadingSx = { color: "#000", fontSize: 20, fontWeight: 600, lineHeight: "26px" };
const revisionDiffDialogContentSx = {
  width: 1000,
  maxWidth: "100%",
  px: 5,
  pt: 0,
  pb: 5,
  boxSizing: "border-box",
  bgcolor: "#fff",
  overflowY: "auto",
  overflowX: "hidden",
  scrollbarColor: "#B8C0CC transparent",
  scrollbarWidth: "thin",
  "&::-webkit-scrollbar": { width: 6 },
  "&::-webkit-scrollbar-track": { background: "transparent", border: "none", boxShadow: "none" },
  "&::-webkit-scrollbar-thumb": { backgroundColor: "#B8C0CC", borderRadius: "999px", border: "none" },
  "&::-webkit-scrollbar-button": { display: "none", width: 0, height: 0 },
  "&::-webkit-scrollbar-corner": { background: "transparent" },
};
const revisionDiffContentStackSx = {
  display: "grid",
  gap: 3,
  width: "100%",
  alignItems: "start",
};
const revisionDiffHighlightRowSx = {
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  gap: 1,
  width: "100%",
  height: 24,
};
const revisionDiffHighlightLabelSx = {
  color: "#2E3341",
  fontSize: 14,
  fontWeight: 500,
  lineHeight: "22px",
  letterSpacing: 0,
};
const revisionDiffSwitchSx = {
  width: 40,
  height: 24,
  p: 0,
  flex: "0 0 auto",
  "& .MuiSwitch-switchBase": {
    p: 0.5,
    color: "#fff",
    "&.Mui-checked": {
      color: "#fff",
      transform: "translateX(16px)",
      "& + .MuiSwitch-track": {
        bgcolor: "#5D88F1",
        opacity: 1,
      },
    },
  },
  "& .MuiSwitch-thumb": {
    width: 16,
    height: 16,
    bgcolor: "#fff",
    boxShadow: "none",
  },
  "& .MuiSwitch-track": {
    borderRadius: "20px",
    bgcolor: "#DCDFE5",
    opacity: 1,
  },
};
const candidateDialogBackdropSx = { bgcolor: "rgba(0, 0, 0, 0.4)" };
const candidateDialogPaperSx = {
  width: 780,
  maxWidth: "calc(100% - 32px)",
  maxHeight: "calc(100% - 32px)",
  m: 0,
  borderRadius: "24px",
  overflow: "hidden",
  bgcolor: "#fff",
  boxShadow: "0px 6px 14px rgba(68, 100, 178, 0.08)",
};
const candidateDialogTitleSx = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: 1,
  minHeight: 90,
  px: 5,
  pt: 5,
  pb: 0,
  boxSizing: "border-box",
  bgcolor: "#fff",
};
const candidateDialogHeadingSx = { color: "#000", fontSize: 20, fontWeight: 600, lineHeight: "26px" };
const candidateDialogSubtitleSx = { mt: 0.5, color: "#6F7279", fontSize: 13, fontWeight: 400, lineHeight: "20px" };
const candidateCloseButtonSx = {
  width: 32,
  height: 32,
  mt: -1,
  mr: -1,
  color: "#6F7279",
  "&:hover": { bgcolor: "#F5F6FA" },
};
const candidateDialogContentSx = { width: 780, maxWidth: "100%", px: 5, pt: 3, pb: 5, boxSizing: "border-box", bgcolor: "#fff" };
const candidateDialogStackSx = { display: "flex", flexDirection: "column", gap: 2 };
const candidateSearchSummarySx = { display: "grid", gap: 2, p: 0, border: "none", borderRadius: 0, bgcolor: "transparent" };
const candidateBasisGridSx = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
  gap: 1,
  width: "100%",
};
const candidateBasisColumnSx = { minWidth: 0, display: "grid", gap: 0.5, alignItems: "start" };
const candidateBasisCardSx = (highlighted = false) => ({
  minWidth: 0,
  minHeight: 94,
  height: 94,
  boxSizing: "border-box",
  borderRadius: "8px",
  bgcolor: highlighted ? "#F0F4FF" : "#FAFAFA",
  p: 2,
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  justifyContent: "flex-start",
  gap: highlighted ? 1.25 : 1,
});
const candidateBasisLabelSx = { color: "#2E3341", fontSize: 13, fontWeight: 600, lineHeight: "20px" };
const candidateBasisActionWrapSx = { display: "flex", justifyContent: "flex-end", width: "100%", mt: "auto" };
const candidateBasisActionSx = {
  width: 72,
  minWidth: 72,
  height: 32,
  px: 1.5,
  py: 0,
  borderRadius: "8px",
  color: "#2E3341",
  borderColor: "#DDE0EA",
  bgcolor: "#fff",
  fontSize: 14,
  fontWeight: 600,
  lineHeight: "14px",
  letterSpacing: 0,
  boxShadow: "none",
  whiteSpace: "nowrap",
  "&:hover": { borderColor: "#C8CEDA", bgcolor: "#fff", boxShadow: "none" },
  "&.Mui-disabled": { borderColor: "#DDE0EA", color: "#A3A8B3", bgcolor: "#fff" },
};
const candidateBasisValueSx = {
  color: "#2E3341",
  fontSize: 14,
  fontWeight: 400,
  lineHeight: "20px",
  letterSpacing: 0,
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
};
const candidateQuerySectionSx = { display: "grid", gap: 0.5, width: "100%" };
const candidateQueryBoxSx = { display: "grid", gridTemplateColumns: { xs: "1fr", sm: "minmax(0, 1fr) 80px" }, gap: 1, alignItems: "start", width: "100%" };
const candidateSearchButtonSx = {
  ...primaryButtonSx,
  minWidth: 80,
  width: 80,
  height: 40,
  px: 2,
  py: 0,
  gap: 1,
  borderRadius: "8px",
  fontSize: 14,
  fontWeight: 600,
  lineHeight: "14px",
  letterSpacing: 0,
  bgcolor: "#2E3341",
  "&:hover": { bgcolor: "#252A36", boxShadow: "none" },
  "&.Mui-disabled": { bgcolor: "#A3A8B3", color: "#fff" },
};
const candidateResultListSx = {
  display: "grid",
  gap: 1,
  width: "100%",
  maxHeight: 400,
  overflowY: "auto",
  overflowX: "hidden",
  pr: 0,
  scrollbarColor: "#B8C0CC transparent",
  scrollbarWidth: "thin",
  "&::-webkit-scrollbar": { width: 6 },
  "&::-webkit-scrollbar-track": { background: "transparent", border: "none", boxShadow: "none" },
  "&::-webkit-scrollbar-thumb": { backgroundColor: "#B8C0CC", borderRadius: "999px", border: "none" },
  "&::-webkit-scrollbar-button": { display: "none", width: 0, height: 0 },
  "&::-webkit-scrollbar-corner": { background: "transparent" },
};
const candidateRowSx = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) auto" },
  gap: 2,
  alignItems: "center",
  minHeight: 82,
  boxSizing: "border-box",
  border: "1px solid #DDE0EA",
  borderRadius: "8px",
  bgcolor: "#fff",
  px: 2,
  py: 2,
  boxShadow: "none",
};
const candidateRowContentSx = { minWidth: 0, display: "grid", gap: 0.5 };
const candidateRowMetaSx = { minWidth: 0, height: 24, flexWrap: "wrap", rowGap: 0.5 };
const candidateSourceChipSx = {
  maxWidth: 120,
  height: 24,
  borderRadius: "16px",
  bgcolor: "#fff",
  border: "1px solid #2E3341",
  color: "#2E3341",
  fontSize: 13,
  fontWeight: 500,
  lineHeight: "13px",
  "& .MuiChip-label": { px: 1, overflow: "hidden", textOverflow: "ellipsis" },
};
const candidateMetaSx = { minWidth: 0, color: "#6F7279", fontSize: 13, fontWeight: 500, lineHeight: "20px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };
const candidateTitleSx = { color: "#000", fontSize: 14, fontWeight: 500, lineHeight: "22px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };
const candidateRowActionSx = { flexWrap: "nowrap", rowGap: 0.5, minWidth: { md: 159 }, alignItems: "center" };
const candidateOriginalIconSx = { width: 16, height: 16, color: "#9AA4B4" };
const candidateOriginalButtonSx = {
  ...outlineButtonSx,
  minWidth: 68,
  width: 68,
  height: 32,
  px: 1.5,
  py: 0,
  gap: 0.5,
  color: "#2E3341",
  border: "1px solid #DDE0EA",
  borderColor: "#DDE0EA",
  fontSize: 14,
  fontWeight: 600,
  lineHeight: "14px",
  letterSpacing: 0,
  "& .MuiButton-startIcon": { m: 0 },
  "&:hover": { border: "1px solid #DDE0EA", borderColor: "#DDE0EA", bgcolor: "#fff", boxShadow: "none" },
};
const candidateApproveButtonSx = {
  ...outlineButtonSx,
  minWidth: 114,
  width: 114,
  height: 32,
  px: 1.5,
  py: 0,
  color: "#2E3341",
  borderColor: "#2E3341",
  bgcolor: "#fff",
  fontSize: 14,
  fontWeight: 600,
  lineHeight: "14px",
  letterSpacing: 0,
  whiteSpace: "nowrap",
  "&:hover": { borderColor: "#2E3341", bgcolor: "#fff", boxShadow: "none" },
  "&.Mui-disabled": { borderColor: "#DDE0EA", color: "#A3A8B3", bgcolor: "#fff" },
};
const candidateApprovedButtonSx = {
  ...candidateApproveButtonSx,
  minWidth: 87,
  width: 87,
  color: "#6F7279",
  borderColor: "#DDE0EA",
  bgcolor: "#F2F2F2",
  "&:hover": { borderColor: "#DDE0EA", bgcolor: "#F2F2F2", boxShadow: "none" },
  "&.Mui-disabled": { borderColor: "#DDE0EA", color: "#6F7279", bgcolor: "#F2F2F2" },
};
const revisionBlockSx = {
  display: "grid",
  gap: 1,
  width: "100%",
  minWidth: 0,
};
const revisionDiffSectionHeadingSx = {
  display: "flex",
  alignItems: "center",
  gap: 1,
  minHeight: 24,
};
const revisionDiffSectionTitleSx = {
  color: "#000",
  fontSize: 16,
  fontWeight: 600,
  lineHeight: "22px",
};
const revisionDiffSectionBadgeSx = (label: string, changed: boolean) => {
  const isBody = label.includes("본문");
  return {
    width: "fit-content",
    minWidth: 0,
    height: 24,
    px: 1,
    flex: "0 0 auto",
    borderRadius: "16px",
    bgcolor: changed && isBody ? "#FFF3DB" : "#DCDFE5",
    color: changed && isBody ? "#F07000" : "#2E3341",
    display: "inline-flex",
    justifyContent: "center",
    alignItems: "center",
    boxSizing: "border-box",
    fontSize: 13,
    fontWeight: 500,
    lineHeight: "13px",
    whiteSpace: "nowrap",
  };
};
const revisionCompareGridSx = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
  gap: 3,
  width: "100%",
  alignItems: "stretch",
};
const revisionTextPanelSx = (longForm: boolean) => ({
  minWidth: 0,
  minHeight: longForm ? 0 : 84,
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: 1.5,
  p: longForm ? 3 : 2,
  borderRadius: "8px",
  bgcolor: "#FAFAFA",
});
const revisionTextPanelTitleSx = {
  color: "#2E3341",
  fontSize: 13,
  fontWeight: 600,
  lineHeight: "20px",
};
const revisionTextSx = {
  color: "#2E3341",
  fontSize: 14,
  fontWeight: 400,
  lineHeight: "22px",
  letterSpacing: 0,
  whiteSpace: "pre-wrap",
  overflowWrap: "anywhere",
};
const revisionTextHighlightSx = (variant: "before" | "after") => ({
  bgcolor: variant === "before" ? "rgba(244, 54, 54, 0.2)" : "rgba(21, 169, 53, 0.2)",
  borderBottom: `1px dashed ${variant === "before" ? "#F43636" : "#15A935"}`,
});
const revisionDiffStateSx = {
  width: "100%",
  minHeight: 120,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  boxSizing: "border-box",
  border: "1px dashed #DDE0EA",
  borderRadius: "8px",
  bgcolor: "#fff",
  color: "#6F7279",
  fontSize: 14,
  fontWeight: 500,
  lineHeight: "22px",
  textAlign: "center",
};
const revisionDiffErrorSx = {
  ...revisionDiffStateSx,
  color: "#E32329",
  borderColor: "#F6C7C9",
  bgcolor: "#FFF7F7",
};

export default NewPressReleasePage;
